// ============================================================
// TERROR TCG — SHARED RULES CONSTANTS
// ============================================================
// Single source of truth for game mechanics.
// Consumed by:
//   - Debugger.js (simulator checks)
//   - Rulebook.js (can consume in future for display values)
//   - Shatterfield.js (game logic — future unification)
//
// When you update a value here, ALL consumers update.
// When you need a new rule, add its code + description here
// so the Debugger's rule catalog stays in sync.
// ============================================================

// N-SEAT (2026-08-22): timeoutWinner needs to know team shape to
// resolve a verdict for 2v2/FFA, not just 1v1 — matchSeats.js is a
// pure, import-free module, so pulling from it here creates no cycle.
import { livingSeats, teamOf, teammatesOf, getEffectiveAnima } from "./matchSeats";
// R113 — the Exile mark needs to know what a card's IDENTITY is, and the
// seal is that identity. necroSeal.js imports nothing at all, so pulling
// baseId in here creates no cycle; this file stays free of the card
// catalogue, which is the property that matters.
import { baseId } from "./necroSeal";

// ── CORE CONSTANTS ──────────────────────────────────────────
export const STARTING_ANIMA = 30;
export const MAX_ANIMA      = 50;
export const STARTING_KHAO  = 3;
export const MAX_KHAO_NAT   = 10;  // Natural max from +1 per turn
export const MAX_KHAO_ABS   = 15;  // Absolute max (cards can push here)
export const STARTING_HAND  = 5;
// ── R10 · THE HAND CAP IS 8. LAW. (caP'n, 2026-08-26) ───────
// The citation that stood here was FALSE TWICE OVER — it read
// "Rulez v2 (July 2026): was 7", and neither half of that is true.
// Rulez v2 is the APRIL 2026 book and its ch.4 Draw
// Phase says SEVEN ("if your hand exceeds 7 cards ... send cards to
// Necropolis until your hand is at 7"), so it never raised anything to
// 8, and there was no July revision of it to cite. The 8 is the number
// the engine has always enforced and the number caP'n has now fixed as
// canon, verbatim:
//
//   *"haha no it did keep changing, I just didn't keep a good record of
//   the rulez… so I think it's 8 now, if it is good leave it… just
//   record it as law canon and scrap the rest so there's never confusion
//   on any of these matters again."*
//
// So: 8. Not "8 since Rulez v2", not "was 7" — EIGHT, ruled 2026-08-26,
// and every trace of the 7 is scrapped rather than footnoted, because a
// footnoted old number is exactly the confusion he asked to end.
// Draw-then-discard at the cap (R10): the draw always resolves.
export const HAND_LIMIT     = 8;
// ── R110 · THE BATTLE LINE IS FOUR (caP'n, 2026-09-07) ──────
// It was three from the first day of Terror to 2026-09-07. The four Role
// Arts (R115) each need a rift of their own, so the line grew by one and
// every seat now pre-places FOUR Esclings instead of three.
//
// caP'n, verbatim: *"starter decks we're going keep everything exactly the
// same, just give the player 4 esclings now and update the rules to match.
// the min max etc everything stays the same, just 4 in beginning instead
// of 3."* Nothing else moved — not the Grimoire minimum, not the Luxmoire
// range, not one per-name deck limit, not one fare.
//
// This number is read by RulesEnforcer (deck legality), GameStateValidator
// (board shape, Tome wielders, the Primera ledger) and the Debugger, so
// moving it here moved all of them. The full-wave banner sits further down
// this file under THE EXILE WAVE.
export const ESCLING_SLOTS  = 4;
export const TOME_SLOTS     = 3;
export const CURIO_SLOTS    = 2;
// ── R75 · THE CURIO GIFTS, ONCE ───────────────────────────────
// Two Curios are pre-placed on every seat before the duel (R05) and each
// of the nine shipped ones is a flat, once-per-duel gift at duel start —
// Anima, or +1 Max Khao — transcribed off the printed face ("you gain 3
// Anima", "add +1 to your Max Khao"). The printed condition ("if you are
// wearing headwear") is met by OWNING the card: the Emporium's equipment
// item IS the Curio (BoosterPack.EQUIPMENT_ITEMS → addCardToCollection).
// ⚠ 2026-09-15 unification pass: this table lived TWICE — a switch in
// Shatterfield.activateCuriosForPlayer and a map in NPC.CURIO_GIFTS — and
// agreed only by luck. Both read this now, and the unification selftest
// re-derives every number from cards.js's printed `effect` text, so a
// card whose face changes without this table changing goes red.
export const CURIO_GIFTS = {
  curio_wizards_cap:   { anima: 3 },
  curio_circle_of_lyf: { anima: 1 },
  curio_jynxi_wares:   { anima: 2 },
  curio_spectacles:    { anima: 1 },
  curio_mages_amulet:  { anima: 1 },
  curio_elixir:        { anima: 1 },
  curio_warlocks_robe: { anima: 2 },
  curio_beb_buddy:     { khao: 1 },
  curio_badass_beb:    { anima: 2 },
};
export const GRAVESHIFT_COST = 10;
export const LUX_SEARCH_COST = 5;   // R88 — the Luxmoire search fare
// R88 — the Grimoire search fare is BY LEVEL, and the flat 4 written
// here first was V'ael's invention, not the board's. The shipping
// Shatterfield has always charged 2 for a Level 1 search and 4 above
// it (handleGrimoireSearch and its picker). The catalogue is the thing
// that was wrong, so the catalogue is the thing that changed — a rule
// that quietly doubles a fare players already pay is a balance change
// wearing a coherency costume.
export const GRIM_SEARCH_COST = { L1: 2, L2: 4, L3: 4 };
export const grimSearchFare = (level) => GRIM_SEARCH_COST[`L${level || 1}`] ?? GRIM_SEARCH_COST.L3;
export const RESURRECT_PER_TURN = 1;// R89 — one resurrection per Turn
export const MULLIGAN_LIMIT = 1;    // R90 — one mulligan per duel, redraw STARTING_HAND
// ═══════════════════════════════════════════════════════════════
// ⛓ THE RESPONSE CHAIN HAS NO LENGTH LIMIT (caP'n, 2026-08-26)
// ═══════════════════════════════════════════════════════════════
// ⚠ NOT A RULE, AND R92 NO LONGER SAYS IT IS. ChainLaw.js — the file that
// actually owns the chain — calls this "a runaway guard, NOT a rule … it is
// a DIAL: raise it freely, nothing else in the engine reads a chain's
// length." The August catalogue pass wrote the 12 into R92's text anyway,
// which promoted a documented non-rule into law and left the two files
// contradicting each other in print. The row was corrected 2026-08-25.
//
// AND THEN THE DESIGNER SETTLED IT OUTRIGHT, 2026-08-26, verbatim:
//
//   *"as long as they want that's the point — you said it, players will
//   run out of cards but mainly Khao in like 2 or 3 chains max, it will
//   never get to 12 and if it does that'd be cool xD but like impossible
//   to even do 5 unless both players sat there trying to make it happen
//   over and over using super low cost cards"*
//
// So the chain is UNBOUNDED as a matter of law, and the correction to
// R92's row was not enough on its own: 12 was still LOW ENOUGH TO BIND.
// The designer says a five-link chain is near-impossible and a twelve-link
// chain would be a delight to witness — and the old guard would have
// TRUNCATED that delight at exactly the moment it happened, silently, in
// the one duel anybody would have wanted to see. A runaway guard that can
// end a legal game state is not a guard, it is a rule wearing a guard's
// coat.
//
// 64 is chosen so it can never bind on real play. The true bound is cards
// and Khao: both seats must hold a legal, affordable true counter at every
// link, alternating, and Khao refills once a Turn. Sixty-four links means
// thirty-two L3 instants a side inside one window — more instants than a
// legal deck can contain, several times over. Nothing in the engine reads
// a chain's length as a game bound (grep CHAIN_DEPTH_CAP: Shatterfield's
// runChain loop and the Debugger's Legal.chainLink, and nothing else), so
// raising it changes no rule, no cost and no card. It exists for exactly
// one thing: a BUG that spins the response window forever cannot hang the
// board. If a chain ever reaches 64 the honest reading is not "the cap did
// its job", it is "something is looping".
export const CHAIN_DEPTH_CAP = 64;  // runaway guard ONLY — never a game bound
export const FIRST_KHAO_TURN = 2;   // R91 — no natural Khao gain before this turn index

// ── THE SIMULATION CAP AND THE TURN CLOCK ──────────────────
// SIM_TURN_CAP is the harness's hard stop; TURN_CLOCK is the RULE.
// R87's twenty minutes can never elapse headless (see the banner
// below), so R93 gives the clock a second hand the simulator can
// actually watch: a Solrend that reaches TURN_CLOCK half-turns is
// CALLED by the same timeoutWinner verdict. One law, two hands.
export const TURN_CLOCK      = 150;  // R93 — half-turns before a duel is called
// 150 half-turns is 75 Turns each. Against R87's twenty minutes that is
// about eight seconds a half-turn — so for two people playing carefully
// R93 would ALWAYS ring first and R87 would be decoration. That is the
// opposite of what R87 is for. A live PvP duel therefore gets a far
// looser turn bell and is governed by the human clock; every other mode
// (and every simulated duel, where R87 can never elapse) keeps the 150.
export const TURN_CLOCK_PVP  = 500;  // R93 — the same bell, hung out of reach of a real game
// ⚠ "LIVE PvP" IS READ OFF THE BOARD, NOT OFF A LABEL (2026-09-15 unification
// pass). Until today this read `match.mode === "pvp" || match.isPvP` — and
// NOTHING IN THE GAME EVER WROTE EITHER. gameManager stamps `mode` as
// "1v1" | "1v1ranked" | "ffa3" | "ffa4" | "2v2" | "2v2ranked" | "gauntlet2v2"
// | "story-coop", never "pvp", and no writer of `isPvP` exists. So every
// human-versus-human Solrend rang R93 at 150 while the book said 500, and
// the Debugger's R93 assertion stayed green by handing the function the
// one shape the game never produces (trap §4.4). A seat is a BRAIN when
// it is driven by one (`controller: "ai"`, the co-op boss) or when no
// account sits in it (no `uid` — the classic NPC seat that App.handleEnterNPC
// injects). A duel is live PvP when every seat is a signed-in human. The
// harness's simulated duels declare `controller: "ai"` on both seats —
// truthfully, the brain plays both — which is what keeps the balancer on
// the 150 bell and its ratified clock-call band where caP'n blessed it.
// The explicit labels are kept as an OVERRIDE (THE EXCEPTION LAW), never
// as the only door.
export const seatIsBrain = (seat) => !seat || seat.controller === "ai" || !seat.uid;
export const isLivePvP = (match) => {
  if (!match) return false;
  if (match.mode === "pvp" || match.isPvP === true) return true;
  const n = Number(match.seatCount) || 2;
  if (!match.players) return false;
  for (let i = 0; i < n; i++) if (seatIsBrain(match.players[i])) return false;
  return true;
};
export const clockLimitFor = (match) => (isLivePvP(match) ? TURN_CLOCK_PVP : TURN_CLOCK);
export const SIM_TURN_CAP = 220;    // harness backstop only, NOT a rule

// ═══════════════════════════════════════════════════════════════
// ⚔ "ATTACK" vs "LYT ATTACK" — THE WORD, FIXED
// ═══════════════════════════════════════════════════════════════
// caP'n, 2026-08-25, verbatim:
//
//   *"remember 'Attack' in Terror is a very broad term that can mean
//   you used a tome or Impera or anything to 'attack' a being or your
//   adversary directly, Lyt Attack is the actual move every being gets
//   1 of per turn."*
//
// So the catalogue now uses two words and never mixes them:
//
//   ATTACK      — the BROAD term. ANY action that dims an adversary's
//                 Anima or damages/ruins an adversary's being, by any
//                 route: a Lyt Attack, a Tome, an Impera, a U'hlti, a
//                 Curio, a Primera stamp, an Affinity, a Prophecy.
//                 If it hurts the other side, it is an Attack.
//   LYT ATTACK  — the SPECIFIC swing. One per being per Turn (R30),
//                 resolved Lyt against Lyf (R22-R29), blocked by
//                 standing beings (R31).
//
// Every rule row below that says "Lyt Attack" means the swing and only
// the swing. Every row that says "Attack" means the broad term. R30 is
// a Lyt rule; R08 is a broad one.
export const ATTACK_KINDS = ["Lyt", "Anima", "Tome", "Impera", "Uhlti", "Curio", "Primera", "Affinity", "Effect"];
export const isLytAttack = (kind) => (kind == null ? true : String(kind) === "Lyt");

// ═══════════════════════════════════════════════════════════════
// 🧱 R31 · THE WALL OF BEINGS STOPS LYT ATTACKS ONLY
// ═══════════════════════════════════════════════════════════════
// caP'n, 2026-08-26, verbatim:
//
//   *"the wall of beings only stops dimming of Anima from Lyt Attacks
//   (excluding fallen states etc) — precisely, it needs 'Lyt' in it,
//   you said it, I must've just forgot it."*
//
// Rulez v2, Chapter 6, is titled "Lyt Attacks Against Anima" and says:
//
//   *"If your adversary has no Escendants standing (only Esclings,
//   Fallen beings, or empty rifts), you may Lyt-attack their Anima
//   directly. ... If some defenders have fallen and others are
//   Esclings, you may bypass the Fallen beings and attack Anima. If
//   even a single standing Escendant remains (not Fallen), Anima cannot
//   be targeted directly."*
//
// The chapter is named for the swing and every sentence in it is about
// the swing. THE RULE IS: you may not LYT-ATTACK an adversary's Anima
// while any of their Escendants stands un-Fallen. A Tome, an Impera, an
// U'hlti, a Curio or a Primera stamp that dims Anima is NOT bound by it
// and reaches through a full board of standing beings.
//
// ⚠ THE CODE ALREADY BEHAVED THIS WAY AND WAS VERIFIED BEFORE THE ROW
// WAS REWRITTEN, so this is a ruling that cost nothing to obey and
// everything to leave unwritten. validateAnimaAttack's R31 leg has one
// door (ruleGate.gateAnimaAttack) and the renderer asks it from exactly
// one place — the Escendant-vs-Anima Lyt Attack handler. The nineteen
// card branches that dim an adversary's Anima ask nothing. What DID
// carry the broad reading was an API: RulesEnforcer's B03 check took a
// bare `animaWasHit` boolean, which cannot tell a swing from a Tome. It
// takes an attack kind now.
//
// `isLytAttack` above is the predicate; R31 is the rule that reads it.

// ═══════════════════════════════════════════════════════════════
// 🌒 THE OPENING CYCLE — R08's window, defined once
// ═══════════════════════════════════════════════════════════════
// ⚠ BANNER, AND THE READING THAT WAS CHOSEN. caP'n's R08 ruling says
// "Turn 1", and a Turn in Terror is ONE SEAT ACTING (see the TURN vs
// CYCLE note by R26). But the same ruling then distinguishes "player
// to go second" from the player going first and gives them DIFFERENT
// draw rights — which is only meaningful if the law covers both seats'
// first Turns. A law that applied only to `match.turn === 0` could not
// say anything about the seat going second, because that seat has not
// moved yet at turn 0.
//
// So R08 is implemented as covering EACH SEAT'S FIRST TURN — the
// opening CYCLE. In 1v1 that is half-turns 0 and 1, which is exactly
// what RulesEnforcer.isOpeningTurn has always tested (`t <= 1`); this
// generalises the same window to a 3-4p FFA or a 2v2, where the
// opening Cycle is the first `seatCount` half-turns.
//
// The alternative reading — literally `turn === 0` — was rejected
// because it makes the second seat's draw clause unreachable and lets
// the second seat swing on the opening Cycle while the first cannot,
// which is a first-player disadvantage nobody asked for.
//
// `match.turn` counts HALF-turns, zero-based (gameManager writes 0).
export const openingCycleLength = (seatCount) => Math.max(2, Number(seatCount) || 2);
export const isOpeningTurnIndex = (turn, seatCount = 2) => {
  const t = Math.floor(Number(turn));
  if (!Number.isFinite(t) || t < 0) return false;
  return t < openingCycleLength(seatCount);
};
export const isOpeningCycle = (match) =>
  isOpeningTurnIndex(match?.turn || 0, match?.seatCount || 2);

// Who is the seat that went FIRST? R08 splits the Grimoire draw on it.
// `turnOrder` is the coin-toss order matchSeats.decideTurnOrder built;
// a classic 1v1 that never wrote one falls back to the seat the match
// opened on, and finally to seat 0.
export const firstSeatOf = (match) => {
  const order = Array.isArray(match?.turnOrder) && match.turnOrder.length ? match.turnOrder : null;
  if (order) return order[0];
  if (match?.firstPlayer != null) return match.firstPlayer;
  return 0;
};

// ═══════════════════════════════════════════════════════════════
// 🔍 R08 · A CARD EFFECT MAY SEARCH ON THE OPENING CYCLE
// ═══════════════════════════════════════════════════════════════
// caP'n, 2026-08-26, verbatim:
//
//   *"brilliant distinction yes, cards are exempt from the first turn
//   search rule"*
//
// R08's search clause was built to cover ANY search. It covers only the
// PLAYER'S OWN SEARCH ACTION now — the deliberate move where a seat pays
// an R88 fare and digs through a Book, and which spends the Turn's draw
// (R11). A CARD that searches — a Tome reading "Add 1 lvl 1 Tome from
// your Grimoire to your hand", an Impera tutor, a Primera stamp — is the
// card doing its printed job, not the seat reaching for the Books, and it
// RESOLVES NORMALLY on the opening Cycle.
//
// ⚠ THIS IS THE SAME IDEA AS THE DRAW EXEMPTION, DELIBERATELY SHAPED THE
// SAME WAY. caP'n's own R08 ruling already carried one: *"if you draw
// from one Book(deck) you cannot draw from the other that turn (card
// effects excluded)"* — modelled as `effectDrewThisTurn`, a flag saying
// A CARD DID THIS, so R11 does not count it. This is that flag's twin:
// `effectSearch` says A CARD DID THIS, so R08 does not bar it. Two
// clauses, one idea — a card effect is never the player's action — and
// they are named alike so nobody reads them as two unrelated exceptions.
//
// ⚠ AND IT IS THE SEARCH BAN ONLY. THE SEARCH BAN AND THE ATTACK BAN ARE
// DIFFERENT BANS AND A CARD CAN BE CAUGHT BY ONE AND NOT THE OTHER. The
// Primera stamps are the case that proves it, and they are why this note
// exists rather than being left to be rediscovered:
//   · MACHINE (Ilk) searches the Grimoire for a Beb.
//   · HUMANOID (Ilk) searches the Luxmoire for a Level 1 Impera.
//     Both are a card's own printed power, so both are EXEMPT from the
//     search ban and legal on the opening Cycle — as R09 already said
//     activation was.
//   · PHANTOM (School) dims the adversary's Anima directly, and BEAST
//     (Ilk) amplifies a dim. Neither searches anything, and both are
//     still REFUSED on the opening Cycle — by the ATTACK ban, which this
//     exemption does not touch and must never be read as touching.
// Nothing here weakens the Attack ban by a single clause. A stamp that
// both searched and dimmed would be exempt as a search and refused as an
// Attack, and the refusal would win, because the two bans are asked
// separately and either one is enough to stop the press.
//
// Accepts `true`, "effect", or the SEARCH_BY_EFFECT constant, so a call
// site can thread a boolean or a named initiator without a translation
// step. ANYTHING ELSE — including undefined, which is what every call
// site that has not been taught this looks like — reads as the player's
// own search and stays barred. The exemption must be CLAIMED; it is
// never the default, because a search whose origin nobody stated is a
// search nobody has vouched for.
export const SEARCH_BY_PLAYER = "player";   // a seat's own search action — R08 bars it
export const SEARCH_BY_EFFECT = "effect";   // a card's printed power — R08 exempts it
export const isEffectSearch = (by) =>
  by === true || String(by).toLowerCase() === SEARCH_BY_EFFECT;

// ═══════════════════════════════════════════════════════════════
// 🩸 R97 · THE PROPHET'S TOLL — a fulfilled Prophecy outside 1v1
// ═══════════════════════════════════════════════════════════════
// caP'n, 2026-08-25, verbatim:
//
//   *"any game mode that isn't 1v1: if a prophecy is fulfilled, dim
//   all of your adversary's Anima by 15, if their Anima is currently
//   greater than 30, halve it, round up."*
//
// ⚠ THE READING, FLAGGED. "all of your adversary's Anima" is read as
// EVERY adversary (each living enemy seat), not as "all of one
// adversary's Anima" — because outside 1v1 there is more than one
// adversary and a Prophecy that only punished one of them would be
// weaker in a 4-player game than in a duel, which inverts the whole
// point of giving multiplayer its own clause. The two clauses are read
// as EXCLUSIVE, in the order the designer wrote them: over 30 → halve
// (round up); otherwise → flat 15. They do not stack.
//
// It is an ADVERSARY EFFECT, not a voluntary payment, so R96 (THE TILL
// LAW) does not protect against it and R33 collects if it reaches 0.
// In 1v1 a fulfilled Prophecy still wins the Solrend outright, subject
// to R98 (co-winners).
export const prophetsToll = (currentAnima) => {
  const have = Number(currentAnima) || 0;
  if (have <= 0) return { from: have, to: have, dimmed: 0, halved: false };
  // "if their Anima is currently greater than 30, halve it, round up"
  if (have > 30) {
    const to = Math.ceil(have / 2);
    return { from: have, to, dimmed: have - to, halved: true };
  }
  // "dim all of your adversary's Anima by 15"
  const to = Math.max(0, have - 15);
  return { from: have, to, dimmed: have - to, halved: false };
};

// R97 only applies OUTSIDE 1v1. "1v1" is two seats and no teams —
// a 2v2 is four seats, an FFA is three or four, and both are non-1v1.
export const isOneVsOne = (match) => (Number(match?.seatCount) || 2) <= 2;

// ═══════════════════════════════════════════════════════════════
// ⚔ R08 · WHAT AN ATTACK LOOKS LIKE FROM THE OUTSIDE
// ═══════════════════════════════════════════════════════════════
// THE PROBLEM THIS SOLVES, stated honestly. caP'n's R08 makes "Attack"
// mean ANY route that hurts the other side — "a tome or Impera or
// anything". There is no list of attacking cards in Terror and there
// should not be one: a curated id set is a second catalogue that drifts
// the first time somebody writes a new effect, which is the exact
// disease this codebase spent August curing everywhere else.
//
// So R08 is not enforced by classifying CARDS. It is enforced by
// recognising the DAMAGE, at the one place damage is written. This
// predicate is that recognition, and it is deliberately about the
// PAYLOAD rather than about intent:
//
//   an ADVERSARY's Anima went DOWN            → an Attack
//   an ADVERSARY's Necropolis GREW            → a being of theirs was
//                                               ruined → an Attack
//   an ADVERSARY's being lost Lyf             → an Attack
//
// Nothing else counts. Raising an adversary's Anima (Devotion, King of
// the Castle) is not an Attack. Dimming YOUR OWN Anima (Magma Carta,
// a fare, the Graveshift) is not an Attack — R08 has never had an
// opinion about what you do to yourself.
//
// ⚠ AND IT IS ONLY EVER ASKED DURING THE OPENING CYCLE. Outside that
// window every one of these is legal and this function is not called,
// so it can never cost a normal Turn anything.
//
// Pure and import-free so the engine, the renderer, the Debugger and
// the transition referee can all ask the identical question.
//
// ════════════════════════════════════════════════════════════════════
// ⚠ THE WIDENING (2026-08-25) — AND WHAT IT STILL CANNOT SEE
// ════════════════════════════════════════════════════════════════════
// The first cut recognised THREE shapes: Anima down, Necropolis grew,
// a being lost Lyf. A closing review walked the actual writes in the
// engine and found nine plausible Attacks that produced none of them —
// every one of them a way to hurt a seat that this function called
// legal:
//
//   • a being RUINED by nulling `escendant` while the Necropolis is
//     written in a different call. `_lyfOf` returns null when there is
//     no escendant, so the Lyf clause skipped the slot entirely.
//   • a being knocked FALLEN.
//   • a being whose `currentLyf` was still null (never yet damaged)
//     taking its first hit — `a != null` refused to compare.
//   • KHAO dimmed. Terror has cards that do this and it is an Attack.
//   • a hand DISCARDED, a Grimoire MILLED, a Luxmoire EMPTIED.
//   • a PROPHECY stripped.
//   • a SOUL stripped off a being.
//   • a TOME removed from `field.tomes` — the Lyf clause only ever
//     walked `esclings`.
//   • `necropolis` arriving as a SPARSE RTDB OBJECT ({1:x}), whose
//     `.length` is undefined, so `now > was` was `undefined > 0`.
//
// THE ANSWER IS NOT A LIST OF ATTACKING CARDS. It never is — a second
// catalogue drifts the first time somebody writes a new effect, which
// is the whole reason this predicate exists instead of an id set. So
// the PAYLOAD question is widened: every clause below is still "what
// does this write DO to them", never "which card is doing it".
//
// ⚠ AND IT REFUSED THINGS THAT ARE NOT ATTACKS. Shatterfield's
// handleDevote sends a COMPLETED CONTRACT TOME to ITS OWN OWNER'S
// Necropolis — the adversary's Necropolis grows, written by the paying
// seat — and the NPC's npcHonourDevotions does the identical thing.
// Both read here as an Attack, and a refusal there is worse than the
// attack it prevents: the payer's Anima has already left. A payload
// cannot tell a completed contract from a ruin, so the WRITER declares
// it (`updatePlayer(..., { notAnAttack: "…" })`) and the backstop logs
// the declaration. That escape hatch is a DECLARATION, not a bypass:
// it names a rule and it is greppable. There is exactly one shape
// using it today and it is the devotion contract.
//
// ⚠ WHAT THIS STILL CANNOT SEE, stated so nobody believes otherwise:
//   · A DAMAGE THAT NEVER TOUCHES THE ADVERSARY'S NODE. An effect that
//     hurts them by writing only to the ACTOR's own seat (a stat the
//     adversary's client later reads and applies to itself) is
//     invisible here — the payload it inspects is the victim's.
//   · A WRITE THAT LOOKS IDENTICAL TO A LEGAL ONE. Cards leaving a
//     hand are a discard to this function whether they were torn out or
//     handed over; a Tome leaving a rift is a ruin whether it was
//     destroyed or completed. It can only be told from the SHAPE, and
//     where the shape is the same the writer must declare (above).
//   · INTENT AND CARD IDENTITY. It cannot name the card, cannot tell a
//     Tome Attack from an Impera Attack, and cannot report WHY.
//   · A REPLACEMENT THAT NETS TO ZERO — a being swapped for another of
//     the same Lyf, a card milled and another added in the same write.
//   · THE RESPONSE WINDOW (R92), deliberately: the counter-writer is
//     writing to the ACTIVE seat's node, so the backstops never look.
//     A Nihil answers a DECLARATION; it does not Attack.
const _lyfOf = (slot) => {
  if (!slot || !slot.escendant) return null;
  const v = slot.currentLyf;
  return v === null || v === undefined ? null : Number(v);
};
// RTDB turns [null, x, null] into {1:x} and drops trailing holes, so
// `.length` is `undefined` on half the arrays this predicate is handed.
// EVERY count and EVERY index in here goes through these two.
const _size = (v) => {
  if (Array.isArray(v)) return v.filter((x) => x !== null && x !== undefined).length;
  if (v && typeof v === "object") return Object.keys(v).length;
  return 0;
};
const _at = (v, i) => {
  if (Array.isArray(v)) return v[i] ?? null;
  if (v && typeof v === "object") return v[i] ?? v[String(i)] ?? null;
  return null;
};
// A pool may be an array (hand, necropolis) or a count-map (grimoire,
// luxmoire: { cardId: n }). Both mean "how many cards are in here".
const _poolSize = (v) => {
  if (Array.isArray(v)) return v.filter((x) => x !== null && x !== undefined).length;
  if (v && typeof v === "object") {
    const vals = Object.values(v);
    // a count-map's values are numbers; an RTDB-sparse array's are not
    if (vals.length && vals.every((n) => typeof n === "number")) return vals.reduce((a, n) => a + (n || 0), 0);
    return vals.length;
  }
  return 0;
};
const _has = (o, k) => Object.prototype.hasOwnProperty.call(o, k);
// A slot's `escendant` is a card-id STRING everywhere the shipping
// engine writes one (Shatterfield.js, NPC.js, gameManager.js). An
// independent caller — src/Debugger.js's own state model — represents
// the same field as an OBJECT (`{ uid, id }`). `adversaryDamageInPayload`
// is shared by both, and comparing raw values with `!==` does value
// equality on a string but REFERENCE equality on an object: any payload
// that rebuilds an equivalent escendant object (which is exactly what
// writing an update through Firebase does) reads as "their being was
// replaced" even when nothing changed. Reduce either shape to the
// identity that actually answers "is this the same being" before
// comparing, so the predicate tests what its own comment says it tests.
const _beingId = (v) => {
  if (v === null || v === undefined) return null;
  if (typeof v === "object") return v.id ?? v.uid ?? null;
  return v;
};

// ── THE SCOPES (R123, caP'n 2026-09-13) ────────────────────────
// caP'n, asked whether "your BEINGS cannot be attacked using Tomes"
// (Shower of Lyt) and "your CARDS cannot be attacked using Tomes"
// (Phoenix Aura) are deliberately different words:
//
//   *"yesss thank you partner, that's a very important distinguishment
//   — Cards is general and Beings is specific, written and chosen
//   specifically for those purposes."*
//
// So the printed noun IS the scope, and this predicate is where a scope
// can actually be expressed — "did this payload harm them" is a question
// about the PAYLOAD, which is the same reason R08 is answered here
// rather than at forty effect branches.
//
//   "all"    — R08's question. Every clause. UNCHANGED, and it is the
//              default precisely so R08's behaviour cannot drift.
//   "cards"  — anything of theirs that IS a card: beings, Souls, Tomes,
//              Curios, Prophecy, hand and Books, and the Necropolis
//              growing. NOT Anima and NOT Khao — those are pools, not
//              cards, and Phoenix Aura does not claim them.
//   "beings" — the battle line only. Shower of Lyt's narrow word.
export const DAMAGE_SCOPES = ["all", "cards", "beings"];

export function adversaryDamageInPayload(before, updates, opts = {}) {
  if (!updates || !before) return null;
  const scope = DAMAGE_SCOPES.includes(opts.scope) ? opts.scope : "all";
  const pools = scope === "all";              // Anima and Khao are not cards
  const cards = scope !== "beings";           // piles, Books, Tomes, Curios, Prophecy

  // 1 · ANIMA WENT DOWN.
  if (pools && _has(updates, "anima")) {
    const was = Number(before.anima);
    const now = Number(updates.anima);
    if (Number.isFinite(was) && Number.isFinite(now) && now < was) {
      return `their Anima was dimmed ${was} → ${now}`;
    }
  }

  // 1b · KHAO WENT DOWN. Terror has cards that dim Khao and they are
  // Attacks; nothing here ever looked.
  if (pools && _has(updates, "khao")) {
    const was = Number(before.khao);
    const now = Number(updates.khao);
    if (Number.isFinite(was) && Number.isFinite(now) && now < was) {
      return `their Khao was dimmed ${was} → ${now}`;
    }
  }

  // 2 · THE NECROPOLIS GREW — something of theirs was ruined.
  if (cards && _has(updates, "necropolis")) {
    const was = _size(before.necropolis);
    const now = _size(updates.necropolis);
    if (now > was) return `${now - was} of their cards were ruined (Necropolis ${was} → ${now})`;
  }

  // 2b · CARDS LEFT THEIR POSSESSION ALTOGETHER — a hand torn up, a
  // Grimoire milled, a Luxmoire emptied. Counted as ONE TOTAL on
  // purpose: a DRAW moves a card from the Grimoire to the hand and the
  // total does not move, so an adversary-side draw (a real card does
  // this) is not mistaken for a mill.
  if (cards && (_has(updates, "hand") || _has(updates, "grimoire") || _has(updates, "luxmoire"))) {
    const poolOf = (o, k) => _poolSize(_has(o, k) ? o[k] : before[k]);
    const wasN = _poolSize(before.hand) + _poolSize(before.grimoire) + _poolSize(before.luxmoire);
    const nowN = poolOf(updates, "hand") + poolOf(updates, "grimoire") + poolOf(updates, "luxmoire");
    if (nowN < wasN) return `${wasN - nowN} card(s) were taken out of their hand/Books (${wasN} → ${nowN})`;
  }

  // 2c · A PROPHECY STRIPPED. A win condition removed is an Attack on
  // the only thing that card does.
  if (cards && _has(updates, "prophecy") && before.prophecy && !updates.prophecy) {
    return `their Prophecy (${before.prophecy}) was stripped`;
  }

  // 3 · THEIR SIDE OF THE BOARD. One walk, five questions — a rift can
  // be hurt in more ways than "the number went down", and the first cut
  // asked only that one.
  if (updates.field) {
    const newEsc = updates.field.esclings;
    if (newEsc) {
      const oldSlots = (before.field && before.field.esclings) || [];
      // ⚠ ESCLING_SLOTS, NOT A LITERAL 3. RTDB deletes null entries and
      // returns sparse integer-keyed objects, so a payload that touches
      // ONLY the fourth rift has `_size` 1 — and a floor of 3 stopped the
      // walk one rift short of the Mind. A first strike on the fourth
      // rift was therefore invisible to the seal whose whole job is to
      // catch the first strike. The floor has to be the width of the
      // battle line, and the battle line is R110's four.
      const n = Math.max(_size(newEsc), _size(oldSlots), ESCLING_SLOTS);
      for (let i = 0; i < n; i++) {
        const o = _at(oldSlots, i), b = _at(newEsc, i);
        if (!o || !o.escendant) continue;          // nothing there to hurt
        // 3a · THE BEING LEFT THE RIFT. Ruined by nulling `escendant`
        // with the Necropolis written somewhere else — invisible to
        // every clause of the first cut.
        if (!b || !b.escendant) return `their being in rift ${i + 1} (${o.escendant}) left the board`;
        if (_beingId(b.escendant) !== _beingId(o.escendant)) return `their being in rift ${i + 1} was replaced (${o.escendant} → ${b.escendant})`;
        // 3b · KNOCKED FALLEN.
        if (!o.fallen && b.fallen) return `their being in rift ${i + 1} was knocked Fallen`;
        // 3c · LYF. `currentLyf` is null until a being is first hurt,
        // so null → a number is that first hit. It cannot tell a gift
        // from a wound on a slot that never carried a number, and it
        // errs toward refusing: R08 declining a rare adversary-side Lyf
        // GIFT for one Cycle is much cheaper than missing every first
        // strike in the game.
        const a = _lyfOf(o), c = _lyfOf(b);
        if (a != null && c != null && c < a) return `a being in rift ${i + 1} lost Lyf ${a} → ${c}`;
        if (a == null && c != null) return `a being in rift ${i + 1} took its first Lyf write (→ ${c})`;
        // 3d · A SOUL STRIPPED.
        const so = _size(o.soul), sb = _size(b.soul);
        if (sb < so) return `${so - sb} Soul card(s) were stripped off their being in rift ${i + 1}`;
      }
    }
    // 3e · A TOME REMOVED FROM A RIFT. The whole `tomes` triple was
    // outside the walk, so a Tome destroyed with no `necropolis` key in
    // the same payload was a free Attack.
    if (cards && updates.field.tomes) {
      const oldT = (before.field && before.field.tomes) || [];
      for (let i = 0; i < 3; i++) {
        const o = _at(oldT, i), b = _at(updates.field.tomes, i);
        if (!o || !o.id) continue;
        if (!b || !b.id) return `their Tome in rift ${i + 1} (${o.id}) was removed`;
        if (b.id !== o.id) return `their Tome in rift ${i + 1} was replaced (${o.id} → ${b.id})`;
      }
    }
    // 3f · THEIR CURIOS.
    if (cards && updates.field.curios) {
      const oldC = (before.field && before.field.curios) || [];
      for (let i = 0; i < 2; i++) {
        const o = _at(oldC, i), b = _at(updates.field.curios, i);
        if (o && !b) return `their Curio in slot ${i + 1} (${o}) was removed`;
      }
    }
  }

  return null;
}

// ═══════════════════════════════════════════════════════════════
// 🫧 R125 · AN INTANGIBLE TREASURE CANNOT BE RUINED
// ═══════════════════════════════════════════════════════════════
// Three shipped Treasures print intangibility and NOT ONE OF THEM HAD A
// READER. Two wrote the flag and one did not even write it:
//
//   · **Khao Paradise** — *"This card is Intangible the turn it was
//     constructed."* Wrote `intangible: true` + `constructedTurn`.
//   · **Stars and Skies** — the same sentence, the same two fields.
//   · **Necklace of Akula** — *"This Treasure is Intangible for the
//     first 3 turns."* Wrote nothing at all; only its +1-Anima-on-ruin
//     listener was ever built.
//
// `attemptRuinOppTome` — the ONE funnel every Tome ruin passes through —
// asked about `nullifyFirstRuin` and about nothing else.
//
// ⚠ "THE TURN IT WAS CONSTRUCTED" IS A HALF-TURN AND "3 TURNS" IS THREE
// FULL ONES, and that asymmetry is R103, not a slip: a named boundary
// means that boundary, a bare number of turns means full Turns. So the
// two Khao cards protect for the half-turn they landed on, and Akula
// protects for three full Turns from the same anchor — six half-turns at
// a duel, a longer window at a bigger table, through the one helper.
export const INTANGIBLE_FULL_TURNS = { tome_necklace_akula: 3 };

/** R125 — is this Treasure untouchable right now? */
export function tomeIsIntangible(tome, turn, seatCount = 2) {
  if (!tome?.id) return false;
  const anchor = tome.constructedTurn ?? tome.placedTurn;
  if (anchor == null) return false;
  const now = Math.floor(Number(turn)) || 0;
  const from = Math.floor(Number(anchor)) || 0;
  const full = INTANGIBLE_FULL_TURNS[String(tome.id).split("~")[0]];
  // A card with its OWN duration in full Turns (Akula).
  if (full) return now < from + durationHalfTurns(full, seatCount);
  // Otherwise the flag means "the half-turn it was constructed".
  return !!tome.intangible && now === from;
}

// ═══════════════════════════════════════════════════════════════
// ⚔ R124 · A SECOND SWING, WHERE A CARD PRINTS ONE
// ═══════════════════════════════════════════════════════════════
// R30 is "each Escendant Lyt-attacks once per Turn", and it was enforced
// as a BOOLEAN — `slot.attackedThisTurn`. Two shipped cards print an
// exception to it and NEITHER WORKED:
//
//   · **King of the Anthill** (p_antdroid_3, Impera L3) — *"This being
//     can make an additional Lyt Attack, for every other Antdroid being
//     in combat."* Shatterfield counted the Antdroids, wrote the count
//     into a local, and then carried a comment saying the total was
//     "tracked as extraAttackCount on the slot (used in attack flow)".
//     `extraAttackCount` appeared NOWHERE ELSE IN THE CODEBASE. The
//     comment was the entire implementation.
// ⚠ AND ONE CARD THAT LOOKS LIKE THE SAME BUG AND IS NOT: **Sword of
// Shadows** grants its wielder a swing through `slot.extraAttack`, a
// BOOLEAN one-shot that both attack paths already honour and clear. It
// works. It is named here so the next reader does not "fix" a live card
// — and because the two mechanisms have to coexist: `extraAttack` is a
// GRANT (given once, spent once) and this is a PASSIVE (true while the
// Antdroids stand, worth N). They add.
//
// ⚠ AND THE COUNT IS ON THE SLOT, NOT A SECOND BOOLEAN. `attacksThisTurn`
// is the number of swings taken; `attackedThisTurn` stays written and
// stays true the moment the allowance is spent, because the transition
// referee (X-codes), GameStateValidator and the NPC all read it and a
// rule change must not quietly retire a field three auditors depend on.
//
// "IN COMBAT" MEANS THE WHOLE BOARD, both halves, which is how every
// other "in combat" count in Terror reads (Z4Z's Time beings, Pulsar's
// Astro beings). "OTHER" excludes the wielder itself.

/** R124 — how many EXTRA Lyt Attacks does this being have, beyond its one? */
export function extraLytAttacks(slot, myRifts = [], oppRifts = [], cardOf = null, selfIndex = null) {
  if (!slot?.escendant) return 0;
  const get = typeof cardOf === "function" ? cardOf : () => null;
  let extra = 0;
  const soul = slot.soul || [];
  // King of the Anthill — one per OTHER Antdroid being in combat.
  //
  // ⚠ "OTHER" IS EXCLUDED BY INDEX WHEN THE CALLER KNOWS IT, and by
  // reference only as a fallback. Reference identity is true of every
  // production caller (each hands the very object it pulled out of the
  // array) and is the kind of thing that is true until somebody clones a
  // slot — which is what `adversaryDamageInPayload`'s `_beingId` banner
  // is about, twenty lines of this same file. Getting it wrong here
  // means a lone King counts ITSELF and swings twice for free.
  if (soul.some((id) => String(id).startsWith("p_antdroid_3"))) {
    const mine = myRifts || [];
    const isAnt = (s) => {
      if (!s?.escendant) return false;
      const c = get(s.escendant);
      return !!c && /antdroid/i.test(c.name || "");
    };
    const skip = (s, i, own) =>
      (own && Number.isInteger(selfIndex) ? i === selfIndex : s === slot);
    extra += mine.filter((s, i) => isAnt(s) && !skip(s, i, true)).length;
    extra += (oppRifts || []).filter((s, i) => isAnt(s) && !skip(s, i, false)).length;
  }
  // The GRANTED one-shots the rest of the engine already writes. They are
  // counted here so there is ONE answer to "how many swings does this
  // being have", rather than an allowance in the gate and a re-arm in
  // two attack handlers that have to agree with it.
  if (slot.extraAttack) extra += 1;
  return extra;
}

/** R30 + R124 — how many swings may this being take this Turn? */
export const lytAttacksAllowed = (slot, myRifts, oppRifts, cardOf, selfIndex = null) =>
  1 + extraLytAttacks(slot, myRifts, oppRifts, cardOf, selfIndex);

// ═══════════════════════════════════════════════════════════════
// 🛡 R123 · THE TOME WARD — "BEINGS" AND "CARDS" ARE NOT THE SAME WORD
// ═══════════════════════════════════════════════════════════════
// caP'n, 2026-09-13, asked whether Shower of Lyt's "your BEINGS cannot
// be attacked using Tomes this turn" and Phoenix Aura's "your CARDS ...
// for 2 turns" are a deliberate distinction or a slip:
//
//   *"yesss thank you partner, that's a very important distinguishment
//   — Cards is general and Beings is specific, written and chosen
//   specifically for those purposes."*
//
// ⚠ BOTH CARDS WERE DEAD. They raised a field flag that NOTHING read,
// on both sides of the board — a 3-Khao card and an 8-Khao card that
// did nothing at all. The flag, the duration arithmetic and the two
// printed texts were all correct and waiting for a reader.
//
// ⚠ AND A GUARD FOR THEM WAS WRITTEN ON 2026-09-08 AND REVERTED THE
// SAME DAY, which is worth recording. It returned at the HEAD of the
// Tome dispatcher, and that does not ward the adversary's cards — it
// turns off their whole Tome LANE, including their own healing, their
// Khao growth, and Divine Justice / Shower of Lyt / Phoenix Aura
// themselves. One 8-Khao Phoenix would have locked an adversary out of
// Tomes for two Turns and eaten every card they spent trying.
//
// THE SHAPE THAT WORKS is R08's: ask the PAYLOAD. A ward is the same
// predicate as R08's with a different condition and a narrower scope —
// which is exactly what the two printed nouns are, and what a
// dispatcher gate could never have expressed.

/**
 * R123 — is this seat's field warded against Tomes right now, and how
 * wide is the ward? Returns "cards" | "beings" | null.
 *
 * "cards" wins when both are up: it is the strictly wider ward, and a
 * seat holding both bought the wider one.
 */
export function tomeWardScope(field, turn) {
  if (!field) return null;
  const now = Math.floor(Number(turn)) || 0;
  // Phoenix Aura — CARDS, for two full Turns. `protectedFromTomesUntilTurn`
  // is an expiry half-turn computed by durationExpiryTurn.
  // ⚠ THROUGH THE HELPER, NOT BY HAND (2026-09-14). This spelled its own
  // `now <= …` while the stamp it reads is written by `durationExpiryTurn`
  // two hundred lines away — so when caP'n ruled the window EXCLUSIVE, a
  // hand-written comparison would not have moved and Phoenix Aura's ward
  // would have outlived Divine Justice by one half-turn for no reason a
  // player could see. A boundary is read by the function that owns it.
  if (durationStillRunning(now, field.protectedFromTomesUntilTurn)) return "cards";
  // Shower of Lyt — BEINGS, this turn only, keyed to the half-turn it
  // was played on so a stale flag cannot ward forever.
  if (field.protectedFromTomesThisTurn && field.protectedTomeTurn === now) return "beings";
  return null;
}

/**
 * R123 — would this Tome payload land harm the ward forbids?
 * Returns the reason string (for a toast) or null to let it through.
 */
export function tomeWardBlocks(beforeSeat, updates, scope) {
  if (!scope) return null;
  return adversaryDamageInPayload(beforeSeat, deslashPayload(updates), { scope });
}

// ⚠ A SEAT PAYLOAD CAN ARRIVE AS SLASH KEYS. `{ "field/tomes/1": null }`
// reaches exactly the same place as `{ field: { tomes: [...] } }`, and
// the NPC writes that shape constantly — so a predicate that only reads
// nested objects is blind to half the writes in the game. This is the
// same regrouping gameManager.updateMatch does for R08's seal, hoisted
// here so both seals share one implementation instead of two.
export function deslashPayload(updates) {
  if (!updates || typeof updates !== "object") return updates;
  const keys = Object.keys(updates);
  if (!keys.some((k) => k.includes("/"))) return updates;
  const out = {};
  for (const k of keys) {
    if (!k.includes("/")) { out[k] = updates[k]; continue; }
    const parts = k.split("/");
    let node = out;
    for (let i = 0; i < parts.length - 1; i++) {
      const seg = parts[i];
      if (!node[seg] || typeof node[seg] !== "object") node[seg] = {};
      node = node[seg];
    }
    node[parts[parts.length - 1]] = updates[k];
  }
  return out;
}

// ═══════════════════════════════════════════════════════════════
// 🏆 R98 · A SOLREND CAN HAVE MORE THAN ONE WINNER
// ═══════════════════════════════════════════════════════════════
// caP'n, 2026-08-25, verbatim:
//
//   *"that is sort of the idea for prophecies where you might actually
//   want someone to deliver the finishing blow thereby fulfilling your
//   prophecy's condition and you also win, keyword, also, both players
//   win; the first player still managed to ruin the first Aenarchist,
//   prophecies do not prevent wins merely grant them, the other player
//   can still win naturally or by their own prophecy"*
//
// So `winner` is no longer the truth — `winners` is. `winner` stays
// populated with the PRIMARY (board) winner for every consumer written
// before this ruling; `winners` is the array and it is the thing to
// read. normalizeWinners is the one derivation, so no consumer has to
// guess whether it has one shape or the other.
export const normalizeWinners = (winner, winners) => {
  const out = [];
  const push = (w) => { if (w != null && !out.includes(w)) out.push(w); };
  push(winner);
  if (Array.isArray(winners)) winners.forEach(push);
  else push(winners);
  return out;
};
// Read `match` and get the truth, whichever fields it carries.
export const winnersOf = (match) => normalizeWinners(match?.winner, match?.winners);
export const isCoWin = (match) => winnersOf(match).length > 1;

// ═══════════════════════════════════════════════════════════════
// ⏳ THE CLOCK — R87, and the only answer Terror has to the lock
// ═══════════════════════════════════════════════════════════════
// caP'n, 2026-08-20: *"let's enforce the time limit now — it's supposed
// to be 15 minutes but I wanted to increase it to 20 minutes, so that
// should stop the Anima freeze for humans."*
//
// ── WHAT IT IS FOR ──────────────────────────────────────────
// The balancer measured a MUTUAL BOARD-LOCK: in a stalled duel Anima
// does not grind down, it freezes around turn 50 and does not move for
// four hundred turns. ~15% of NPC-vs-NPC duels never ended at all,
// almost all of them mirror matches. Three rules that are each correct
// alone make the lock stable — R31 (Anima is unreachable while any
// being stands) · R28 (Lyf refreshes for the ACTING seat at the end of
// its OWN Turn, so an untouched board mends every Turn — corrected here
// 2026-09-14: this banner still described the reading caP'n's own ruling
// killed, "Lyf resets every cycle, so chip damage is erased before it
// can accumulate". R28's own row and RULEZ_V2 ch.9 say the opposite, and
// the difference is load-bearing for the lock this banner is about:
// chip damage has real value INSIDE a Turn and none across one, so the
// lock is stable for a narrower reason than was written down here) ·
// R44 (beings come back, out of the
// Necropolis a ruined being just went into) — and R43 had already
// removed deck-out as a loss, so there was no clock of any kind. A
// human concedes. The game had no opinion. Now it does.
//
// ⚠ THIS DOES NOT UNSTICK THE BALANCER, and saying so plainly matters.
// tools/balance.mjs runs the brain headless at thousands of turns per
// second; twenty minutes of wall clock will never elapse inside it.
// Its own --turncap (220) is what stops those runs, and a capped run is
// still reported as unresolved rather than as a win. The lock is a
// RULES problem and it is still open; this is the door out for a human
// who would otherwise sit in front of a board that cannot move.
export const DUEL_TIME_LIMIT = 20 * 60;   // seconds of LIVE duel — R87
// ── R126 · THE THIRD CLOCK ─────────────────────────────────────
// Forty-five seconds a half-turn, in every mode, PvP and brain alike.
// caP'n raised it from 35 himself. It ends a TURN, never a Solrend, and
// produces no verdict. It holds while a response window is open, through
// a whole chain, during a cinematic hold and while a PvP declaration is in
// flight; it does not run at all on a Turn whose controller is "ai".
// ⚠ 2026-09-15 unification pass: this number lived in Shatterfield.js as
// a module-local literal — the one rule constant outside the catalogue,
// and R126's own row said so. THE CATALOGUE LAW: one catalogue, every
// consumer reads it. Shatterfield imports it now.
export const PLAYER_TIMER = 45;   // seconds per half-turn — R126

// ── THE JUDGEMENT ───────────────────────────────────────────
// Terror has no draw. `matches/{code}.winner` is a seat index in 1v1/
// FFA or a team id in 2v2, and every consumer downstream — the faction
// tally, the replay commit, the Depths ELO, the world-map node clear —
// reads it through didSeatWin, never a raw `=== 0/1`. So the clock does
// not invent a fourth outcome; it AWARDS the duel on the evidence
// already on the board, in a fixed order, and every step is something
// a player can see and play toward while the clock runs:
//
//   1 · ANIMA. The whole game is a race to zero someone else's. The
//       one who took less is the one who was winning. (2v2 pools one
//       Anima per team — getEffectiveAnima already returns that pooled
//       number for both teammates, so ranking by it ranks the team.)
//   2 · BEINGS STANDING. Equal Anima is a stalemate of pressure; the
//       board that still has a fist is the board that would have won.
//       (Teams sum this across their living seats.)
//   3 · GRIMOIRE DEPTH. Two equal boards, and the deeper reserve wins
//       — R43 says running out is not a loss, so having more left is
//       the last honest advantage on the table. (Teams sum this too.)
//   4 · SEAT 0. Deterministic, documented, and vanishingly rare: a
//       twenty-minute duel that ties on all three measures. It goes to
//       the contestant holding the lowest seat index (a team's own
//       lowest seat, for 2v2) because seat 0 is the human in every mode
//       that is not PvP, and a timeout is the ENGINE failing to
//       resolve, not the player. It should not cost them the duel.
//
// N-SEAT (2026-08-22): a 3-4p FFA and a 2v2 both reach this clock now.
// Every LIVING seat or team is ranked by the same three rows above —
// not just seats 0/1 — and the runner-up (whoever/whichever team ranks
// second) rides along, because a Tournament of Kings payout needs a
// 3rd place even when a pairing ends by clock instead of elimination.
//
// Pure, so the Debugger and the harness can both ask it without a
// board, a screen or a network.
export function timeoutWinner(match) {
  const players = match?.players || {};
  const standing = (p) => ((p?.field?.esclings) || [])
    .filter((x) => x && x.escendant && !x.fallen).length;
  const depth = (p) => {
    const g = p?.grimoire;
    if (Array.isArray(g)) return g.length;
    if (g && typeof g === "object") return Object.values(g).reduce((n, c) => n + (Number(c) || 0), 0);
    return 0;
  };

  const seen = new Set();
  const contestants = [];
  for (const idx of livingSeats(match)) {
    const t = teamOf(match, idx);
    const key = t == null ? `seat:${idx}` : `team:${t}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const members = t == null ? [idx] : Array.from(new Set([idx, ...teammatesOf(match, idx)]));
    contestants.push({
      id: t == null ? idx : t,
      seatForSeed: Math.min(...members),
      anima: getEffectiveAnima(match, idx),
      beings: members.reduce((n, i) => n + standing(players[i]), 0),
      depth: members.reduce((n, i) => n + depth(players[i]), 0),
    });
  }

  const cmp = (c1, c2) => {
    if (c1.anima !== c2.anima) return c2.anima - c1.anima;
    if (c1.beings !== c2.beings) return c2.beings - c1.beings;
    if (c1.depth !== c2.depth) return c2.depth - c1.depth;
    return c1.seatForSeed - c2.seatForSeed;
  };
  const ranked = [...contestants].sort(cmp);
  const by = (() => {
    if (ranked.length < 2) return "seat";
    const [x, y] = ranked;
    if (x.anima !== y.anima) return "anima";
    if (x.beings !== y.beings) return "beings";
    if (x.depth !== y.depth) return "grimoire";
    return "seat";
  })();

  return {
    winner: ranked.length ? ranked[0].id : 0,
    runnerUp: ranked.length > 1 ? ranked[1].id : null,
    by,
  };
}

// ── R96 · THE TILL LAW ──────────────────────────────────────
// The balancer found R33-at-the-till in August: every Anima spend read
// `current < amount`, which lets an EXACT payment through — pay 10 with
// 10, land on 0, and nothing ended the Solrend, because every endMatch
// hung off an attack resolving and paying a cost is neither. That half
// was fixed by ending the duel. This is the other half, and it is a
// rules question rather than a bug: SHOULD a voluntary payment be
// allowed to kill you? R41 makes Graveshift cost exactly 10 and R43
// says you cannot lose by running out of cards — so a seat on exactly
// 10 Anima with an empty Grimoire either loses to R43's own remedy or
// the remedy is off the table. One law settles it: you may not pay
// yourself to zero. Damage still can, and R33 still collects.
//
// ⚠ RATIFIED — caP'n, 2026-08-26, verbatim:
//
//   *"It is a payment and yes you cannot end yourself that way."*
//
// It was V'ael's ruling flagged for veto. It is now the designer's,
// and it BINDS EVERY CARD EFFECT THAT DIMS ITS OWN CONTROLLER'S
// ANIMA — not just the tills. Before this ruling landed, exactly one
// of the self-dimming cards asked this function (Magma Carta); nine
// others and the two Devotion legs did a raw `Math.max(0, anima - n)`,
// which meant a seat could pay itself to exactly 0 and the duel did
// not even END, because every endMatch in the project hangs off an
// attack resolving and a self-dim is not one. Both halves are closed:
// the activation is REFUSED at the till, and because it is refused,
// the zero is unreachable rather than merely unhandled.
//
// THE DOOR IS THIS FUNCTION. If you write a new card that dims its
// controller's Anima, call this before the write or you have
// reintroduced the bug.
export function canPayVoluntary(current, amount, { exempt = false } = {}) {
  if (amount <= 0) return true;
  const have = Number(current) || 0;
  // The exemption is R43's remedy and nothing else: pass exempt:true ONLY
  // for the Graveshift fare. Everything else must clear the till.
  return exempt ? have >= amount : have > amount;
}

// ═══════════════════════════════════════════════════════════════
// 🩸 R100 · SELF-INFLICTED ANIMA LOSS NEVER FEEDS A PROPHECY
// ═══════════════════════════════════════════════════════════════
// caP'n, 2026-08-26, verbatim:
//
//   *"Also I don't know if you asked later or if I ever mentioned it,
//   but dimming your own Anima in any way at all does not satisfy
//   Prophecy requirements."*
//
// This is the second half of the same ruling as R96 and it is a rule
// nobody had written down. R96 says a voluntary self-dim may not KILL
// you. R100 says it may not WIN for you either.
//
// ── WHAT WAS BROKEN ────────────────────────────────────────────
// `prophecyEngine.buildAnimaLossUpdate` incremented ONE counter —
// `totalAnimaLost` — from every source that dimmed a seat's Anima:
// an adversary's Lyt Attack, an adversary's Tome, a card cost, a
// self-dim card effect, the Graveshift fare, a Devotion, a Grimoire
// search. Bloody Hell fires at 45 lost, on EITHER side. So a seat
// could farm its own Anima — Z4Z Apocalypse is −8 a pop — and win a
// Solrend by hurting nobody but itself. That is not an exotic line;
// it is six activations.
//
// ── THE SPLIT ──────────────────────────────────────────────────
// There are now TWO ledgers and they never mix:
//
//   totalAnimaLost / animaLostThisTurn
//       ADVERSARIAL loss ONLY. Anima an adversary took off you — a
//       Lyt Attack, an Anima strike, an adversary's Tome / Impera /
//       U'hlti / Curio / stamp, a reflected attack, the Prophet's
//       Toll. THIS is the Prophecy ledger. Bloody Hell reads it.
//
//   totalAnimaSelfDimmed / animaSelfDimmedThisTurn
//       Anima YOU dimmed: a card cost, a self-dim effect, the
//       Graveshift fare, a Devotion, a fare of any kind. Kept because
//       it is real and the Codex, the wound-crack visuals and any
//       future "you have bled yourself N" reader want it — but NO
//       PROPHECY MAY EVER READ IT. If you are writing a Prophecy
//       predicate, the pair you want is the adversarial one, always.
//
// ── THE JUDGEMENT CALL ON DEVOTION, DECLARED ───────────────────
// A Devotion (King of the Castle, Magma Carta) is Anima you hand to
// the adversary because their contract Tome demands it. It is
// compelled by their card, which argues adversarial; but it is YOUR
// hand moving YOUR Anima, and the designer's words are "in any way at
// all". It is filed as SELF. Said out loud rather than buried,
// because it is the one row a reasonable person could file the other
// way, and if caP'n disagrees it is two call sites.
//
// The kinds are named rather than passed as a bare boolean so that a
// call site reads as a CLASSIFICATION and not as a flag somebody
// forgot to flip.
export const ANIMA_LOSS_KINDS = { ADVERSARY: "adversary", SELF: "self" };
export const animaLossFeedsProphecy = (kind) =>
  (kind == null ? true : kind === ANIMA_LOSS_KINDS.ADVERSARY);

// ── R93 · THE SECOND HAND ───────────────────────────────────
// timeoutWinner is the verdict; this is the other trigger for it.
// R87's twenty minutes is a HUMAN clock and can never elapse in a
// headless run, which is why the balancer reported ~13% of duels as
// "never ended" while the shipping game had an answer. A Solrend is
// also bounded in TURNS. Pure and import-free, so the engine, the
// brain, the Debugger and the harness all ask the same question.
//
// `match.turn` counts HALF-turns (one seat acting), which is what
// every existing consumer already increments — so the bound is in
// half-turns too, and the comment says so instead of the code lying
// about it.
export function clockExpired(match, limit = TURN_CLOCK) {
  return (match?.turn || 0) >= limit;
}

// Returns the R87/R93 verdict when the duel is over on the clock,
// or null while it still has time. `reason` says WHICH hand called
// it, because "we ran out of patience" and "we ran out of minutes"
// are different findings even though the verdict is identical.
export function callOnClock(match, { turnLimit = null, elapsedSeconds = null } = {}) {
  const byTurns = clockExpired(match, turnLimit ?? clockLimitFor(match));
  const byTime  = elapsedSeconds != null && elapsedSeconds >= DUEL_TIME_LIMIT;
  if (!byTurns && !byTime) return null;
  return { ...timeoutWinner(match), reason: byTurns ? "turns" : "time", rule: byTurns ? "R93" : "R87" };
}

// ── DECK CONSTRUCTION ──────────────────────────────────────
export const GRIMOIRE_MIN  = 35;
// ── R05/R06 · THE DECK LANES ARE THE GAME'S, NOT THE BOOK'S ─
// caP'n, 2026-08-26, verbatim:
//
//   *"the way the game currently works is correct. Grimoire —
//   Escendants and Tomes only. Luxmoire — everything else that doesn't
//   immediately get set to the board, like Curio and Prophecy."*
//
// ⚠ AND THE OLD CITATION HERE WAS FALSE. It read "Rulez v2" as the
// authority for these lanes, and Rulez v2 says the OPPOSITE: its ch.3
// puts "Escendants, Tomes, U'hltis, Curios, Prophecies, and Affinities"
// in the Grimoire and says the Luxmoire "contains only Primera",
// 10-30. Every clause of that is now superseded — the lanes below are
// the ruling, and the April text is wrong, not merely old.
//
//   GRIMOIRE  — Escendants + Tomes. Nothing else.
//   LUXMOIRE  — Impera + U'hltis + Affinities (10-40).
//   PRE-PLACED — Curios and Prophecies sit on the Shatterfield before
//                the duel and belong to NO deck.
export const LUXMOIRE_MIN  = 10;
export const LUXMOIRE_MAX  = 40;

// ── R104 · THREE U'HLTIS TO A LUXMOIRE (caP'n, 2026-08-26) ──
// Verbatim:
//
//   *"Luxmoire rulings are set from earlier about Imperas 5/4/3 rule.
//   Affinities only 1 per Lux. U'hltis rules as it was in Grimoire just
//   for the Lux, which should be 3 U'hltis if I'm not mistaken."*
//
// Two limits now bind an U'hlti and they are DIFFERENT SHAPES, which is
// why one of them cannot live in DECK_LIMITS:
//   · PER UNIQUE NAME — 1 (R59, DECK_LIMITS.UHLTI). Unchanged.
//   · PER LUXMOIRE, TOTAL — 3 (this, R104). NEW.
// So three U'hltis of three different names is legal; a fourth is not,
// and two of one name never was.
//
// ⚠ FLAGGED FOR THE DESIGNER: *"if I'm not mistaken"* is not a number
// he checked. 3 is what he said and 3 is what is built; it is the one
// figure in this pass he may want to move.
export const LUXMOIRE_UHLTI_MAX = 3;

// ── R62 · ALLY-ILK CURIOS: TWO PER SHATTERFIELD (caP'n, 2026-08-26) ──
//   *"per Shatterfield, and update it so it's 2 because I made it 2
//   Curios since way back then, just never wrote it in the rulebook
//   apparently."*
// It matches CURIO_SLOTS exactly — the two Curio rifts — so the cap is
// "both of your rifts may hold an Ally". Rulez v2's "1 per Grimoire" is
// wrong on BOTH halves: the number and the container.
export const ALLY_ILK_CURIO_MAX = 2;

// Per-unique-name deck limits
export const DECK_LIMITS = {
  ESCENDANT_L1: 5,
  ESCENDANT_L2: 4,
  ESCENDANT_L3: 3,
  TOME_L1:      3,
  TOME_L2:      2,
  TOME_L3:      1,
  IMPERA_L1:    5, // R94
  IMPERA_L2:    4, // R94
  IMPERA_L3:    3, // R94
  UHLTI:        1,
  AFFINITY:     1, // per LUXMOIRE (not per name) — R60
  CURIO:        1,
  PROPHECY:     1, // per Solrend
};

// ── RESURRECTION COSTS ─────────────────────────────────────
export const RESURRECT_COST = { L1: 2, L2: 4, L3: 4 };

// ── RESURRECTION TALLIES (Rulez July 25, 2026 wave) ────────
// Per Solrend, PER COPY. EVERY resurrection method counts toward
// the tally — paid, card effect, Tome, the Time Primera — no
// matter the method or source.
//
// PER COPY, NOT PER NAME (caP'n, July 27 2026). Three Bebs of the
// same name in a Grimoire are three separate beings with three
// separate tallies: nine potential returns, not three. This is what
// necroSeal.js exists to make representable — two ruined copies are
// otherwise two identical strings in a flat Necropolis array.
export const RESURRECT_LIMIT = { L1: 3, L2: 2, L3: 1 }; // Beb / Champion / Lord

// ── PRIMERAS — THE STAMPS (Rulez July 25, 2026 wave) ───────
// Every Escendant bears two innate stamp powers: its Ilk's and
// its School's. NOT cards — they live on the being itself.
// Cost = the being's Level, paid ENTIRELY in Khao or ENTIRELY in
// Anima — NEVER split. See R107 below; this fare used to be the one
// mixable cost in the game and caP'n struck that on 2026-08-26.
// Own Turn only — never in a Response Window.
// Limits (both reset at the start of your next Turn):
//   1. One Primera per BEING per Turn (Ilk OR School, never both).
//   2. One use per WORD per Turn across your whole field —
//      Dragon is ONE word on both axes.
// Effects from different beings stack within the Turn.
// Consumed by: Rulebook.js (display) · Shatterfield stamp UI (H22).
export const PRIMERAS = {
  ilk: {
    Dragon:   { id: "p_ilk_dragon",   effect: "Reduce the user's Lyf by 1; add 1 to its Lyt for the Turn." },
    Beast:    { id: "p_ilk_beast",    effect: "If the user dims the adversary's Anima this Turn, dim it by 1 more." },
    Machine:  { id: "p_ilk_machine",  effect: "Search your Grimoire for 1 Beb and add it to your hand, free. Shuffle after. Does not consume your Turn's draw." },
    Humanoid: { id: "p_ilk_humanoid", effect: "Search your Luxmoire for 1 Level 1 Impera and Train it onto the user, free. Matching laws still apply. Does not consume your Turn's draw." },
  },
  school: {
    Dragon:  { id: "p_school_dragon",  effect: "Reduce the user's Lyt by 1; add 1 to its Lyf for the Turn." },
    Phantom: { id: "p_school_phantom", effect: "Dim the adversary's Anima directly by 1, bypassing all beings." },
    Time:    { id: "p_school_time",    effect: "Resurrect 1 Beb from your Necropolis at no cost. Still counts toward that being's Resurrection tally." },
    Space:   { id: "p_school_space",   effect: "Ascend 1 Level 2 being from your Grimoire over a matching Level 1 you control, free. Does not consume your Turn's one Ascend." },
  },
};
// Primera activation cost by the user's level. ONE POOL OR THE OTHER —
// R107. (This line said "mixable" until 2026-08-26 and the engine
// obeyed it; see THE NO-MIXING LAW below for the ruling that changed it
// and for what that costs at the table.)
export const PRIMERA_COST = { L1: 1, L2: 2, L3: 3 };

// ── DEVIANT COSTS ──────────────────────────────────────────
// ⚠ "BOTH", NOT "MIXED". Heretical Training charges 6 Khao AND 6 Anima
// — the card names two currencies and demands both in full. R107 bars
// SPLITTING one number across two pools; it does not touch a card that
// prints two numbers. Show Stomper (10 Khao + 10 Anima) is the same
// shape. See THE NO-MIXING LAW.
export const HERETICAL_TRAINING_COST = { khao: 6, anima: 6 };

// ═══════════════════════════════════════════════════════════════
// 🪙 R107 · THE NO-MIXING LAW — ONE POOL PER FARE, ALWAYS
// ═══════════════════════════════════════════════════════════════
// caP'n, 2026-08-26, asked whether a fare could be part-Khao and
// part-Anima. Verbatim:
//
//   *"no, that was something you must've added hehe — no mixing for any
//   reason at all unless the card tells you, like U'hltis etc."*
//
// EVERY fare in Terror is paid ENTIRELY in Khao or ENTIRELY in Anima.
// There is no split, no remainder, no "Khao drains first and Anima pays
// the rest". If you cannot cover a cost out of one pool, you cannot pay
// it — pick the other pool or do something else.
//
// ── THE ONE EXCEPTION, AND IT IS NOT AN EXCEPTION ──────────────
// A card whose own printed text names BOTH currencies charges both, in
// full, and always did: Heretical Training (6 Khao AND 6 Anima, R67),
// Show Stomper (10 Khao AND 10 Anima). That is TWO FARES ON ONE CARD,
// not one fare split across two pools, and caP'n's own "unless the card
// tells you" is what licenses it. Nothing about those changed.
//
// ── ⚠ THIS IS A LIVE BALANCE CHANGE, NOT A TIDY-UP ─────────────
// The PRIMERA STAMP FARE (R79) was genuinely mixable, in the code, on
// both sides, for as long as stamps have existed — Shatterfield's
// payPrimeraCost drained Khao then topped up from Anima, NPC.js had a
// whole private till (npcPayPrimeraFare) written BECAUSE the shared
// either/or helper "would decline a fare the rules say is payable", and
// rules.js said "mixable" in as many words. All of that is now wrong.
// A Level 3 stamp costs 3 Khao or 3 Anima; a seat on 2 Khao and 40
// Anima can still press it (from Anima) but a seat on 2 Khao and 2
// Anima can no longer press it at all. Presses get rarer near the
// bottom of both pools, which is exactly where they used to be free.
//
// Rulez v2's *"You may mix"* line for Resurrection is superseded by the
// same sentence — and the shipping board never mixed there anyway.
export const FARE_POOLS = ["khao", "anima"];

/**
 * THE one predicate for a single-number fare. `amount` must come out of
 * ONE pool. Returns which pools can carry it, in the order a payer
 * should be offered them (Khao first — Anima is a life total).
 *
 * `animaExempt` forwards to R96's till law: a VOLUNTARY payment may not
 * take its payer to 0, so the Anima leg needs strictly more than the
 * fare. Only the Graveshift is exempt (R41).
 */
export function farePoolsFor(amount, { khao = 0, anima = 0, animaExempt = false } = {}) {
  const need = Math.max(0, Number(amount) || 0);
  if (need === 0) return ["khao", "anima"];
  const out = [];
  if ((Number(khao) || 0) >= need) out.push("khao");
  if (canPayVoluntary(Number(anima) || 0, need, { exempt: animaExempt })) out.push("anima");
  return out;
}

/** R107 — can this fare be paid at all, out of one pool or the other? */
export const canPayFare = (amount, pools = {}) => farePoolsFor(amount, pools).length > 0;

// ═══════════════════════════════════════════════════════════════
// 🔀 R101 · THE DEVIANT FARE — ONE TABLE, FOUR CONSUMERS
// ═══════════════════════════════════════════════════════════════
// Rulez v2, Chapter 15, confirmed TWICE in its own ⚠ CAP'N CHECK
// blocks — once for Ascending and once for Training, in identical
// words:
//
//   *"Full Deviant Ascending schedule, confirmed. 1 trait shared:
//   L1 = 1 Chao or 1 Anima, L2 = 2 Chao or 2 Anima, L3 = 3 Chao or
//   3 Anima. 0 traits shared: L1 = 2 Chao or 2 Anima, L2 = 3 Chao
//   or 3 Anima, L3 = 5 Chao or 4 Anima."*
//
//   *"Deviant Training uses the same schedule as Deviant Ascending.
//   ... (The duplicated 'Level 1' label in the source was a typo for
//   Level 2.)"*
//
// (Rulez v2 says "Chao". The resource is now called KHAO. The word
// was retired; the number was not.)
//
// ⚠ THE LEVEL-3 / 0-TRAIT ROW IS DELIBERATELY ASYMMETRIC — 5 Khao OR
// 4 Anima. Every other row is a single number payable in either pool.
// This one is not, and it is not a typo: it is the only place in the
// game where Anima is the CHEAPER currency, which is exactly the sort
// of thing a well-meaning "simplification" flattens. So the table
// stores a PAIR per cell, and every consumer reads the pair.
//
// ── WHICH LEVEL? THE DESTINATION. ──────────────────────────────
// For Deviant TRAINING the level is unambiguous: it is the attached
// Impera's own printed level. For Deviant ASCENDING three
// implementations disagreed — Shatterfield's two helpers charged by
// the level being ASCENDED TO, RulesEnforcer.validateAscend charged
// by the level being ascended FROM. A 0-trait L1→L2 climb therefore
// cost 3 on the board and 2 at the gate, and whichever ran second
// won. Read it the same way Training reads it: THE LEVEL OF THE
// ESCENDANT BEING PLACED — the level you are ascending TO.
//
// ⚠ ⚠ THE "1 TRAIT SHARED" COLUMN IS UNREACHABLE WITH TODAY'S CARDS.
// src/cards.js pairs Ilk and School 1:1 across the entire shipped set
// (every Humanoid is Space, every Beast is Phantom, every Machine is
// Time, every Dragon is Dragon), so `sharedTraits` — which counts a
// School match and an Ilk match — can only ever return 0 or 2. NEVER 1.
// The whole cheaper column is dead data today, and Deviant Transcend
// (School matches, Ilk does not) is unreachable for the identical
// reason. The table is still the right thing to encode: it is the
// designer's schedule, it costs nothing to carry, and the day a card
// crosses the pairing it is already correct. But nobody should read
// this file and believe the cheap column is doing work. IT IS NOT.
//
// Keyed [sharedTraits][level]. 2 shared traits is a NATURAL action and
// is free (R20) — it lives here so a consumer never has to special-case
// the natural leg outside the table.
export const DEVIANT_FARE = {
  2: { 1: { khao: 0, anima: 0 }, 2: { khao: 0, anima: 0 }, 3: { khao: 0, anima: 0 } },
  1: { 1: { khao: 1, anima: 1 }, 2: { khao: 2, anima: 2 }, 3: { khao: 3, anima: 3 } },
  0: { 1: { khao: 2, anima: 2 }, 2: { khao: 3, anima: 3 }, 3: { khao: 5, anima: 4 } },
};

// THE one reader. `shared` is 0, 1 or 2 (School match + Ilk match);
// `level` is the DESTINATION level for an Ascend and the Impera's own
// level for a Training. Out-of-range input clamps rather than throws —
// a fare table that returns `undefined` becomes NaN Khao at a till.
export function deviantFare(shared, level) {
  const s = DEVIANT_FARE[Math.max(0, Math.min(2, Number(shared) || 0))] || DEVIANT_FARE[0];
  const l = Math.max(1, Math.min(3, Number(level) || 1));
  return { ...s[l] };
}

// ── R66 / R102 · HOW MANY DEVIANT ACTIONS PER TURN ─────────────
// Rulez v2 Chapter 4 (Main Phase): *"Ascend one Escendant per turn
// (one total, regardless of Natural or Deviant)"* and *"Perform one
// Deviant Training, one Deviant Ascension, and any number of Deviant
// Transcends per turn."* The glossary says it a second time:
// *"Ascend — ... Natural Ascending is free; Deviant Ascending costs
// Chao or Anima. Once per turn."*
//
// So the Ascend budget is ONE PER TURN TOTAL and the Natural/Deviant
// distinction buys no extra climb. Transcends are unlimited. Training
// is one Deviant Training per Turn (Natural Trainings are unlimited —
// Chapter 4 lists Impera attachment with "no per-turn limit").
export const PER_TURN_DEVIANT_LIMITS = {
  ascend: 1,        // R18/R66 — ONE TOTAL, Natural or Deviant
  deviantTraining: 1,
  deviantTranscend: Infinity,
};

// ── EFFECT TAG SEMANTICS ───────────────────────────────────
// Tomes with these tags DO NOT ruin after use (R39 exception)
export const PERSISTENT_TOME_TAGS = ["OPT", "OP2", "OP3", "ATV"];

// ═══════════════════════════════════════════════════════════════
// 🏮 R105 · THE ACTIVATION FARE IS PAID EVERY TIME
// ═══════════════════════════════════════════════════════════════
// ⚠ THIS RULE WAS OVERTURNED ON 2026-09-13 AND READS THE OPPOSITE OF
// WHAT IT READ BETWEEN 2026-08-26 AND THEN. caP'n, asked directly
// whether a recurring card re-pays, verbatim:
//
//   *"If you mean a card like OPT etc then yes, you must pay its fare
//   again, thats the cost of it being able to stay on the field
//   'indefinitely' unlike other cards that are typically one use like
//   most Tomes etc."*
//
// So the economy is the plain one: **a Tome or Curio pays its printed
// fare on EVERY activation, first and last alike.** The privilege a
// persistent card buys is that it STAYS — not that it becomes free. An
// OPT Tome at 6 Khao costs 6 Khao every Turn you fire it, forever, and
// that price is what the OPT tag is for.
//
// ── WHAT THIS REPLACES, SAID OUT LOUD (§2.-1, THE RULING SWEEP LAW) ──
// The 2026-08-26 reading was "paid once and that's it", taken from
// caP'n's answer to a *different* question — a bug report about the
// engine double-charging — and it was built into `activationFare`, a
// `firstActivation` flag, a `REPEAT_FARE_TEXT` matcher that matched no
// shipped card, and a Rulebook line. All of it is gone. A rule this
// file once stated in bold and now states inverted is exactly the shape
// §2.-1 exists to make impossible to miss, so the old text is NOT left
// standing beside the new: it is deleted, and this paragraph is its
// only remains.
//
// ── AND THE COST IS BOTH NUMBERS WHEN A CARD PRINTS BOTH (2026-09-13) ──
// caP'n, on the U'hltis: *"if it has a number there its both… so that
// particular card uses 10 Khao and 10 Anima, most U'hltis (if not all)
// use both because they are such powerful Arts."* All twelve U'hltis
// print both, as do four Tomes. This is NOT the split R107 forbids:
// R107 forbids paying ONE number out of two pools. A card that prints
// two numbers is naming both pools itself, which is the exemption R107
// has always carried.
//
// ── AND LYF IS NEVER A FARE (2026-09-13) ────────────────────────────
// caP'n: *"the costs at the bottom right and/or bottom left (assuming
// Khao and Anima, sometimes Tomes etc can have Lyf bottom left, you
// cannot pay Lyf so only Anima/Khao) absolutely must be paid for at
// least once."* Twenty-odd Tomes and Curios carry a `lyf` number —
// that is the Treasure's own durability, printed in the same corner,
// and it is NOT money. `printedFare` reads `khaoCost` and `animaCost`
// and nothing else, on purpose, and a future reader who adds `lyf` to
// it is reintroducing an unpayable currency.

/**
 * THE printed fare of a card, in both pools. Khao and Anima are charged
 * INDEPENDENTLY — each out of its own pool — because a card that prints
 * two numbers names two pools (see R107's exemption above).
 */
export const printedFare = (card) => ({
  khao:  Math.max(0, Number(card?.khaoCost)  || 0),
  anima: Math.max(0, Number(card?.animaCost) || 0),
});

/**
 * R105 — what a Tome/Curio activation costs RIGHT NOW. It costs the
 * printed fare. Every time. There is no `firstActivation` any more and
 * there is deliberately no option object: a caller that wants a
 * discount has to invent it in the open rather than pass a flag.
 *
 * Returns { khao, anima, free, reason }. `free` stays in the shape
 * because call sites branch on it, and it is now true for exactly one
 * reason — the card prints nothing.
 */
export function activationFare(card) {
  const { khao, anima } = printedFare(card);
  return { khao, anima, free: khao === 0 && anima === 0, reason: "" };
}

// ═══════════════════════════════════════════════════════════════
// 💰 R128 · WHEN AN IMPERA'S PRINTED NUMBER IS PAID
// ═══════════════════════════════════════════════════════════════
// caP'n, 2026-09-14, asked outright where an Impera's Khao is charged —
// at the Training, at the firing, or both — and ruled, verbatim:
//
//   "To keep consistency, we are going to make the user pay to use an
//    Impera if it has OPT, OP3, etc type acronyms. If it doesn't have
//    them, then you only pay once, if it does, you must pay to activate
//    again"
//
// THE LAW, in the order the money moves:
//
//   1. A NATURAL TRAINING PAYS THE CARD'S PRINTED FARE. Always, tagged
//      or not. This is not new — RULEZ_V2 ch.10 has said it the whole
//      time ("That is a Natural Training: pay the card's own fare and
//      it is done") — it is the BOARD that was not charging it. A
//      Deviant Training pays the R101 Deviant table INSTEAD, never both:
//      the book offers them as alternatives and so does this.
//
//   2. IF THE IMPERA CARRIES A CADENCE TAG, IT PAYS THE PRINTED FARE
//      AGAIN ON EVERY ACTIVATION. That is R105, already enforced at the
//      soul-card till. The word in caP'n's ruling is "again", and this
//      is what it is again to.
//
//   3. IF IT CARRIES NO TAG, THE TRAINING PAYMENT IS THE ONLY PAYMENT
//      THE CARD WILL EVER TAKE. An untagged Impera is a passive — there
//      is no activation to charge — so "you only pay once" resolves to
//      once, at the Training.
//
// ⚠ WHAT THIS FIXED, AND IT WAS THE LARGEST SINGLE NUMBER IN THE GAME.
// Until 2026-09-14 the board charged NOTHING for a Natural Training
// (`Shatterfield.handleAttachImpera` put the whole fare block inside its
// `if (!isNatural)` branch, and `NPC.trainOneImpera` touched no pool at
// all), so a full Impera set — 6 + 7 + 10 Khao by the tutorial's own
// script — attached for free. And an untagged Impera such as Good
// Morning, with `effectTag: null`, was therefore **free forever**: never
// charged at the Training because the branch skipped it, never charged
// at activation because a passive is never activated. The tutorial had
// been telling players the truth and the game had been lying to them.
//
// ⚠ AND IT IS IMPERA ONLY. U'hltis and Affinities still ATTACH free and
// pay at activation — every one of them carries a tag, so every one of
// them pays every firing, which is already consistent. caP'n's ruling
// names the Impera and nothing else; widening it to U'hltis would make
// Show Stomper cost 10 Khao + 10 Anima to attach AND again to fire,
// which he did not ask for. Flagged to him rather than assumed.
export const imperaPaysOnActivation = (card) => !!(card && card.effectTag);

// ═══════════════════════════════════════════════════════════════
// 🍬 R129 · THE TWO ATV TREASURES, AND WHAT THEY EAT
// ═══════════════════════════════════════════════════════════════
// caP'n, 2026-09-14, describing the whole life of one of them:
//
//   "you(the player) can only feed it, no one else, then once you have
//    fed the necklace 6 Khao (so you just give it Khao and it stores it
//    for you, if the card is ruined the Khao is gone) then once its full
//    of 6 of the owner's Khao, the owner gets 6 Anima. That Tome has 3
//    Lyf, so it remains on the board (most if not all treasures behave
//    this way) until ruined by Lyt attack or other means"
//
// and, asked whether it may be filled a second time:
//
//   "yes, it can be fed after use, it needs to be ruined for it to be
//    taken off the shatterfield"
//
// THE LAW, and every clause is a separate check:
//   · the food is the card's PRINTED currency — Khao for Joyous Courage,
//     Anima for Congealed Death. Read it here; never from a caller.
//   · `perTurn` is a real ALLOWANCE, not one feed per Turn.
//   · ONLY THE OWNER may feed it, on their own Turn.
//   · the food is STORED ON THE CARD and is LOST if the card is ruined.
//     Never refunded. That risk is what the card sells.
//   · at `threshold` the payout fires — and THE TREASURE STANDS. It is
//     not voided by paying out.
//   · THE STORE THEN CLEARS AND IT MAY BE FILLED AGAIN. It leaves the
//     Shatterfield only when something ruins it.
//
// ⚠ A TREASURE'S PRINTED `lyf` IS DURABILITY, NOT A FARE — "most if not
// all treasures behave this way". That is the same reading R105 already
// carries ("you cannot pay Lyf so only Anima/Khao"), generalised by the
// designer to the whole type. Twenty-odd Treasures print a lyf number.
//
// ⚠ AND THE FOOD IS NOT A SECOND FARE. The printed `khaoCost` is what it
// costs to PLAY the Treasure; the food is the card's own mechanic on top.
// R105's naming of ATV is about a card that FIRES again, and feeding is
// not a firing. The board had this right and the brain was charging twice.
export const ATV_FEED = {
  tome_necklace_death:   { currency: "anima", perTurn: 4, threshold: 8 },
  tome_necklace_courage: { currency: "khao",  perTurn: 3, threshold: 6 },
};

/** Is this card one of the two fed Treasures? */
export const isFedTreasure = (card) =>
  !!(card && Object.prototype.hasOwnProperty.call(ATV_FEED, card.id));

/** How much more may be fed to this Treasure THIS Turn. */
export const feedAllowanceLeft = (card, tome, turn) => {
  const spec = ATV_FEED[card?.id];
  if (!spec) return 0;
  const spentThisTurn = (tome?.fedThisTurn === turn) ? (tome?.fedAmountThisTurn || 0) : 0;
  const room = Math.max(0, spec.threshold - (tome?.fed || 0));
  return Math.max(0, Math.min(spec.perTurn - spentThisTurn, room));
};

/**
 * R128 — what a NATURAL Training of this card costs. The printed fare.
 * A Deviant Training does not call this: it pays `deviantFare` instead.
 */
export function trainingFare(card) {
  const { khao, anima } = printedFare(card);
  return {
    khao, anima,
    free: khao === 0 && anima === 0,
    // Stated so a call site can explain the charge in the toast rather
    // than taking the pools silently — the commonest complaint about a
    // fare is not knowing it was charged.
    paysAgain: imperaPaysOnActivation(card),
  };
}

/**
 * R105 + R96 — can this seat pay a card's printed fare right now?
 * Khao must cover the Khao half outright; the Anima half is a VOLUNTARY
 * payment and may never take the payer to 0 (R96), so it needs strictly
 * more than the number unless exempt.
 */
export function canPayPrintedFare(card, { khao = 0, anima = 0, animaExempt = false } = {}) {
  const f = printedFare(card);
  if ((Number(khao) || 0) < f.khao) return false;
  if (f.anima > 0 && !canPayVoluntary(Number(anima) || 0, f.anima, { exempt: animaExempt })) return false;
  return true;
}

// ═══════════════════════════════════════════════════════════════
// 🕯 ATV = ACTIVE TILL VOID (caP'n, 2026-08-26)
// ═══════════════════════════════════════════════════════════════
// The expansion printed in the Rulebook and repeated in the code was
// "Active or Total Void", which is not what the letters stand for and
// is not what the tag does. caP'n, verbatim:
//
//   *"ATV = Active Till Void — that's why it's 'active' UNTIL it's
//   'voided'. Just as it implies: it can stay on the field (active)
//   until it's voided; if it's voided it is no longer active and can no
//   longer stay on the field."*
//
// So the definition is a LIFECYCLE, not a coin-flip:
//   · while ACTIVE, the card stays on the field and keeps working;
//   · when it is VOIDED it is no longer active, and it LEAVES THE FIELD.
// Voiding is the end of the card, not a pause in it. That is why ATV
// sits in PERSISTENT_TOME_TAGS beside OPT/OP2/OP3 — it survives its own
// use — and why it is the only one of the four with a printed way to
// die that is not damage.
export const ATV = "ATV";
export const ATV_NAME = "Active Till Void";
export const ATV_MEANING =
  "Stays on the field while ACTIVE; the moment it is VOIDED it is no longer active and leaves the field.";

// ═══════════════════════════════════════════════════════════════
// 🔎 R106 · A LUXMOIRE SEARCH FINDS IMPERA. ONLY IMPERA.
// ═══════════════════════════════════════════════════════════════
// caP'n, 2026-08-26, verbatim:
//
//   *"Yes it costs your draw, it's one or the other (because we added
//   the U'hlti and Affinity into the Lux we can't have it so easy). And
//   just to make sure — you can still only search the Lux for Impera,
//   not the U/A's."*
//
// TWO CLAUSES, BOTH LAW:
//   1. THE SEARCH SPENDS THE TURN'S DRAW. One or the other — search or
//      draw, never both. (Already enforced on both sides; Rulez v2 ch.3
//      and ch.11 say "this does not consume your turn's draw" TWICE and
//      both lines are SUPERSEDED.)
//   2. THE SEARCH MAY ONLY FIND AN IMPERA. Never an U'hlti, never an
//      Affinity — even though both now live in the Luxmoire. They are
//      drawn, not tutored. The reason is in the ruling itself: putting
//      them in the Lux would otherwise have made them "so easy".
//
// A CARD EFFECT that names its own target is not this search and is not
// bound by it — it is bound by its own printed text.
export const LUX_SEARCHABLE_TYPES = ["Impera"];
export const isLuxSearchable = (card) =>
  !!card && LUX_SEARCHABLE_TYPES.includes(card.type);

// ═══════════════════════════════════════════════════════════════
// ⏱ R103 · A FULL TURN IS TWO HALF-TURNS — THE COOLDOWN CADENCE
// ═══════════════════════════════════════════════════════════════
// caP'n, 2026-08-26, verbatim:
//
//   *"If I make a general statement like once per turn, especially the
//   'OPT or OP2' etc, it is referring to combination of the user who
//   played the card (1/2 turn) ending then the turn of their opponent
//   ending, thus creating 1 full turn. But if I say at the end of the
//   user's turn, that is the half turn."*
//
// Rulez v2 Chapter 18 (Acronyms & Shorthand) is what he is defining:
//
//   *"OPT — Once Per Turn. The card's effect may activate once per
//   turn. After first activation, the card is not ruined; it remains
//   in play and may activate again next turn."*
//   *"OP2 — Once Per 2 Turns. The card's effect may activate once
//   every two turns. The first activation pays the activation fare;
//   subsequent activations do not re-pay it."*
//   *"OP3 — Once Per 3 Turns. The card's effect may activate once
//   every three turns. Same rules as OP2 regarding fare."*
//
// ⚠ THE FARE HALF OF THAT QUOTE IS SUPERSEDED AND THE QUOTE IS LEFT
// WHOLE ANYWAY, because it is caP'n's own words and quotes are not
// edited. On 2026-09-13, asked directly, he ruled that a persistent
// card re-pays on every activation — see R105 above. What survives from
// this quote is the CADENCE, which is what this banner is about.
//
// ── WHAT WAS WRONG ─────────────────────────────────────────────
// `match.turn` counts HALF-turns — one seat acting — and it always
// has (see THE OPENING CYCLE banner above; gameManager writes 0 and
// every hand-off increments by one seat). Both cooldown consumers
// compared a half-turn delta against the RAW tag number: OPT → 1,
// OP2 → 2, OP3 → 3. In 1v1 that made OPT mean "once per half-turn"
// (i.e. every time your turn comes round — accidentally right) and
// OP2 mean "once per full turn", which is what OPT was supposed to
// mean. OPT and OP2 were one half-turn apart instead of two, and at
// the seat's own cadence they were INDISTINGUISHABLE: an OPT tome and
// an OP2 tome both fired on every one of its Turns.
//
// ── THE DEFINITION ─────────────────────────────────────────────
// A FULL TURN IS TWO HALF-TURNS: the card's controller acting, then
// its adversary acting. Once-per-turn = 2 half-turns. Once-per-2-turns
// = 4. Once-per-3-turns = 6.
//
// ⚠ AT A 3-4 SEAT TABLE "A FULL TURN" IS A FULL ROUND OF THE TABLE.
// The designer's sentence describes 1v1 because that is the shape he
// was holding — "the user ... then the turn of their opponent". The
// generalisation is the same one THE OPENING CYCLE already makes for
// R08: the unit is EVERY SEAT ACTING ONCE, which is `seatCount`
// half-turns, and `openingCycleLength` is the function that already
// says so. So the two laws carry the seat count identically and a 4p
// OPT tome fires once per lap of the table, not four times.
//
// This is the ONE place the cadence is defined. Shatterfield's
// isTomeOffCooldown and NPC.js's persistent-tome re-activation gate
// both call it; neither carries a ternary of its own any more.
export const COOLDOWN_FULL_TURNS = { OPT: 1, OP2: 2, OP3: 3, ATV: 1 };

// How many HALF-turns are in one FULL turn at this table. 1v1 → 2.
// A 3-seat FFA → 3. A 2v2 → 4. Same predicate as R08's window, so the
// two can never drift apart.
export const halfTurnsPerFullTurn = (seatCount) => openingCycleLength(seatCount);

// ── R91 / R132 · THE CLIMB FLOOR, ONCE ────────────────────────────
// The first half-turn index at which a seat sitting down may receive the
// natural +1 Max Khao. R91 says never on the opening Turn; R132 says the
// climb pays when a seat SITS DOWN after a full quiet Turn — and a full
// Turn at a 3–4 seat table is a full LAP. `Math.max` because the lap can
// only ever RAISE R91's floor, never lower it: byte-identical at 1v1
// (FIRST_KHAO_TURN is 2 and so is the lap), strictly later beyond it.
// ⚠ 2026-09-15 unification pass: the seam (turnEngine.handOffHalfTurn)
// held this expression inline, and RulesEnforcer.validateKhaoRefill held
// a WEAKER copy still on the two-seat approximation — so the validator
// would have blessed a climb the seam refuses at a 3–4 seat table. Both
// read this now. The Debugger keeps its OWN copy of the expression on
// purpose: it is the independent second implementation, and a checker
// that imports the thing it checks can no longer disagree with it.
export const khaoClimbFloor = (seatCount = 2) =>
  Math.max(FIRST_KHAO_TURN, halfTurnsPerFullTurn(seatCount));

// The cooldown, IN HALF-TURNS, for an effect tag. This is the number
// `match.turn - lastUsedTurn` must reach. An unknown/absent tag falls
// back to one full turn, which is the safe direction: a mislabelled
// card recurs too slowly rather than every half-turn.
export function cooldownHalfTurns(effectTag, seatCount = 2) {
  const fullTurns = COOLDOWN_FULL_TURNS[effectTag] ?? COOLDOWN_FULL_TURNS.OPT;
  return durationHalfTurns(fullTurns, seatCount);
}

// ═══════════════════════════════════════════════════════════════
// 🕮 R105b · THE CADENCE TAGS BIND THE SOUL TOO (caP'n, 2026-09-13)
// ═══════════════════════════════════════════════════════════════
// Asked whether OPT/OP2/OP3 are real on every card or only on Tomes,
// caP'n, verbatim: *"every card, acronyms like OPT OP3 etc are real,
// they are not just there to look cool."*
//
// They were enforced on TOMES ONLY. Twenty-nine of the thirty-six
// Impera, ten of the twelve U'hltis and all four Affinities carry a
// cadence tag, and every one of them could be fired as many times in a
// half-turn as the seat could pay for — which, with R105 now charging
// the fare every time, is "as many times as you have Khao".
//
// ── THE STAMP IS A MAP ON THE SLOT, NOT A FLAG ────────────────
// `slot.soulUsed = { [cardId]: halfTurnItLastFired }`. A map because a
// decorated being wears up to five activatable cards with three
// different cadences, and one `lastUsedTurn` on the slot would have
// made the L1 Impera's OPT govern the U'hlti's OP3. The value is the
// same `match.turn` half-turn count `lastUsedTurn` holds on a Tome, so
// the SAME cooldownHalfTurns answers both and the two can never drift.
//
// An untagged card (the seven passive Impera, the two passive U'hltis)
// has no activation to gate — those are read, never pressed — but the
// fallback in cooldownHalfTurns is one full turn either way, so a card
// that acquires a tag later is gated the moment it does.

/** R105b — may this soul card fire on this half-turn? */
export function soulCardOffCooldown(slot, cardId, effectTag, turn, seatCount = 2) {
  const last = slot?.soulUsed?.[cardId];
  if (last == null) return true;                       // never fired
  const now = Math.floor(Number(turn)) || 0;
  return (now - (Math.floor(Number(last)) || 0)) >= cooldownHalfTurns(effectTag, seatCount);
}

/** R105b — the stamp to write after it fires. Returns a NEW map. */
export const stampSoulUsed = (slot, cardId, turn) => ({
  ...(slot?.soulUsed || {}),
  [cardId]: Math.floor(Number(turn)) || 0,
});

// ═══════════════════════════════════════════════════════════════
// ⏳ R103, SECOND HALF · "2 TURNS" IS 4 HALF-TURNS — DURATIONS
// ═══════════════════════════════════════════════════════════════
// caP'n, 2026-08-26, verbatim, and he asked for this one to be nailed
// down for every card yet to be written:
//
//   *"if it's just broad '2 turns' that means 2 full turns which
//   consists of 4 × 1/2 turns. That should clear up a lot of confusion
//   in the future too — make sure you nail this point in for the future
//   when we make cards, you'll know exactly what I mean already."*
//
// ⭐ THE RULE FOR EVERY CARD EVER WRITTEN, IN ONE SENTENCE:
//
//     A NUMBER OF "TURNS" ON A CARD IS ALWAYS FULL TURNS.
//     ONE FULL TURN = EVERY SEAT ACTING ONCE = `seatCount` HALF-TURNS.
//     1v1: "2 turns" = 4 half-turns. NEVER 2.
//
// The ONLY way to mean a half-turn is to SAY so — "at the end of your
// turn", "this turn", "until your next turn" — which is the same
// distinction he drew for the cadence tags above ("but if I say at the
// end of the user's turn, that is the half turn"). Broad number of
// turns → full turns. Named boundary → that boundary.
//
// ── WHY IT LIVES IN THIS FUNCTION AND NOT IN THE CARDS ─────────
// It is the SAME LAW as the OPT/OP2/OP3 cadence, applied to how long
// something LASTS instead of how often it RECURS, so it must not be a
// second implementation of the same arithmetic. Divine Justice and
// Phoenix Aura both wrote `(match.turn || 0) + 2` at their call sites —
// a raw half-turn offset that made a "2 turn" ward last ONE full turn,
// exactly half of what the card says. Both now route through here, so a
// duration and a cooldown cannot drift apart: change
// halfTurnsPerFullTurn and every clock in the game moves together.
//
// ⚠ THE SAME V'AEL GENERALISATION IS FLAGGED HERE AS ON THE COOLDOWN.
// caP'n's sentence describes 1v1. Reading "a full turn" at a 3-4 seat
// table as a full ROUND of the table is R08's unit, borrowed.

/** HALF-turns in a duration expressed in FULL turns. 1v1: 2 → 4. */
export const durationHalfTurns = (fullTurns, seatCount = 2) =>
  Math.max(0, Math.floor(Number(fullTurns) || 0)) * halfTurnsPerFullTurn(seatCount);

// ═══════════════════════════════════════════════════════════════
// 🕰 R35 · THE WAIT BELONGS TO THE FACE-DOWN SET, NOT TO THE CARD
// ═══════════════════════════════════════════════════════════════
// caP'n, 2026-09-13, asked whether a Level 1 Tome may be activated
// straight out of hand on your own turn, verbatim:
//
//   *"Yes we can, but if you place it face down, you must wait 1 full
//   turn to activate it."*
//
// So the wait is a price on SETTING, not a property of the card. An L1
// Tome played face-up from hand on your own turn resolves immediately;
// an L1 Tome set face-down into a rift is latent for one FULL Turn.
//
// ⚠ TWO THINGS WERE WRONG AND THEY HID EACH OTHER.
//
//   1 · THE WAIT WAS UNCONDITIONAL. `validateTomeActivation` charged it
//   off `card.level` alone, and Shatterfield's hand-play path asks the
//   gate with `turnsOnField: 0` — so playing an L1 Tome face-up from
//   hand was REFUSED OUTRIGHT. The rulebook has said "activate it from
//   hand by paying the fare" since Chapter 13 was written.
//
//   2 · THE NUMBER WAS IN THE WRONG UNIT. TOME_WAIT.L1 was 1 and every
//   consumer compares it against a HALF-turn delta (`match.turn -
//   placedTurn`), so "a full Turn" was being enforced as one half-turn
//   — the set Tome woke on the adversary's half-turn instead of on the
//   seat's own next Turn. It is declared in FULL TURNS now and routed
//   through durationHalfTurns, which is the same function R103 uses, so
//   it generalises to a 3-4 seat table for free.
//
// L2 and L3 keep their zero: the book gives L2 "including the turn it
// was set" and L3 the hand interrupt, and neither was in question.
export const TOME_WAIT_FULL_TURNS = { L1: 1, L2: 0, L3: 0 };

/**
 * R35/R36/R37 — how many HALF-turns a Tome must sit before it may fire.
 *
 * `faceDown` is the whole rule: a Tome played face-up from hand is
 * being activated, not set, and a set Tome is what waits. Absent, it
 * defaults to TRUE — the safe direction, because a caller that forgot
 * to say gets the stricter answer rather than a free interrupt.
 */
export function tomeWaitHalfTurns(level, { faceDown = true, seatCount = 2 } = {}) {
  if (!faceDown) return 0;
  const full = TOME_WAIT_FULL_TURNS[`L${level || 1}`] ?? 0;
  return durationHalfTurns(full, seatCount);
}

// ⚠ THE OLD NAME, KEPT AS A DERIVED VIEW AND NOT AS A SECOND TRUTH.
// `TOME_WAIT` was a bare map of half-turn numbers that four files read
// directly (the Debugger reads it in three places and ASSERTS against
// it, which is what makes it worth keeping rather than deleting). It is
// now COMPUTED from the full-Turn table at the 1v1 cadence, so it
// cannot drift from the function above: change the table and both move.
export const TOME_WAIT = {
  L1: tomeWaitHalfTurns(1), L2: tomeWaitHalfTurns(2), L3: tomeWaitHalfTurns(3),
};

/**
 * The `match.turn` at which a duration of `fullTurns` has run out —
 * i.e. the last half-turn on which the effect is still standing.
 * THE ONE PLACE a card's "for N turns" becomes a number on the board.
 */
export const durationExpiryTurn = (nowTurn, fullTurns, seatCount = 2) =>
  (Math.floor(Number(nowTurn)) || 0) + durationHalfTurns(fullTurns, seatCount);

/** Is a duration stamped by durationExpiryTurn still running at `turn`? */
// ⚠ EXCLUSIVE, BY RULING — caP'n, 2026-09-14, verbatim:
//
//   "3 turns or any type of broad reference always refers to a full turn;
//    I will specify with words like 'on your turn' or 'your opponent's
//    turn' something like that, but 'for 3 turns' is way too broad and
//    that means 6 half turns, once the 6 half turns are over, you can
//    proceed"
//
// So a bare "N turns" is N FULL Turns = N x halfTurnsPerFullTurn, and the
// effect is DONE once those half-turns have passed. This comparison was
// `<=` until that ruling, which ran every duration in the game ONE
// HALF-TURN LONG: a 3-Turn loan stamped at half-turn t was "still
// running" at t..t+6 — seven half-turns, not six.
//
// ⚠ IT WAS FLAGGED, NOT GUESSED. Two places called the inclusive reading
// intended (`Shatterfield`'s own comment and a canon0826 assertion) and
// one helper disagreed (`tomeIsIntangible`, already exclusive), so the
// answer was a BALANCE change either way and it went to caP'n rather
// than to a grep. `tomeIsIntangible` now agrees with this by accident of
// having been right first.
//
// ⭐ AND THE NAMED BOUNDARIES ARE UNTOUCHED. "At the end of the turn",
// "on your turn", "the turn it was constructed" are SPECIFIC and stay
// half-turns — caP'n draws that line himself in the ruling above. Only a
// bare number of turns is broad.
export const durationStillRunning = (turn, expiryTurn) =>
  expiryTurn != null && (Math.floor(Number(turn)) || 0) < expiryTurn;

// ═══════════════════════════════════════════════════════════════
// 🕳 THE EXILE WAVE — caP'n, 2026-09-06/07
// ═══════════════════════════════════════════════════════════════
// One session, one message, and the largest single change Terror has
// taken since it went digital. Everything from here to the phase gate
// is that wave. It arrives as five things that lean on each other:
//
//   R110  THE FOURTH RIFT       — the battle line is four, not three.
//   R111  EXILIATION            — an Art every Aenarchist knows.
//   R112  THE EXILE RIFT        — where an exiled card goes, and stays.
//   R113  THE RITUAL PROPER     — buying one back, once and once only.
//   R114  THE HOUSE ROUNDS      — every rounding goes against the actor.
//   R115  THE ROLE RIFTS        — four Arts, one per battle rift.
//   R116  THE TERROR ART        — the DPS rift.
//   R117  THE HEALER ART        — the free return.
//   R118  THE TANK ART          — the shield and the dulled swing.
//   R119  THE WARDEN ART        — the untouchable that is not a wall.
//   R120  AN ART IS NOT A CARD  — what may answer one, and what may not.
//
// ⚠ EVERY ROW IN THIS WAVE IS caP'n's, ruled in the 2026-09-06 session
// and its 2026-09-07 follow-ups. Where a leg of one is V'ael's reading
// rather than his words, it is flagged in RULE_PROVENANCE and it is
// flagged in the row itself. Nothing here was invented quietly.

// ── R110 · THE FOURTH RIFT ─────────────────────────────────────
// The battle line grew from three rifts to four, because the four Role
// Arts (R115) need somewhere to live and a rift is where they live.
// caP'n, 2026-09-07: *"starter decks we're going keep everything exactly
// the same, just give the player 4 esclings now and update the rules to
// match. the min max etc everything stays the same, just 4 in beginning
// instead of 3."*
//
// So: FOUR pre-placed Esclings, one per battle rift. Grimoire minimum,
// Luxmoire range, every per-name deck limit, every fare — untouched.
// ESCLING_SLOTS is the one number that moved, and it moved at the top of
// this file so that every consumer moved with it.

// ── THE RIFT COUNT, AND THE ARITHMETIC NOBODY DID ──────────────
// ⚠ RULEZ_V2 SAID "ELEVEN RIFTS" IN FIVE PLACES AND ITS OWN TABLE HAS
// ALWAYS LISTED TWELVE: Prophecy 1 + battle 3 + Necropolis 1 + Grimoire 1
// + Luxmoire 1 + Tome 3 + Curio 2 = 12. The eleven was simply wrong, and
// had been since the book was written. Corrected in the same pass rather
// than carried forward, because a number in a rulebook is a promise.
// With the fourth battle rift and the Exile rift the true count is 14.
export const EXILE_SLOTS    = 1;
export const RIFTS_PER_SEAT = 14;  // 1 Prophecy · 4 battle · 1 Necropolis · 1 Exile · 1 Grimoire · 1 Luxmoire · 3 Tome · 2 Curio

// ═══════════════════════════════════════════════════════════════
// 🕳 R111 · EXILIATION — THE ART EVERY AENARCHIST KNOWS
// ═══════════════════════════════════════════════════════════════
// caP'n, 2026-09-06, verbatim:
//
//   *"At any point a player can do this, this is a level 3 Deviant
//   U'hltima the Aenarchist knows."*
//
// Read that sentence slowly, because three separate laws are folded into
// it and each one matters.
//
//   1. "THE AENARCHIST KNOWS" — it is not a card. There is nothing to
//      draw, nothing to build into a Book, nothing to attach to a being.
//      Every seat has it from the first Turn to the last. caP'n,
//      2026-09-07, confirming: *"it still requires nothing other than
//      the payment."*
//
//   2. "A LEVEL 3 ... U'HLTIMA" — the level is the TIMING. A Level 3 art
//      in Terror activates on your own turn, on an adversary's turn, and
//      from your hand on an adversary's turn (Rulez v2 ch.12). caP'n,
//      2026-09-07: *"yes that's why I called it a 'level 3' Deviant
//      U'hltima because I have explained how level 3 arts behave."* So
//      "at any point" is not loose talk — it is the Level 3 timing band,
//      written out.
//
//   3. "DEVIANT" — and this is the part that is genuinely new. An
//      U'hltima has always been the Natural being's reward and has always
//      been barred to the Deviant (Rulez v2 ch.15, ch.19). A DEVIANT
//      U'hltima is the other side of that coin: an art the Deviant path
//      knows and the Natural path also knows, because it belongs to the
//      Aenarchist rather than to any being. It is the first attachment-
//      shaped thing in Terror that attaches to nobody.
//
// THE FARE IS THE LEVEL OF THE CARD YOU EXILE, not the level of the art.
// caP'n, 2026-09-07: *"to perform an Exilation this cost is across the
// board for all of them, if you Exile lvl 1 = 2 Khao or 2 Anima, lvl 2 =
// 3 K or 3 A, lvl 3 = 4 K or 4 A."* One pool or the other, never split
// (R107). It does NOT consume your Turn's draw — *"this Primera does not
// eat your draw"* — which is what separates it from a search (R106/R109).
export const EXILE_FARE = { L1: 2, L2: 3, L3: 4 };
export const exileFare = (level) => EXILE_FARE[`L${level || 1}`] ?? EXILE_FARE.L3;

// The cadence: once, then locked for two FULL Turns. caP'n: *"You can
// only perform an Exiliation once per 2 turns starting whenever it gets
// activated."* That is the OP2 tag exactly, so it is spelled as the tag
// and measured by the one helper every cadence in the game is measured
// by (R103) — four half-turns at a two-seat table, a full lap of a
// bigger one.
export const EXILIATION_TAG = "OP2";
export const exiliationCooldown = (seatCount = 2) => cooldownHalfTurns(EXILIATION_TAG, seatCount);

// THE FOUR EXILIATIONS. caP'n, 2026-09-07: *"yes, they are all
// Exhilations and performing one means you cannot perform another for 2
// full turns, can only choose 1."* So this is a menu of four and you take
// exactly one; taking any of them spends the same OP2 clock.
//
// ⚠ THE SECOND OPTION WAS NARROWED BY caP'n ON 2026-09-07 AND THE
// NARROWING IS THE WHOLE POINT. As first written it read "add 1 card from
// either your G or L", which would have tutored an U'hlti or an Affinity
// out of the Luxmoire and driven straight through R106 — his own law that
// a Luxmoire may be searched for Impera and nothing else. He closed it
// himself: *"true let's change it search for 1 Being from G of same level
// or 1 Impera from L of same level."* R106 stands untouched.
//
// "Of the same level" always means the level of the card you EXILED. The
// exiled card is the price and the price sets the ceiling.
export const EXILIATION_OPTIONS = [
  { id: "necro_being",
    exileFrom: "necropolis", findIn: "necropolis", finds: ["Escendant"], sameLevel: true,
    text: "Exile 1 card from your Necropolis — add 1 being of that level from your Necropolis to your hand." },
  { id: "hand_search",
    exileFrom: "hand",       findIn: "books",      finds: ["Escendant", "Impera"], sameLevel: true,
    text: "Exile 1 card from your hand — add 1 being of that level from your Grimoire, or 1 Impera of that level from your Luxmoire, to your hand." },
  { id: "grim_tome",
    exileFrom: "grimoire",   findIn: "necropolis", finds: ["Tome"], sameLevel: true,
    text: "Exile 1 card from your Grimoire — add 1 Tome of that level from your Necropolis to your hand." },
  { id: "necro_impera",
    exileFrom: "necropolis", findIn: "necropolis", finds: ["Impera"], sameLevel: true,
    text: "Exile 1 card from your Necropolis — add 1 Impera of that level from your Necropolis to your hand." },
];
export const exiliationOption = (id) =>
  EXILIATION_OPTIONS.find((o) => o.id === id) || null;
export const EXILIATION_OPTION_IDS = EXILIATION_OPTIONS.map((o) => o.id);

// ⚠ THE HAND_SEARCH OPTION REACHES INTO A BOOK AND IS NOT A SEARCH.
// R08 bars a SEAT from searching either Book on the opening Cycle, and
// has always exempted a card effect that searches — *"it is the card
// doing its printed job, it pays no fare, it does not consume your Turn's
// draw"*. An Art is that same shape: it is the Aenarchist's printed job,
// it pays its own fare rather than a search fare, and caP'n ruled it does
// not eat the draw. So it rides the existing exemption rather than
// needing a second one. THE READING IS V'AEL'S and is flagged in
// RULE_PROVENANCE — caP'n ruled the fare and the draw, not the opening
// Cycle. Reverse it by making this return SEARCH_BY_PLAYER.
export const EXILIATION_SEARCH_BY = SEARCH_BY_EFFECT;

// ═══════════════════════════════════════════════════════════════
// 🕳 R112 · THE EXILE RIFT
// ═══════════════════════════════════════════════════════════════
// caP'n, 2026-09-07: *"yeah new rift literally right above the
// Necropolis."* So the seat gains a twelfth kind of rift, and it sits
// where the eye already goes when a card dies. The Necropolis is where
// the ruined WAIT. The Exile rift is where they are FORGOTTEN.
//
// A card in the Exile rift is out of the Solrend. It is not in a Book, it
// is not in the Necropolis, it cannot be Resurrected, it cannot be
// tutored, it cannot be Graveshifted back into a Grimoire. The only door
// is R113, and that door opens once per card, ever.
//
// ⚠ CONSERVATION. The Exile rift is a DESTINATION, and every card that
// leaves a Book, a hand or a Necropolis by an Exiliation must arrive
// here. This is trap §4.3 with a new place to hide — a card removed with
// no destination write leaves the game — so the between-boards referee
// gets an Exile-rift clause and the conservation multiset counts the
// Exile rift as a pile like any other.
export const EXILE_RIFT_FIELD    = "exile";          // the rift itself: an array of card ids
export const EXILE_RETURNED_FIELD = "exileReturned"; // ids that have spent their one return

// ═══════════════════════════════════════════════════════════════
// 🕳 R113 · THE RITUAL PROPER — BUYING ONE BACK
// ═══════════════════════════════════════════════════════════════
// caP'n, 2026-09-06: *"Exiled cards can never return from their new rift
// unless you pay half your Anima, you must have at least 10 Anima to
// perform this level 3 Ritual the Aenarchist knows."*
//
// And, 2026-09-07, separating the two numbers that sentence runs
// together: *"You need 10 Anima to even have access to that Ritual. Then
// once you have at least 10 Anima, no matter how much even 33, halve it
// if you wish to perform the Ritual proper and retrieve a card from the
// Exile Rift. There is no Anima minimum to perform an Exilation."*
//
// So there are TWO arts here and only one of them has a floor:
//   · Exiliation (R111)  — no Anima floor at all. Pay the card's level.
//   · The Ritual (R113)  — 10 Anima to reach it, then HALF of whatever
//                          you actually hold, rounded against you (R114).
//                          At 33 Anima the fare is 17, not 16.
export const EXILE_RETRIEVAL_ANIMA_FLOOR = 10;
export const canReachExileRift = (anima) =>
  (Number(anima) || 0) >= EXILE_RETRIEVAL_ANIMA_FLOOR;
export const exileRetrievalFare = (anima) => roundAgainstActor((Number(anima) || 0) / 2);

// The cadence is its own, and it is longer. caP'n: *"This is a Ritual so
// its separate but make sure it is OP3."* Three full Turns — six
// half-turns at a two-seat table — and it does not touch Exiliation's
// OP2 clock in either direction.
export const EXILE_RETRIEVAL_TAG = "OP3";
export const exileRetrievalCooldown = (seatCount = 2) =>
  cooldownHalfTurns(EXILE_RETRIEVAL_TAG, seatCount);

// WHERE IT GOES AND WHOSE IT IS. caP'n: *"Card goes to hand yes; only
// ever your cards."* You cannot reach into an adversary's Exile rift at
// any price. What you exiled is what you may buy back.
//
// THE ANIMA IS YOURS AND IT FEEDS NOTHING. caP'n, same message: *"this
// also doesn't count towards the blood prophecy or other cards etc
// because you're self dimming."* That is R100 restated, not a new rule —
// the fare lands on the SELF ledger and no Prophecy may read it. It is
// also a voluntary payment, so the Till Law (R96) binds it: the Ritual is
// REFUSED rather than fatal if half your Anima is all of your Anima.
// Below 10 you cannot reach the rift at all, so in practice the floor
// bites first and the Till Law is the belt beside the braces.
export const EXILE_RETRIEVAL_LOSS_KIND = ANIMA_LOSS_KINDS.SELF;

// ⚠ ONCE EVER, PER CARD — AND caP'n ADDED THIS UNPROMPTED, mid-answer,
// on 2026-09-07: *"also new limitations I just thought of, cards brought
// from Exile no matter the level cannot be resurrected once they get
// placed again and ruined, they are sent to the Exile rift after being
// ruined and can no longer be retrieved, only once ever per card."*
//
// So a card that has come back from exile carries a mark for the rest of
// the Solrend, and the mark says three things:
//   1. When it is ruined again it goes to the EXILE RIFT, not the
//      Necropolis. It never re-enters the pile the Necropolis rites read.
//   2. It can never be Resurrected — no fare, no Tome, no stamp, nothing.
//      This is stricter than the Graveshift generation (R86), which still
//      leaves card effects a door. This leaves none.
//   3. It can never be retrieved from the Exile rift a second time.
//
// The mark is a per-COPY fact, exactly like the Resurrection tally, so it
// is kept the way the tally is kept: a list of ids on the seat, sealed
// ids included, so two copies of the same Beb are two different cards.
// See necroSeal.js — the seal IS the identity, not decoration.
// ── HOW EXACT "PER CARD" CAN ACTUALLY BE, AND WHY ──────────────
// caP'n ruled "only once ever per card". Terror can honour that exactly
// for BEINGS and only approximately for everything else, and the reason
// is structural rather than lazy: necroSeal.js seals Escendants and
// nothing else, by an explicit design decision whose own banner explains
// that keeping the blast radius small is the point. A Tome, an Impera, an
// U'hlti, an Affinity and a Curio are BARE IDS — two copies of one Tome
// in a Necropolis are literally the same string, and no amount of
// bookkeeping here can tell them apart.
//
// So the mark is keyed by whatever identity the card actually has:
//   · an ESCENDANT is marked by its SEALED id — genuinely per copy, the
//     same unit R85's resurrection tally uses.
//   · everything else is marked by NAME. Buy one copy of a Tome back out
//     of exile and every copy of that Tome is marked with it.
//
// caP'n, 2026-09-07, choosing this deliberately over sealing every card
// type: *"1 is very 4xS let's go with that, very strict but at the same
// time, meh ;3"* — and it errs in the safe direction, which is what makes
// it the right approximation. The failure mode is a card that is MORE
// forgotten than the rule demands, never one that sneaks back. It also
// reads correctly in the fiction: the Exile rift forgets a thing once.
//
// `isEscendant` is INJECTED, exactly as necroSeal injects it, so this
// file never learns what a card is. Pass the caller's own getCard-backed
// predicate. Omit it and everything is treated as a bare id, which is the
// stricter reading and therefore the safe default for a caller that has
// not been taught yet.
export const exileKey = (id, isEscendant) => {
  if (id == null) return id;
  const being = typeof isEscendant === "function" ? !!isEscendant(id) : !!isEscendant;
  return being ? String(id) : baseId(id);
};
export const isExileSpent = (id, exileReturned, isEscendant) => {
  if (!Array.isArray(exileReturned) || !exileReturned.length) return false;
  const k = exileKey(id, isEscendant);
  return exileReturned.some((e) => exileKey(e, isEscendant) === k);
};
/** R113 — where does this card go when it is ruined? */
export const ruinDestination = (id, exileReturned, isEscendant) =>
  isExileSpent(id, exileReturned, isEscendant) ? EXILE_RIFT_FIELD : "necropolis";
/** R113 — may this card be Resurrected at all, by any method? */
export const exileBarsResurrection = (id, exileReturned, isEscendant) =>
  isExileSpent(id, exileReturned, isEscendant);

// ── THE ROUTER · ONE PLACE, NOT FORTY-FIVE ─────────────────────
// A card carrying the Mark is ruined INTO the Exile rift rather than into
// the Necropolis. The obvious way to build that is to teach every ruin
// site to ask `ruinDestination` — and there are about forty-five of them
// in the renderer alone, plus the brain's, plus four inside the descent
// funnel itself. Forty-five chances to forget, and the forty-sixth would
// be written next month by somebody who never read this comment.
//
// So it is done the way R08's damage backstop is done, and the pooled
// Anima redirect, and the necro seal: at the ONE function every write
// already passes through. Each driver hands its payload to this on the
// way out, and a ruin site needs to know nothing at all.
//
// HOW IT DECIDES. It compares the Necropolis being WRITTEN against the
// Necropolis that is LIVE and looks only at what is genuinely new — a
// MULTISET diff, not a membership test, because a Grimoire holds five
// copies of a Beb and a Set cannot see one of them arrive. Anything new
// and Marked is lifted out of the Necropolis and appended to the Exile
// rift in the same payload, so the card is never in flight without a
// destination. That is trap 4.3 — a card removed with no destination
// write leaves the game — answered structurally rather than by discipline.
//
// ⚠ IT IS DELIBERATELY INERT WHEN THERE IS NOTHING TO DO. No Mark list,
// no `necropolis` key, or nothing new in it, and the payload is returned
// by reference, untouched. Every ordinary write in the game pays one
// property check for this.
export function routeExileOnWrite(updates, live, isEscendant) {
  if (!updates || !Object.prototype.hasOwnProperty.call(updates, "necropolis")) return updates;
  const marked = live && live[EXILE_RETURNED_FIELD];
  if (!Array.isArray(marked) || !marked.length) return updates;

  const before = Array.isArray(live?.necropolis) ? live.necropolis : [];
  const after  = Array.isArray(updates.necropolis) ? updates.necropolis : null;
  if (!after) return updates;

  const held = new Map();
  before.forEach((id) => held.set(id, (held.get(id) || 0) + 1));

  const keep = [], divert = [];
  after.forEach((id) => {
    const c = held.get(id) || 0;
    if (c > 0) { held.set(id, c - 1); keep.push(id); return; }  // already there — not a new arrival
    if (isExileSpent(id, marked, isEscendant)) divert.push(id); else keep.push(id);
  });
  if (!divert.length) return updates;

  // If the same payload is also writing the rift (a retrieval, say), build
  // on THAT array rather than the live one, or the two writes race and the
  // last one wins.
  const riftNow = Array.isArray(updates[EXILE_RIFT_FIELD])
    ? updates[EXILE_RIFT_FIELD]
    : (Array.isArray(live?.[EXILE_RIFT_FIELD]) ? live[EXILE_RIFT_FIELD] : []);

  return { ...updates, necropolis: keep, [EXILE_RIFT_FIELD]: [...riftNow, ...divert] };
}

// ═══════════════════════════════════════════════════════════════
// 🎲 R114 · THE HOUSE ROUNDS AGAINST YOU
// ═══════════════════════════════════════════════════════════════
// caP'n, 2026-09-07, asked only about halving Anima and answering about
// the whole game instead:
//
//   *"always round up so its against the players favor, think of J'ynxi
//   being the house bending rules to them hehe in every situation it
//   always rounds against the player performing the action."*
//
// This is a STANDING LAW, not a clause of the Ritual. Every rounding in
// Terror resolves against whoever is performing the action. There is one
// helper and everything that rounds calls it, so the law can never be
// half-applied.
//
// ⚠ IT WAS ALREADY TRUE ONCE AND NOBODY HAD NOTICED. The Prophet's Toll
// (R97) halves an adversary's Anima ROUNDED UP, which leaves the victim
// holding MORE — the rounding falls on the Prophet, who is the one acting.
// The Toll has obeyed this law since the day it was written. It simply had
// no name until now, and prophetsToll is left exactly as it is: correcting
// code to match a law it already satisfies is how a coherency pass becomes
// a balance change wearing a costume.
export const roundAgainstActor = (n) => Math.ceil(Number(n) || 0);
export const roundForActor     = (n) => Math.floor(Number(n) || 0);

// ═══════════════════════════════════════════════════════════════
// 🛡 R115 · THE ROLE RIFTS — BODY · SOL · HEART · MIND
// ═══════════════════════════════════════════════════════════════
// caP'n, 2026-09-07, and it is worth quoting in full because the first
// reading of his 2026-09-06 message was wrong in an expensive direction:
//
//   *"no its not based on Esclings, I didn't explain a lot in that
//   message maybe on purpose I knew you'd find and ask the right
//   questions no matter haha so the idea is there are specific rifts, you
//   can place whatever being you want there and it gains that Roles
//   Primera."*
//
// So a Role does NOT live on a card. No Escendant gained a field; not one
// of the 88 beings was touched. THE RIFT CARRIES THE ART, and whatever
// being is standing in that rift is wearing it this Turn. Move the being,
// and the Art stays behind with the rift.
//
// That is why the battle line went to four (R110): four Arts, four rifts,
// one each, in a fixed order that never changes — Body, Sol, Heart, Mind,
// left to right.
// ── THE FOUR RIFTS HAVE NAMES, AND THE NAMES ARE THE CANON ─────
// caP'n, 2026-09-07: *"Body far left, then [Sol] to its Ryt, then Heart
// rift, and finally on the far right is the Mind Rift"*, and the pairings
// in his own shorthand: *"(Body tank Lyf, mind warden chao, Heart Healer
// Lyt, Sol Dps Anima)"*.
//
// ⚠ "DPS" IS DEAD VOCABULARY AND IS NOT USED ANYWHERE IN TERROR. caP'n,
// same message: *"let's not even call it DPS ever again that's the Terror
// role."* It survives in this one comment as the record of what the word
// used to be, and nowhere else — not in a name, not in a string, not in a
// player-facing line.
//
// Each rift binds three things: a NAME (the rift), a ROLE (the Art it
// lends), and an ELEMENT (what it is of). The element is flavour today —
// nothing reads it — and it is carried on the row rather than in prose so
// that the day something DOES read it, the data is already there and no
// enum has to be widened. That is THE EXCEPTION LAW applied to a table:
// one more field now beats one more subsystem later.
export const ROLES = {
  TANK:   "Tank",
  TERROR: "Terror",
  HEALER: "Healer",
  WARDEN: "Warden",
};
export const RIFTS = [
  { i: 0, name: "Body",  role: ROLES.TANK,   element: "Lyf"   },
  { i: 1, name: "Sol",   role: ROLES.TERROR, element: "Anima" },
  { i: 2, name: "Heart", role: ROLES.HEALER, element: "Lyt"   },
  { i: 3, name: "Mind",  role: ROLES.WARDEN, element: "Khao"  },
];
export const ROLE_ORDER = RIFTS.map((r) => r.role);
export const RIFT_NAMES = RIFTS.map((r) => r.name);
export const roleOfRift = (i) => RIFTS[i]?.role ?? null;
export const riftOfRole = (role) => ROLE_ORDER.indexOf(role);
export const riftNameOf = (i) => RIFTS[i]?.name ?? null;
export const riftByName = (name) =>
  RIFTS.find((r) => r.name.toLowerCase() === String(name).toLowerCase()) || null;
export const BODY_RIFT = 0, SOL_RIFT = 1, HEART_RIFT = 2, MIND_RIFT = 3;

// ⚠ THE CONTRADICTION GATE — THE FIFTH SEAM, AND THE CHEAPEST INSURANCE
// IN THIS WAVE. Terror already has one of these and it is the reason Tome
// coverage is structural rather than clerical: npcTomeBook throws at IMPORT
// if its rows and cards.js disagree in either direction. This is the same
// device for the rift tables.
//
// Four tables now have to agree — RIFTS, ESCLING_SLOTS, ROLE_FARE and
// ROLE_CADENCE — and nothing but arithmetic keeps them in step. That exact
// gap is what let RULEZ_V2 say "eleven rifts" over a table of twelve for the
// entire life of the book: nobody did the sum, and nothing was ever going to
// make them. A fifth rift added tomorrow, or a role added without a fare,
// now fails LOUDLY at import instead of quietly at the table.
(function assertRiftTablesAgree() {
  const problems = [];
  if (RIFTS.length !== ESCLING_SLOTS)
    problems.push(`RIFTS has ${RIFTS.length} rows but ESCLING_SLOTS is ${ESCLING_SLOTS}`);
  RIFTS.forEach((r, i) => {
    if (r.i !== i) problems.push(`RIFTS[${i}] carries index ${r.i}`);
    if (!r.name)  problems.push(`RIFTS[${i}] has no name`);
    if (!r.role)  problems.push(`RIFTS[${i}] has no role`);
    if (!r.element) problems.push(`RIFTS[${i}] (${r.name}) has no element`);
  });
  const roleValues = Object.values(ROLES);
  roleValues.forEach((role) => {
    if (!ROLE_ORDER.includes(role)) problems.push(`ROLES.${role} sits in no rift`);
  });
  if (new Set(ROLE_ORDER).size !== ROLE_ORDER.length)
    problems.push("two rifts claim the same role");
  if (new Set(RIFT_NAMES).size !== RIFT_NAMES.length)
    problems.push("two rifts claim the same name");
  if (problems.length) {
    throw new Error("THE RIFT TABLES DISAGREE WITH EACH OTHER:\n  " + problems.join("\n  "));
  }
})();
// The role-named aliases, so a reader who is thinking in Arts rather than
// in rifts does not have to translate. They are the SAME indices.
export const TANK_RIFT = BODY_RIFT, TERROR_RIFT = SOL_RIFT,
             HEALER_RIFT = HEART_RIFT, WARDEN_RIFT = MIND_RIFT;

// WHAT KIND OF THING A ROLE ART IS. caP'n, 2026-09-07: *"No new stamps,
// just cool world building terminology for their Art"* and *"the rifts
// are level 3 Deviant Primeras. That means they're level 3 and can be
// used whenever, but they still have their cooldowns associated if they
// have em."*
//
// So: NOT a third axis. The Axis is still two words (R-Axis, ch.6), Ilk
// and School, and the eight Primera stamps are still eight. A Role Art is
// a Level 3 DEVIANT Primera — same family as Exiliation, same timing band
// (Level 3: whenever), and it does not consume, compete with or replace
// the being's own Ilk and School stamps.
//
// ⚠ AND THE PER-BEING AND PER-WORD LAWS DO NOT REACH IT. caP'n,
// 2026-09-07: *"Deviant Primeras have no limit on use like the Exilation.
// Only their specific cooldown stops them from stacking."* R81's "one
// Primera per being per Turn" and R82's "one use per word per Turn" are
// laws of the EIGHT innate stamps. A Deviant Primera answers only to its
// own cadence tag. The single exception is the Warden's own clause below.
export const DEVIANT_PRIMERA_TIMING = 3;   // Level 3 — activates whenever

// THE THREE-TURN LOCK. caP'n, 2026-09-06: *"Cannot be used for the first
// 3 turns"*, and 2026-09-07 confirming its reach: *"3 full turns (6
// half-turns) is correct; correct, it locks all roles effects even the
// warden and Tank."*
//
// So it is not a lock on ACTIVATION, it is a lock on the ART. The Tank's
// free passive immunity is dark. The Warden's shield is dark. A Warden is
// an ordinary being for three full Turns and can be Lyt-attacked like any
// other. This matters enormously to opening play and it is deliberate.
export const ROLE_LOCK_FULL_TURNS = 3;
export const roleLockHalfTurns = (seatCount = 2) =>
  ROLE_LOCK_FULL_TURNS * halfTurnsPerFullTurn(seatCount);
/** R115 — are the Role Arts awake yet? Measured in half-turns, like every clock here.
 *
 * ⚠ `>=`, NOT `>`. `match.turn` counts half-turns from 0, so half-turns
 * 0..5 ARE the three full Turns and half-turn 6 is the first one after
 * them. `>` woke the rifts on half-turn 7, which gave the second seat its
 * Arts on its 4th Turn and the first seat only on its 5th — asymmetric,
 * and a Turn late for both. Every other clock in this file uses "reached".
 *
 * ⚠ A MISSING `turn` READS AS ASLEEP — AND ASLEEP IS **NOT** THE PRE-WAVE
 * GAME. An earlier draft of this banner claimed it was, and that was
 * wrong in all three directions, which is worth writing down because the
 * claim is the sort that stops people checking:
 *
 *   wardenOpenToLytAttack(line)  -> true   (pre-wave: the shield HELD)
 *   blocksAnimaLytAttack(m, 3)   -> true   (pre-wave: a Warden never walled)
 *   mindIsSilenced(line)         -> false  (pre-wave: the Mind WAS silent)
 *
 * So a call site that forgets the clock does not get yesterday's game. It
 * silently switches R119's shield off, silently makes the Warden a wall,
 * and silently un-silences the Mind — three rule changes in a corner
 * nobody grepped, which is exactly what the default was supposed to
 * prevent. **THE PARAMETER IS EFFECTIVELY REQUIRED.** It is not enforced
 * with a throw only because these predicates are also asked by the
 * Debugger and by stored-duel validation, where a board may legitimately
 * predate the wave; every LIVE call site passes it, and
 * tools/exileWave.selftest.mjs asserts the real behaviour above rather
 * than the comfortable story. If you add a caller, pass the turn. */
export const rolesAwake = (turn, seatCount = 2) =>
  (Math.floor(Number(turn)) || 0) >= roleLockHalfTurns(seatCount);

// ⚠ AN ESCLING IN A ROLE RIFT CARRIES NO ART, AND THIS IS DERIVED, NOT
// RULED. Rulez v2 ch.7 — *"Esclings bear no Primera stamps"* — and ch.11
// — *"Esclings have none."* A Role Art is a Primera (caP'n's own word for
// it, above), so the standing law already answers it and no new ruling was
// needed. It also has to be this way: an Escling cannot be Lyt-attacked,
// cannot be ruined by a card effect and cannot be targeted at all, so a
// Warden Escling would be permanently untouchable AND permanently
// unruinable, which would make the Warden bounty (R119) unclaimable and
// the whole Tank-then-Warden sequence meaningless. The Art wakes when an
// Escendant stands in the rift. FLAGGED IN RULE_PROVENANCE as V'ael's
// reading of caP'n's law rather than his sentence.
// ⚠⚠ WHAT "STANDING" MEANS ON A REAL SLOT, AND THE BUG THAT TAUGHT US ⚠⚠
// The first cut of every predicate in this wave asked
// `rift.escendant && !rift.escling`, on the reasonable-sounding theory
// that a rift still holding an Escling is a rift with only an Escling in
// it. THAT IS NOT THIS ENGINE'S SLOT SHAPE AND THE READING WAS WRONG IN
// THE WORST POSSIBLE DIRECTION.
//
// A slot is built as `{ escling: <id>, escendant: null, soul: [], … }` and
// Transcending writes `{ ...slot, escendant: id }` — THE ESCLING FIELD IS
// NEVER CLEARED, because the Escling is the eternal foundation the being
// rose from and it is still down there. So `!escling` is false on every
// rift that has ever held anything, and every predicate built on it
// answered as though the board were empty:
//
//   · the Warden shield never shielded,
//   · the Mind was never silenced,
//   · no rift Art ever woke,
//   · and — by far the worst — THE WALL OF BEINGS STOPPED BLOCKING, so a
//     seat's Anima read as open to a Lyt Attack with four beings standing
//     in front of it.
//
// Every one of those fails OPEN, which is the direction that does not
// throw, does not warn, and looks like a working game. It was found by
// running the predicates against a realistic four-rift line rather than
// against the hand-made `{escendant:true}` fixtures the unit tests used —
// and those fixtures are exactly why the tests were green. A test built
// from a shape the game never produces is a test that cannot fail.
//
// THE RULE, ONCE AND FOR ALL: a rift is HELD when `escendant` is truthy.
// The escling field says nothing about occupancy and must never be read
// as though it does.
export const riftHeld = (rift) => !!(rift && rift.escendant);
export const riftArtIsAwake = (rift, turn, seatCount = 2) =>
  riftHeld(rift) && rolesAwake(turn, seatCount);

// ── THE FARES ──────────────────────────────────────────────────
// Three are flat. The Healer's is the Resurrection fare of whatever it
// returns — caP'n, 2026-09-07: *"whoops yeah lets make it cost the price
// of a normal Resurrection."*
//
// ⚠ THE BODY RIFT'S SQUARE IS ASYMMETRIC ON PURPOSE — 3 Khao OR 1 Anima. It is
// the second such fare in Terror, after the Deviant L3/no-traits square
// (5 Khao or 4 Anima, R101), and it leans the same way: Anima is the
// cheaper coin. Do not tidy it. Given straight by caP'n, 2026-09-07:
// *"(3 Khao or 1 Anima is the cost to use the Primera just so we're
// clear)."* No mixing, either way — R107 binds these like every other fare.
export const ROLE_FARE = {
  [ROLES.TANK]:   { khao: 3, anima: 1 },   // Body
  [ROLES.TERROR]: { khao: 5, anima: 5 },   // Sol
  [ROLES.HEALER]: null,                    // Heart — see roleFare, the Resurrection schedule
  [ROLES.WARDEN]: { khao: 5, anima: 5 },   // Mind
};
export const roleFare = (role, level = 1) => {
  if (role === ROLES.HEALER) {
    const f = RESURRECT_COST[`L${level || 1}`] ?? RESURRECT_COST.L3;
    return { khao: f, anima: f };
  }
  return ROLE_FARE[role] || null;
};

// ── THE CADENCES ───────────────────────────────────────────────
// ⚠ THE TERROR ART HAS NO COOLDOWN AND THAT IS caP'n's RULING, NOT AN
// OMISSION. He gave OPT for the Tank, "once per turn" for the Healer, OP2
// for both Warden options, and nothing at all for the Terror rift — then
// ruled the general case outright: *"Deviant Primeras have no limit on use
// like the Exilation. Only their specific cooldown stops them from
// stacking."* No cooldown means no brake but the Khao pool, and at 5 a
// firing against a natural ceiling of 10 that is two a Turn, three at the
// card ceiling. ⚠ THIS IS THE ONE NUMBER IN THE WAVE MOST LIKELY TO WANT
// A PLAYTEST — it is a DIAL, not a law: give it "OPT" here and it is
// once-per-Turn everywhere, in the one resolver and the brain's cadence gate, with no other edit.
export const ROLE_CADENCE = {
  [ROLES.TANK]:   "OPT",    // Body   — caP'n wrote the tag himself
  [ROLES.TERROR]: null,     // Sol    — no cooldown; the pool is the only brake
  [ROLES.HEALER]: "OPT",    // Heart  — "1 free rez once per turn"
  [ROLES.WARDEN]: "OP2",    // Mind   — per option, and see WARDEN_ARTS_PER_TURN
};
export const roleCooldown = (role, seatCount = 2) => {
  const tag = ROLE_CADENCE[role];
  return tag ? cooldownHalfTurns(tag, seatCount) : 0;
};

// The second half of the contradiction gate (see assertRiftTablesAgree
// above) — it has to run down here, after the two tables it compares exist.
// A role with no fare row and a role with no cadence row are both silent
// failures otherwise: roleFare returns null and roleCooldown returns 0, and
// "0" reads as "no cooldown", which is a REAL and legal value for the Sol
// rift. So an omission would be indistinguishable from a ruling.
(function assertEveryRoleIsPriced() {
  const problems = [];
  ROLE_ORDER.forEach((role) => {
    if (!(role in ROLE_FARE))
      problems.push(`${role} has no ROLE_FARE row (null is legal — ABSENT is not)`);
    if (!(role in ROLE_CADENCE))
      problems.push(`${role} has no ROLE_CADENCE row (null is legal — ABSENT is not)`);
  });
  Object.keys(ROLE_FARE).forEach((role) => {
    if (!ROLE_ORDER.includes(role)) problems.push(`ROLE_FARE prices "${role}", which is not a rift's role`);
  });
  if (problems.length) {
    throw new Error("THE ROLE TABLES DISAGREE WITH THE RIFTS:\n  " + problems.join("\n  "));
  }
})();

// ═══════════════════════════════════════════════════════════════
// ⚔ R116 · THE TERROR ART — THE SOL RIFT
// ═══════════════════════════════════════════════════════════════
// caP'n, 2026-09-06: *"Terror dps rift can ruin 1 free fractured being if
// it ruins one OR if its lyt attack exceeds the adversary's Lyf by at
// least one, ruin it instead of fracturing. Cost: 5 Khao or Anima."*
// (He retired the word "dps" the following day — the rift is Sol and the
// Art is Terror. The quote is left exactly as he wrote it, because a
// quotation that has been tidied is no longer evidence of anything.)
//
// Two limbs, bought together for one fare:
//
//   THE FREE RUIN. If the being in the Sol rift ruins something, it may
//   also ruin one Fractured being for free. A Fractured being is one at 0
//   Lyf (R31) and is normally ruined by a second Lyt Attack; this reaches
//   it without spending a swing.
//
//   OVERKILL. Combat's ordinary reading is that Lyt at or above Lyf drops
//   the defender to 0 and FRACTURES it, and only a second strike ruins
//   (Rulez v2 ch.8). The Terror Art rewrites the top of that table for
//   this being: strictly GREATER than, by one or more, and the defender is
//   RUINED where it stands. Equal is still a Fracture — "exceeds ... by at
//   least one" is a strict inequality and it is quoted here so nobody
//   softens it into ">=" later.
export const TERROR_ART_OVERKILL = (lyt, lyf) =>
  (Number(lyt) || 0) - (Number(lyf) || 0) >= 1;

// ═══════════════════════════════════════════════════════════════
// ✚ R117 · THE HEALER ART
// ═══════════════════════════════════════════════════════════════
// caP'n, 2026-09-06: *"Healer rift gives 1 free rez once per turn, does
// not incur Necropolis limitation(same costs)."* Two limits were folded
// into "Necropolis limitation" and he named both on 2026-09-07: *"yes
// those were the limits I was actually referencing, the return tally is
// not spent."*
//
// Terror has TWO separate brakes on Resurrection and the Healer slips
// both:
//   · R89 — ONE PAID RITE PER TURN. The Healer's return does not use it,
//     so a seat may take its ordinary paid Resurrection AND this one.
//   · R85 — THE PER-COPY TALLY, Beb 3 / Champion 2 / Lord 1, spent for the
//     whole Solrend by every method that returns a being. THE HEALER
//     SPENDS NONE OF IT. That is the expensive half and it is deliberate:
//     while a Healer stands, one being can be brought back without limit.
//
// It is not free of FARE — it pays the ordinary Resurrection schedule for
// the level it returns (see roleFare). What it buys is the clock and the
// tally, not the coin.
export const HEALER_SPENDS_TALLY   = false;  // R85 does not bite
export const HEALER_USES_PAID_RITE = false;  // R89 does not bite

// ═══════════════════════════════════════════════════════════════
// 🛡 R118 · THE TANK ART
// ═══════════════════════════════════════════════════════════════
// caP'n, 2026-09-06: *"Tank cannot be ruined by the dps role effect,
// passive and free. Opt: can reduce 1 being's Lyt attack's Lyt power by 1.
// Cost: 3 Khao or 1 Anima."*
//
// THE PASSIVE costs nothing, is never activated, and cannot be answered,
// because there is nothing to answer — it simply holds. It is narrow on
// purpose: it stops R116 and only R116. An ordinary Lyt Attack ruins a
// Tank exactly as it ruins anything else.
//
// THE DULLED SWING is an OPT and caP'n wrote the tag himself. It takes 1
// off a Lyt Attack's Lyt for THAT ONE SWING — 2026-09-07: *"it lasts for
// only that one specific Lyt Attack. It can only hit 1 of your Adversary's
// beings per turn."* Not a duration, not a debuff on the being; a single
// swing dulled as it lands, against one adversary being per Turn.
export const TANK_IMMUNE_TO = ["R116"];   // the Terror Art's ruin, and nothing else
export const TANK_LYT_REDUCTION = 1;
export const TANK_TARGETS_PER_TURN = 1;

// ═══════════════════════════════════════════════════════════════
// 👁 R119 · THE WARDEN ART
// ═══════════════════════════════════════════════════════════════
// caP'n, 2026-09-06: *"Warden: tank must be ruined and either left in a
// fallen state, or only esclings left on board to attack the Warden with
// Lyt attacks. All other ways are always viable."*
//
// THE SHIELD IS THE TANK'S, NOT THE WARDEN'S. A Warden cannot be
// Lyt-attacked while its seat's Tank still stands. Two doors open it:
//   1. The Tank is ruined AND left Fallen — the half-turn state a being
//      wears after descending from a Lyt-ruin (Rulez v2 ch.8). Note that
//      a Fallen being cannot itself be Lyt-attacked and does not block, so
//      opening this door costs the attacker the Tank and nothing else.
//   2. Only Esclings are left on that side.
//
// "ALL OTHER WAYS ARE ALWAYS VIABLE" — a Tome, an Impera, an U'hlti, a
// Curio, a stamp, an Affinity reaches a Warden through the shield on any
// Turn. The shield stops the SWING and only the swing, which is the same
// shape as the wall of beings (R31) and deliberately so.
//
// ⚠ NO TANK ON THE FIELD MEANS NO SHIELD. DERIVED, NOT RULED — flagged in
// RULE_PROVENANCE. If a seat has never stood a being in its Tank rift, the
// condition "the Tank must be ruined and left Fallen" can never be met,
// and reading that as a permanent shield would make the Warden untouchable
// by declining to play a card. The shield is a thing the Tank provides;
// no Tank, nothing provided. Reverse it in this one function.
export function wardenOpenToLytAttack(rifts, turn, seatCount = 2) {
  // R115 — *"No Role does anything at all for the first three full Turns.
  // Not the activated ones, not the free ones, not the ones that simply
  // hold."* The shield is one that simply holds, so for three Turns the
  // Mind rift is an ordinary being and is swingable like any other.
  if (!rolesAwake(turn, seatCount)) return true;
  const line = Array.isArray(rifts) ? rifts : [];
  const standing = riftHeld;   // see THE SLOT SHAPE banner above
  const onlyEsclings = line.every((r, i) => i === WARDEN_RIFT || !standing(r));
  if (onlyEsclings) return true;
  const tank = line[TANK_RIFT];
  if (!standing(tank)) return true;   // ruined out entirely, or never stood
  if (tank.fallen) return true;       // ruined and left Fallen
  return false;
}

// ⚠ A WARDEN IS NOT A WALL. caP'n, 2026-09-06: *"You dont need to ruin the
// warden or leave it in a fallen state to dim Anima, only the other 3."*
// And 2026-09-07, on how to say it to a player: *"the dimming Anima works
// exactly the same, pretend as though the Warden isn't there is a good way
// to explain that."*
//
// So for the one question "may I swing at your Anima", the Warden is
// invisible. Three standing beings hold the wall; the fourth does not.
// This is the cost the Warden pays for being unreachable, and it is why
// the Art is a trade rather than a gift.
// ⚠ AND THIS SLEEPS WITH THE REST OF THE ROLE (R115). For three full
// Turns a Mind is *an ordinary being*, and an ordinary being walls. The
// hole the Warden opens in the wall opens when the Art wakes, not on the
// opening draw.
export const blocksAnimaLytAttack = (rift, i, turn, seatCount = 2) =>
  riftHeld(rift) && !rift.fallen && !(i === WARDEN_RIFT && rolesAwake(turn, seatCount));
/** R119 + R31 — is this seat's Anima open to a Lyt Attack right now? */
export const animaOpenToLytAttack = (rifts, turn, seatCount = 2) =>
  !(Array.isArray(rifts) ? rifts : []).some((r, i) => blocksAnimaLytAttack(r, i, turn, seatCount));

// THE TWO WARDEN OPTIONS. Both OP2, both 5 Khao or 5 Anima, and both pay
// a card into the Necropolis on top of the fare — the card is part of the
// price, not a target. caP'n, 2026-09-06:
//   *"OP2: you can send 1 card from your hand to Necropolis and Nullify 1
//   card effect. Cost: 5 Khao or 5 Anima."*
//   *"OP2: you can send 1 card from your Grim to Necropolis and Nullify 1
//   Lyt Attack. Cost: 5 Khao or 5 Anima."*
export const WARDEN_OPTIONS = [
  { id: "nullify_effect", pay: "hand",     nullifies: "effect",
    text: "Send 1 card from your hand to the Necropolis — nullify 1 card effect." },
  { id: "nullify_swing",  pay: "grimoire", nullifies: "lytAttack",
    text: "Send 1 card from your Grimoire to the Necropolis — nullify 1 Lyt Attack." },
];
export const wardenOption = (id) => WARDEN_OPTIONS.find((o) => o.id === id) || null;

// ⚠ ONE WARDEN ART PER AENARCHIST PER TURN — the single place a per-word
// law does reach a Deviant Primera, and caP'n wrote it himself: *"Each
// Aenarchist can only use 1 of their Warden Primeras per turn."* It is per
// SEAT, not per being, and it spans both options: fire the hand one and
// the Grimoire one is shut for the rest of the Turn.
export const WARDEN_ARTS_PER_TURN = 1;

// THE BOUNTY. caP'n, 2026-09-06 gave it at Level 1 only; 2026-09-07 he
// widened it: *"let's just make it a reward for ruining any level Warden,
// doesn't matter same reward at every level."* The ATTACKER chooses.
//
// ⚠ THE KHAO CEILING IT OBEYS IS THE CARD CEILING (15), NOT THE NATURAL
// ONE (10) — DERIVED, flagged in RULE_PROVENANCE. R-Khao already splits
// the two: the +1-per-quiet-Turn climb stops at 10, and effects may push
// to 15. A bounty is an effect, so it takes the effect ceiling.
export const WARDEN_BOUNTY = { anima: 3, maxKhao: 1 };
export const WARDEN_BOUNTY_CHOICES = ["anima", "maxKhao"];

// ═══════════════════════════════════════════════════════════════
// 🤐 R121 · THE MIND'S SILENCE
// ═══════════════════════════════════════════════════════════════
// caP'n, 2026-09-07, and he flagged it himself as the one he had left out:
//
//   *"very important! I forgot to add this as well! Whatever being is in
//   the Warden Rift cannot perform any arts, attacks, etc of any kind at
//   alllll except for the Warden Rift Primera, if the other 3 rifts are
//   occupied. So, it behaves like a normal being if only 2 of the Trio
//   Rifts are occupied, then when all 3 ... are occupied with Ascendants
//   not Esclings, the Mind cannot perform any attacks of any kind etc
//   except the Warden Primera."*
//
// THIS IS THE PRICE OF THE SHIELD, AND IT IS A STEEP ONE. R119 makes the
// Mind unreachable by a swing while the Body still stands. R121 is what
// that costs: a full line — Body, Sol and Heart all holding ESCENDANTS —
// silences the Mind completely. No Lyt Attack. No Primera stamp of its
// own Ilk or School. No Impera, U'hlti or Affinity power. No Exiliation
// declared by it. Nothing, except the Warden Art of the rift it stands in.
//
// And the moment one of the three falls — ruined out to its Escling, or
// simply never Transcended — the Mind wakes up.
//
// ⚠ THE TRIO IS COUNTED IN ESCENDANTS, NOT IN CARDS. caP'n's own words,
// "occupied with Ascendants not Esclings": a rift holding a bare Escling
// is NOT occupied for this count. Three Esclings and a Lord in the Mind
// rift is a fully awake Warden.
//
// ⚠ A FALLEN ESCENDANT STILL COUNTS AS OCCUPYING ITS RIFT. Derived, and
// flagged. Fallen is a half-turn state of a being that is standing there
// (Rulez v2 ch.8) — it is not gone, it is not an Escling, and the rift is
// not empty. Reading it the other way would hand a seat a one-half-turn
// window in which its Mind woke up because its own Body had just been
// knocked down, which rewards being beaten. Reverse it in trioOccupied.
//
// ⚠ AND GROWTH IS NOT SILENCED. caP'n, asked directly: *"no you can keep
// ascending etc and attaching cards to it etc like your ace in the hole
// if you want to play that way, you can beef up your warden and when your
// scapegoat is ruined its unleashed with all these Tomes/support cards
// etc hehe."* So the line is between what the BEING performs and what the
// AENARCHIST does to it. Transcending into the Mind rift, Ascending it,
// Training Impera onto it, bonding an U'hlti, swearing an Affinity,
// Heretical Training — all legal, all Turn, every Turn. It is deliberately
// a place to build something terrible and wait.
export const TRIO_RIFTS = [BODY_RIFT, SOL_RIFT, HEART_RIFT];
/** R121 — is this rift held by an Escendant? A bare Escling does not count. */
export const riftOccupied = riftHeld;
/** R121 — are Body, Sol and Heart ALL held by Escendants? */
export const trioOccupied = (rifts) => {
  const line = Array.isArray(rifts) ? rifts : [];
  return TRIO_RIFTS.every((i) => riftOccupied(line[i]));
};
/** R121 — is the being in the Mind rift silenced right now?
 *  Silence is the price of the Warden's shield, so it sleeps with the
 *  shield: for three full Turns a Mind is an ordinary being and acts. */
export const mindIsSilenced = (rifts, turn, seatCount = 2) =>
  rolesAwake(turn, seatCount) &&
  riftOccupied((Array.isArray(rifts) ? rifts : [])[MIND_RIFT]) && trioOccupied(rifts);

// The one thing a silenced Mind may still do. Everything else it might
// have performed is barred, and this is an ALLOW-LIST for the same reason
// the phase gate is one (R108): a new kind of action invented tomorrow is
// barred by DEFAULT rather than slipping through a deny-list nobody
// remembered to update.
// ⚠ BOTH TOKENS, AND THE SECOND ONE IS NOT A CONVENIENCE. The gate that
// asks this question is validateRoleArt, and the action type it is handed
// is "ROLE_ART" — the enforcer's own case label. With only "WARDEN_ART"
// on the list, a silenced Mind was refused the ONE thing R121 says it may
// still do, by a guard whose failure message said so out loud. R115 gives
// the Art to the RIFT, so a Role Art performed from the Mind rift is the
// Warden Art by definition; there is no other Art it could mean.
export const MIND_SILENCE_ALLOWS = ["WARDEN_ART", "ROLE_ART"];
// What the AENARCHIST may still do TO it — growth, not performance. Also
// an allow-list, and deliberately a separate one: these are not things the
// being does, so they were never inside the silence to begin with.
// ⚠ THE ENFORCER'S OWN CASE LABELS, plus the readable aliases. The
// enforcer dispatches Impera training and U'hlti bonding as one action
// type, "ATTACH" (RulesEnforcer.validateAction), and neither of the two
// prettier names below was ever a string this list would be asked about.
// Same disease as the line above; same cure.
export const MIND_SILENCE_GROWTH_ALLOWS = [
  "TRANSCEND", "ASCEND", "ATTACH", "TRAIN_IMPERA", "ATTACH_UHLTI",
  "ATTACH_AFFINITY", "HERETICAL_TRAINING", "RESURRECT",
];
/** R121 — may the being in the Mind rift take this action right now? */
export const mindMayAct = (rifts, actionType, turn, seatCount = 2) =>
  !mindIsSilenced(rifts, turn, seatCount) ||
  MIND_SILENCE_ALLOWS.includes(actionType) ||
  MIND_SILENCE_GROWTH_ALLOWS.includes(actionType);

// ═══════════════════════════════════════════════════════════════
// 🚫 R120 · AN ART IS NOT A CARD
// ═══════════════════════════════════════════════════════════════
// caP'n, 2026-09-07, on whether a Nihil or an Omnia may answer an
// Exiliation:
//
//   *"no you can definitely stop it if a card is broad enough like stop
//   something from activating, but if it outright mentions a card, then
//   no, because they are not cards, these are Arts the Aenarchist knows
//   that's why I was so specific."*
//
// So the answering law (R-Chain) is unchanged in who may answer — still
// only a Nihil or an Omnia, still Level 3 from hand and Level 2+ from the
// field, and caP'n's reason is lore: *"only those 2 can because in Lore
// they're would be that powerful."* What changes is WHAT they may answer.
//
// A card that nullifies "an activation" or "an effect" reaches an Art.
// A card that nullifies "a card" does not, because an Art is not one.
//
// ⚠ THIS IS AN ALLOW-LIST AND IT DEFAULTS TO PROTECTED. The engine does
// not read printed prose to decide — that is trap §4.8, where the printed
// text and the branch disagreed on five cards, and trap §4.4, where a
// regex-over-source check could not fail. Instead a card DECLARES its
// scope with `answerScope: "broad"`, and anything that has not declared it
// cannot touch an Art. An undeclared card is therefore narrow, which is
// the safe direction: the failure mode is an Art that survives an answer
// it should have taken, which a data row fixes, rather than an Art
// silently voided by a card whose text never claimed it could.
//
// ⚠ THE DATA PASS IS OWED. No card carries `answerScope` yet, so today
// NOTHING answers an Art. That is a deliberate, declared, reversible
// starting position and not a claim that the rule is finished — the Nihil
// and Omnia rows want reading one at a time against their printed text.
export const ANSWER_SCOPE_BROAD  = "broad";
export const ANSWER_SCOPE_CARD   = "card";
export const answerScopeOf = (card) =>
  (card && card.answerScope) === ANSWER_SCOPE_BROAD ? ANSWER_SCOPE_BROAD : ANSWER_SCOPE_CARD;
/** R120 — may this answering card reach an Aenarchist's Art? */
export const answerReachesArt = (card) =>
  answerScopeOf(card) === ANSWER_SCOPE_BROAD;

// The Arts themselves, named, so a nullify has something to name back.
export const ART_KINDS = ["Exiliation", "ExileRetrieval", "RoleArt"];
export const isAenarchistArt = (kind) => ART_KINDS.includes(String(kind));

// ⚠ ROADMAP, IN caP'n's OWN WORDS AND PARKED ON PURPOSE, 2026-09-07:
// *"there are tens of thousands of school and therefore even more arts,
// more counters, even shared between schools. So there may be a school
// who's whole purpose is stopping Exilations and Aenarchist focused Arts
// ;3 (save this for the roadmap xD)"* — a School built to answer Arts is a
// designed future, not an oversight in the present. `answerScope` is the
// seam it lands on when it comes.

// ═══════════════════════════════════════════════════════════════
// 🚦 R108 · THE PHASE GATE — FIVE PHASES, AND ATTACK CLOSES YOUR HAND
// ═══════════════════════════════════════════════════════════════
// caP'n, 2026-08-26, verbatim:
//
//   *"let's canonize the 5 phases. After main phase is over — playing
//   all your cards, ascending, resurrection, everything — now you
//   declare attack phase, you cannot play any more cards. After your Lyt
//   Attacks and/or Imperas, U'hltis etc (so yes you can use cards
//   attached to a being's Soul here, but I think typically most if not
//   all are written so you'd actually just use them in the main phase
//   with the other cards anyway) — then turn over."*
//
// THE FIVE PHASES, in order, and they only ever run forwards:
//
//   REPLENISH · DRAW · MAIN · ATTACK · END
//
// ⚠ THE ENGINE HAS NEVER HAD A PHASE MACHINE. Everything was a
// free-form set of independent gates — `myTurn`, `ascendedThisTurn`,
// `drawnThisTurn` — so a seat could swing with every being and THEN
// Transcend, Ascend, resurrect and set three Tomes. Nothing forbade it
// because nothing knew what a phase was. This is that machine.
//
// ── DECLARING ATTACK IS ONE-WAY ────────────────────────────────
// It is an explicit act inside your own Turn, and there is no way back
// to Main. Once declared, NO MORE CARDS MAY BE PLAYED.
//
// ── BARRED once ATTACK is declared (each is "playing a card") ───
//   ACTIVATE_TOME    a Tome from hand or a face-down flip
//   PLACE_TOME       setting one face-down
//   TRANSCEND        an L1 onto an Escling
//   ASCEND           natural or Deviant
//   ATTACH           an Impera / U'hlti onto a Soul  (TRAINING)
//   ATTACH_AFFINITY  an Affinity onto a Soul
//   HERETICAL_TRAINING
//   RESURRECT        the paid rite
//   ACTIVATE_CURIO   a Curio out of its rift
//   GRAVESHIFT       reforging the Grimoire
//   DRAW / SEARCH / MULLIGAN — those belong to the Draw phase and were
//                    already spent; barring them here is belt and braces
//
// ── STILL LEGAL in the ATTACK phase ────────────────────────────
//   ATTACK           the Lyt Attack, which is what the phase is FOR
//   ATTACK_ANIMA     the direct strike
//   ATTACK_EFFECT    an attacking effect from something already in play
//   ACTIVATE_SOUL    a card ALREADY ATTACHED to a being's Soul — its
//                    Impera, its U'hlti, its Affinity. Named in the
//                    ruling: *"yes you can use cards attached to a
//                    being's Soul here"*. It is not being PLAYED; it is
//                    already on the board and it is being USED.
//   PRIMERA          the stamps. Of the flesh (R84), never cards, so
//                    "you cannot play any more cards" does not reach
//                    them. They are the being's own innate power.
//   KHAO_SPEND / GAIN_ANIMA / DISCARD — bookkeeping, not actions.
//
// ── AND A RESPONSE WINDOW IS NOT A PHASE ───────────────────────
// Everything above governs YOUR OWN TURN. When you answer a
// declaration on your adversary's Turn you are not in your Main phase
// and you are not in your Attack phase — you are in their Turn, and
// R36/R37/R92 govern what you may do. Your own phase pointer is not
// consulted and is not advanced. `phase` is only ever read for the
// ACTING seat.
export const PHASE = {
  REPLENISH: "Replenish",
  DRAW: "Draw",
  MAIN: "Main",
  ATTACK: "Attack",
  END: "End",
};
export const PHASES = [PHASE.REPLENISH, PHASE.DRAW, PHASE.MAIN, PHASE.ATTACK, PHASE.END];
export const phaseIndex = (phase) => PHASES.indexOf(phase);

// ⚠ ABSENT MEANS MAIN, AND THAT IS THE COMPATIBILITY HINGE. Every match
// written before this rule — every save, every replay, every headless
// fixture — has no `phase` on its seats. Reading that as MAIN means the
// gate passes exactly what it passed before for all of them, and only a
// seat that has DELIBERATELY declared Attack is ever restricted. A gate
// that failed closed on a missing field would have retro-illegalised
// every stored duel in the game.
export const phaseOf = (seat) => {
  const p = seat && seat.phase;
  return PHASES.includes(p) ? p : PHASE.MAIN;
};
export const inAttackPhase = (seat) => phaseOf(seat) === PHASE.ATTACK;

// The action types the ATTACK phase still permits. Anything not named
// here is barred once Attack is declared — an allow-list, deliberately,
// so a NEW action type added tomorrow is barred by DEFAULT rather than
// silently slipping through a deny-list nobody updated.
export const ATTACK_PHASE_ALLOWS = [
  "ATTACK",          // the Lyt Attack — the phase's whole purpose
  "ATTACK_ANIMA",    // the direct strike
  "ATTACK_EFFECT",   // an attacking effect from something in play
  "ACTIVATE_SOUL",   // Impera / U'hlti / Affinity already on a Soul
  "PRIMERA",         // stamps are of the flesh, not cards (R84)
  "KHAO_SPEND",      // paying for the above
  "GAIN_ANIMA",      // bookkeeping
  "DISCARD",         // the hand cap can still bite
  "END_TURN",
  // ── THE EXILE WAVE ──────────────────────────────────────────
  // ⚠ THESE THREE ARE HERE BECAUSE THE LIST IS AN ALLOW-LIST AND SILENCE
  // MEANS "BARRED". An Art is not a card, so declaring the Attack phase —
  // which shuts your HAND — was never meant to shut them; R108's own text
  // says what survives is "Lyt Attacks, cards ALREADY ATTACHED to a being's
  // Soul, and Primera stamps, which are of the flesh." A Role Art is a
  // Primera by caP'n's own word for it (R115) and Exiliation is an Art the
  // Aenarchist knows rather than a card played from hand (R111), so both
  // belong on the same side of that line as a stamp.
  //
  // Leaving them off would have been a particularly nasty bug: R121 says a
  // silenced Mind may still fire its Warden Art, and the Warden Art's whole
  // purpose is answering — nullifying a Lyt Attack, which by definition
  // happens in somebody's Attack phase. The rule would have been written,
  // documented, tested at the unit level and dead on the board.
  "EXILIATION",       // R111 — the Art, at Level 3 timing: whenever
  "EXILE_RETRIEVE",   // R113 — the Ritual proper
  "ROLE_ART",         // R115-R119 — the Art of the rift a being stands in
];

// Why each barred type is barred, in the words of the ruling. Read out
// by the enforcer so a refusal names the phase AND the reason.
export const ATTACK_PHASE_BARS = {
  ACTIVATE_TOME:      "a Tome is a card being played",
  PLACE_TOME:         "setting a Tome face-down is playing a card",
  ACTIVATE_CURIO:     "a Curio is a card being played",
  TRANSCEND:          "Transcending plays an Escendant from your hand",
  ASCEND:             "Ascending plays an Escendant from your hand",
  ATTACH:             "Training attaches a card from your hand",
  ATTACH_AFFINITY:    "an Affinity is attached from your hand",
  HERETICAL_TRAINING: "the rite bonds a card from your hand",
  RESURRECT:          "the paid rite is a Main-phase action",
  GRAVESHIFT:         "reforging the Grimoire is a Main-phase action",
  DRAW:               "the Draw phase is over",
  SEARCH:             "the Draw phase is over",
  MULLIGAN:           "the Draw phase is over",
};

/**
 * R108 — may `actionType` be taken in `phase`? THE one predicate.
 * Returns { allowed, reason }.
 *
 * Only the ATTACK phase restricts anything today. MAIN is the permissive
 * phase every pre-R108 match reads as, and REPLENISH/DRAW/END are
 * bookkeeping beats the engine passes through rather than sits in.
 */
export function phaseAllows(actionType, phase = PHASE.MAIN) {
  const ph = PHASES.includes(phase) ? phase : PHASE.MAIN;
  if (ph !== PHASE.ATTACK) return { allowed: true, reason: "" };
  if (ATTACK_PHASE_ALLOWS.includes(actionType)) return { allowed: true, reason: "" };
  const why = ATTACK_PHASE_BARS[actionType] || "no more cards may be played";
  return {
    allowed: false,
    reason: `You declared the Attack phase: ${why}. Lyt Attacks, cards already on a being's Soul, Primera stamps and the Arts are all that remain.`,
  };
}

/** The next phase in the fixed order; END stays END. */
export const nextPhase = (phase) => {
  const i = phaseIndex(PHASES.includes(phase) ? phase : PHASE.MAIN);
  return PHASES[Math.min(PHASES.length - 1, i + 1)];
};

/** Phases only run forwards — R108's one-way clause, as a predicate. */
export const canEnterPhase = (from, to) =>
  phaseIndex(to) >= phaseIndex(PHASES.includes(from) ? from : PHASE.MAIN);

// ============================================================
// MASTER RULE CATALOG
// ============================================================
// Every rule has a code, a category, and a human-readable desc.
// The Debugger simulator checks specific rule codes during
// game simulation; the Rulebook displays them to players.
//
// Categories map to color groups in the Debugger UI.
// ============================================================

export const RULES = [
  // ── GAME SETUP ──────────────────────────────────────────
  { code:"R01", cat:"Setup",    desc:"Both players start with 30 Anima (max 50)" },
  { code:"R02", cat:"Setup",    desc:"Both players start with 3 Max Khao" },
  { code:"R03", cat:"Setup",    desc:"Starting hand = 5 cards each" },
  { code:"R04", cat:"Setup",    desc:"4 Esclings placed before duel begins, one per battle rift (R110)" },
  { code:"R05", cat:"Deck",     desc:"THE LANES, ruled 2026-08-26: 'the way the game currently works is correct. Grimoire — Escendants and Tomes only. Luxmoire — everything else that doesn't immediately get set to the board, like Curio and Prophecy.' So: Grimoire = Escendants + Tomes ONLY, min 35. Luxmoire = Impera + U'hltis + Affinities, 10-40. Curios and Prophecies are PRE-PLACED on the Shatterfield and belong to NO deck; Esclings likewise. ⚠ Rulez v2 ch.3 says the opposite on every clause (U'hltis, Curios, Prophecies and Affinities in the Grimoire; a Luxmoire of 10-30 Primera only) and is SUPERSEDED — wrong, not merely old" },
  { code:"R06", cat:"Deck",     desc:"Luxmoire min 10, max 40 cards (Impera, U'hltis, Affinities). Within it: Impera 5/4/3 per unique name by level (R94), U'hlti 1 per unique name AND 3 per Luxmoire total (R59/R104), Affinity 1 per Luxmoire across all names (R60)" },

  // ── TURN STRUCTURE ──────────────────────────────────────
  { code:"R07", cat:"Turn",     desc:"The seat going FIRST has no Grimoire draw on its opening Turn — its Luxmoire draw IS allowed. The seat going SECOND may draw from its Grimoire (but may not search it — R08; a card effect that searches it is exempt)" },
  // ── R08 · THE OPENING CYCLE (caP'n ruling, 2026-08-25) ─────
  // The row this replaced read: Neither player can Lyt Attack on turn
  // 1 — which named only the swing. See THE OPENING CYCLE banner above for why
  // the window is each seat's first Turn rather than `turn === 0`,
  // and the ATTACK vs LYT ATTACK banner for what "Attack" means.
  { code:"R08", cat:"Turn",     desc:"THE OPENING CYCLE. On each seat's FIRST Turn: no ATTACKS of any kind — 'Attack' is the broad term and covers a Lyt Attack, an Anima strike, a Tome, an Impera, a U'hlti, a Curio, a Primera stamp or any other effect that dims an adversary's Anima or damages an adversary's being. Neither seat may SEARCH either Book (Grimoire or Luxmoire — the R88 fares are unpayable on the opening Cycle) — but that bar is on the PLAYER'S OWN search action only: a CARD EFFECT that searches (a Tome tutor, an Impera tutor, the Machine or Humanoid Primera stamp) is EXEMPT and resolves normally, exactly as a card-effect draw is excluded from R11. The search ban and the Attack ban are DIFFERENT bans: an exempt search does not exempt a stamp that also damages the adversary. Both seats MAY draw 1 from their Luxmoire. The seat going first may NOT draw from its Grimoire (R07); the seat going second MAY. R09 still stands: Transcend, Ascend and activation are legal" },
  { code:"R09", cat:"Turn",     desc:"First player CAN Transcend/Ascend/activate turn 1" },
  { code:"R10", cat:"Turn",     desc:"HAND LIMIT = 8. LAW, ruled 2026-08-26: 'so I think it's 8 now, if it is good leave it… just record it as law canon and scrap the rest so there's never confusion on any of these matters again.' Drawing at the cap still resolves — then discard DOWN TO 8, the seat choosing which (draw-then-discard): an effect that draws three into a hand of seven sheds two. THE ROW USED TO SAY 'discard 1', which is narrower than both the book (RULEZ_V2, Phase 2 Draw, 'send cards to the Necropolis until you are at 8') and the board (the Debugger's `while (me.hand.length > ACT.HAND_LIMIT)`, and npcEnforceHandCap's loop). Rulez v2 ch.4's SEVEN is superseded and the 7 is scrapped, not footnoted" },
  { code:"R11", cat:"Turn",     desc:"Both players draw a mandatory opening 5 PRE-DUEL; one draw per Turn thereafter, and DRAWING FROM ONE BOOK BARS DRAWING FROM THE OTHER THAT TURN — Grimoire OR Luxmoire, never both. Card effects are excluded: an effect that says 'draw' is not the Turn's draw. caP'n calls this 'as always', and it was already enforced (RulesEnforcer.validateFirstTurnRestrictions' R11 leg + Shatterfield's drawnThisTurn ledger) before it was written this plainly" },

  // ── KHAO ────────────────────────────────────────────────
  { code:"R12", cat:"Khao",     desc:"THE MAX-KHAO CLIMB. Your Max Khao rises by 1 only when ONE FULL TURN passes with NO Khao spent at all — your own half-turn plus your adversary's. caP'n, 2026-08-26: 'one full turn must pass without using ANY Khao at all — that's the importance of Khao Garden and getting it early.' EVERY Khao payment breaks it, and he overruled Rulez v2 Ruling 3 outright to say so: 'Regardless of who or how or where, Khao was consumed and therefore the rule about gaining more Khao depends on the user not spending any — this still counts as spending it. Doesn't matter the process, if you spent Khao in any way shape or form it doesn't count.' Resurrection fares, search fares, attach fares, the Graveshift, a stamp, a counter you fired on your ADVERSARY's turn — all of it breaks the climb. THE ONE EXEMPTION HE NAMED IS NOT ABOUT THIS RULE: 'However — if a card is only referring to card effects using Khao, then yes, only care about card effects' — a CARD whose text speaks specifically about card effects spending Khao reads only card effects. R12 itself never does. \u26a0 AND THE MOMENT IS THE BEGINNING OF THE TURN, not the end of the one that earned it \u2014 caP'n, 2026-09-15: 'after 1 full turn of not using Khao, so if you didn't use any turn 1, then the beginning of turn 2 you get +1 to your Max Khao hopefully that also clarifies other rulings as far as how the 1 full turn etc works.' See R132 for what that settled and what it broke" },
  { code:"R13", cat:"Khao",     desc:"Natural Khao max = 10 (cards can push to 15)" },
  { code:"R14", cat:"Khao",     desc:"KHAO REFILLS AT THE START OF A TURN. caP'n, 2026-09-13: 'Yes, at the beginning of every single turn, regardless if you used any or not, replenish your current Max Khao to its full amount.' So a seat OPENS its half-turn on a full pool and crosses into its adversary's half-turn holding whatever it did not spend. ⚠ THE SHIPPING BOARD REFILLED THE OUTGOING SEAT — both of Shatterfield's hand-offs and all three NPC brains wrote the refill onto the seat whose Turn was ENDING, so every seat answered through its adversary's half-turn on a full pool. The Debugger's simulator had it right the whole time (see its R14 assertion, which measures the refill against the carry). The refill now lives in turnEngine.handOffHalfTurn, written onto the INCOMING seat inside the chair transaction" },
  { code:"R15", cat:"Khao",     desc:"Khao cost must be paid before card activates" },

  // ── TRANSCEND / ASCEND ──────────────────────────────────
  { code:"R16", cat:"Transcend",desc:"Transcend: L1 Escendant must match School of Escling" },
  { code:"R17", cat:"Transcend",desc:"Can Transcend ALL Esclings in one turn (no limit)" },
  { code:"R18", cat:"Ascend",   desc:"Only 1 Ascension per turn" },
  { code:"R19", cat:"Ascend",   desc:"Natural Ascend requires matching School AND Ilk" },
  { code:"R20", cat:"Ascend",   desc:"Natural Transcend/Ascend is free (no Khao cost)" },
  { code:"R21", cat:"Ascend",   desc:"Deviant Ascend costs Khao or Anima" },

  // ── COMBAT ──────────────────────────────────────────────
  { code:"R22", cat:"Combat",   desc:"Lyt >= Lyf -> Fracture (Lyf drops to 0)" },
  { code:"R23", cat:"Combat",   desc:"Fractured L1 -> Ruined and sent to Necropolis on next hit" },
  { code:"R24", cat:"Combat",   desc:"Fractured L2/L3 -> Descends to prev level, Fallen state" },
  { code:"R25", cat:"Combat",   desc:"Fallen Escendants are IMPERVIOUS to Lyt Attacks" },
  // ── TURN vs CYCLE (Rulez v2 definitions) ──────────────────
  // A TURN is one player's turn: their draw through their end phase.
  // A CYCLE is both players' Turns back-to-back (one full Solrend beat).
  // "Owner's end of turn" = the end of THAT player's own Turn, not the
  // end of the Cycle. Fallen lasts only the adversary's HALF-turn — it
  // clears the moment its owner's turn begins (R26, canon July 2026).
  //
  // ── R28 · LYF REFRESHES FOR THE ACTING SEAT ONLY ────────────
  // caP'n, 2026-08-26, verbatim:
  //
  //   *"as coded is better — I think I did write it meaning to be
  //   refreshed after the user's barrage of Lyt attacks were over, but
  //   it feels better the way it is now as coded; the being's Lyf stays
  //   chipped or even in a fractured state until its own turn."*
  //
  // THE CODE WAS RIGHT AND THESE TWO ROWS WERE WRONG. Shatterfield's
  // end-of-turn write (the "Lyf reset (R28)" block) has only ever
  // rebuilt `field.esclings` for the seat whose Turn is ENDING; the
  // adversary's board is not touched. NPC.js's `_lockShot` banner has
  // said so in words since August. What the catalogue said — "Lyf
  // resets at the end of EACH Turn ... so chip damage cannot accumulate
  // across Turns" — described a reset that no line of code performs and
  // told a player their barrage was worthless when it is not.
  //
  // NO CODE CHANGED FOR THIS RULING. The rows below did.
  //
  // ⚠ AND THE PASSAGE THAT SEEMS TO CONTRADICT IT IS NOT A MECHANIC.
  // Rulez v2's *"the Fracture heals at your adversary's convenience …
  // Never let a Fractured being rest"* reads like a rule and is not
  // one: it is J'YNXI'S NARRATIVE VOICE, and caP'n called it a genuine
  // plot hole in the prose on 2026-08-26 — to be fixed IN THE BOOK, not
  // in the game. Lyf refreshing is canon exactly as coded. Nobody is to
  // "reconcile" the engine with that paragraph.
  // ────────────────────────────────────────────────────────────
  { code:"R26", cat:"Combat",   desc:"Fallen beings are bypassable to Anima IMMEDIATELY; Fallen is a HALF-turn state — it clears the moment its owner's turn begins (Lyf regen stays at end of turn, R28)" },
  { code:"R27", cat:"Combat",   desc:"Chip damage (Lyt < Lyf) STANDS. A being chipped on an adversary's board keeps the wound — Fractured included — through the rest of the attacker's Turn AND through its own seat's whole Turn, and only mends when that seat's Turn ends. So a barrage accumulates: three swings into one being in one Turn stack, and a Fracture left on an adversary's board is still a Fracture when play returns to them. What chip damage can never do is survive its OWN owner's end of Turn" },
  { code:"R28", cat:"Combat",   desc:"Lyf refreshes to full at the end of its OWN seat's Turn — for every being that seat controls, Fallen included. It is the ACTING seat's board that mends; an adversary's chipped or Fractured beings are untouched and stay chipped until their own Turn comes round" },
  { code:"R29", cat:"Combat",   desc:"0-Lyf Escendants are always Fractured, can be ruined immediately" },
  { code:"R30", cat:"Combat",   desc:"Each Escendant can only attack once per turn — and see R124, which is where a card prints more; the allowance is a COUNT, not a boolean. THE ESCLING SWING, written down 2026-09-14 because it had no row ANYWHERE in this catalogue: an Escling may Lyt-attack an opposing ESCENDANT with its printed Lyt, once per Turn like any being, and may NEVER target Anima. It is never Lyt-attacked in return and it never blocks (R31) — it is eternal and belongs to no deck (R04/R05). Board evidence: Shatterfield's attack starter sets targetMode 'escling' with the comment 'can only target beings, not Anima', and RULEZ_V2 ch.4 Phase 4 states all four legs. Every row that names an Escling — R04, R05, R16, R17, R31, R46, R78, R110, R119, R121, R74 — granted it nothing and barred it nothing." },

  // ── ANIMA ATTACKS ───────────────────────────────────────
  { code:"R31", cat:"Anima",    desc:"THE WALL OF BEINGS STOPS LYT ATTACKS ONLY. You may not LYT-ATTACK an adversary's Anima while any of their Escendants stands un-Fallen. Fractured Escendants still BLOCK — only Fallen is bypassed, and Esclings never block. The wall binds the SWING and nothing else: a Tome, an Impera, an U'hlti, a Curio or a Primera stamp that dims Anima is NOT bound by it and reaches through a full board of standing beings" },
  { code:"R32", cat:"Anima",    desc:"Anima cannot exceed 50" },
  { code:"R33", cat:"Anima",    desc:"Anima at 0 = loss" },

  // ── TOMES ───────────────────────────────────────────────
  { code:"R34", cat:"Tomes",    desc:"Tomes placed face-down, no cost to place" },
  { code:"R35", cat:"Tomes",    desc:"THE WAIT IS THE PRICE OF SETTING. A Level 1 Tome SET FACE-DOWN is latent for one FULL Turn — two half-turns at a duel, a full lap at a bigger table — before it may flip. A Level 1 Tome played FACE-UP out of hand on your own turn, paying its fare, resolves immediately and waits for nothing. caP'n, 2026-09-13: 'Yes we can, but if you place it face down, you must wait 1 full turn to activate it.' ⚠ TWO FAULTS FELL OUT OF THIS AT ONCE: the board refused the face-up hand play outright at Level 1, and the wait was stored as 1 while every consumer compared it against a HALF-turn delta — so a set L1 woke on the adversary's half-turn, a whole seat early" },
  { code:"R36", cat:"Tomes",    desc:"L2 Tome: may flip and activate on an adversary's turn, including the turn it was set" },
  { code:"R37", cat:"Tomes",    desc:"L3 Tome: everything L2 may, and it may activate from HAND on an adversary's turn" },
  { code:"R38", cat:"Tomes",    desc:"Three Tome rifts a side, no more (TOME_SLOTS)" },
  { code:"R39", cat:"Tomes",    desc:"Tomes ruined after use (unless OPT/OP2/OP3/ATV)" },

  // ── GRAVESHIFT ──────────────────────────────────────────
  { code:"R40", cat:"Graveshift",desc:"Graveshift ONLY when Grimoire is completely empty" },
  { code:"R41", cat:"Graveshift",desc:"Graveshift costs exactly 10 Anima" },
  { code:"R42", cat:"Graveshift",desc:"Graveshift combines hand + Necropolis into new Grimoire" },
  { code:"R43", cat:"Graveshift",desc:"Cannot lose by running out of cards (Graveshift exists)" },

  // ── RESURRECTION ────────────────────────────────────────
  { code:"R44", cat:"Resurrect",desc:"Resurrection tally by line: Beb (L1) 3x · Champion (L2) 2x · Lord (L3) 1x per Solrend, PER CARD INSTANCE. caP'n, 2026-08-26: 'the game is canon, perfectly put as well, because you can have duplicates of a card, those are separate (the Mark helps here so much).' Rulez v2's flat 'once per being per Solrend' is SUPERSEDED — three copies of one Beb carry three separate 3x tallies, which is what necroSeal.js exists to make representable (R85)" },
  { code:"R45", cat:"Resurrect",desc:"Must resurrect in order — L1 first, then L2, then L3" },
  { code:"R46", cat:"Resurrect",desc:"Cannot skip levels (no L3 directly onto Escling)" },
  { code:"R47", cat:"Resurrect",desc:"Cost: L1=2 Khao/Anima, L2=4 Khao/Anima, L3=4 Khao/Anima" },
  { code:"R48", cat:"Resurrect",desc:"Resurrected Escendants keep their Souls" },

  // ── SOUL ────────────────────────────────────────────────
  { code:"R49", cat:"Soul",     desc:"Soul = all cards attached to an Escendant" },
  { code:"R50", cat:"Soul",     desc:"When L1 ruined, Soul sent to Necropolis" },
  { code:"R51", cat:"Soul",     desc:"When L2/L3 descends, only the ruined level leaves; every attached card stays on the survivor" },
  { code:"R52", cat:"Soul",     desc:"Impera, U'hltis and Affinities survive descent — nothing attached is level-bound — unless the entire Soul is ruined" },

  // ── DECK LIMITS ─────────────────────────────────────────
  { code:"R53", cat:"Deck Limits",desc:"L1 Escendant: max 5 of unique name" },
  { code:"R54", cat:"Deck Limits",desc:"L2 Escendant: max 4 of unique name" },
  { code:"R55", cat:"Deck Limits",desc:"L3 Escendant: max 3 of unique name" },
  { code:"R56", cat:"Deck Limits",desc:"L1 Tome: max 3 of unique name" },
  { code:"R57", cat:"Deck Limits",desc:"L2 Tome: max 2 of unique name" },
  { code:"R58", cat:"Deck Limits",desc:"L3 Tome: max 1 of unique name" },
  { code:"R59", cat:"Deck Limits",desc:"U'hlti: max 1 of unique name (and see R104 — no more than 3 U'hltis in a Luxmoire in total)" },
  { code:"R60", cat:"Deck Limits",desc:"Affinity: max 1 per Luxmoire (Affinities are Luxmoire cards — R05). An Affinity in a Grimoire is an R05 violation, not an R60 one" },
  { code:"R61", cat:"Deck Limits",desc:"Curio: max 1 of unique name" },
  { code:"R62", cat:"Deck Limits",desc:"Curio Ally Ilk: max 2 PER SHATTERFIELD, across your pre-placed Curios — one per Curio rift. caP'n, 2026-08-26: 'per Shatterfield, and update it so it's 2 because I made it 2 Curios since way back then, just never wrote it in the rulebook apparently.' Rulez v2's '1 per Grimoire' is wrong on both the number and the container. ⚠ AND THE RULE IS READABLE: the earlier claim that it could not be enforced because no Curio carries an Ally Ilk was simply FALSE — Beb Buddy is ilk 'ally' in cards.js and the Debugger has read it correctly all along" },
  { code:"R63", cat:"Deck Limits",desc:"Prophecy: 1 per Solrend" },

  // ── DEVIANT / HERETICAL ────────────────────────────────
  { code:"R64", cat:"Deviant",  desc:"Deviant Escendants cannot equip U'hltis or Affinities" },
  { code:"R65", cat:"Deviant",  desc:"Deviant Transcend costs nothing but marks Deviant" },
  { code:"R66", cat:"Deviant",  desc:"Only 1 Deviant Ascend per turn" },
  { code:"R67", cat:"Deviant",  desc:"Heretical Training: 6 Khao AND 6 Anima (both), any L3 with an empty U'hlti slot — Natural or Deviant. The rite itself marks the being Deviant permanently" },
  { code:"R68", cat:"Deviant",  desc:"Heretical Tome not part of Soul (doesn't return on Resurrect)" },

  // ── SUPREME BEINGS ──────────────────────────────────────
  { code:"R69", cat:"Supreme",  desc:"Supreme = Natural L3 + 3 Impera + U'hlti + Affinity" },
  { code:"R70", cat:"Supreme",  desc:"Affinity only attachable when Natural L3 has U'hlti. AND THE U'HLTI ITSELF IS LEVEL 3 ONLY — caP'n, 2026-08-26: 'I did want to try Bebs using U'hltis but it's not a good idea, so have the rules say only level 3 can use it.' The code has always enforced it; the in-game Rulebook said 'from Level 1 onward' and was corrected in the same pass" },
  { code:"R71", cat:"Supreme",  desc:"Escendant can only have 1 Impera of each level" },
  { code:"R72", cat:"Supreme",  desc:"Escendant can only have 1 U'hlti or Tome attached" },
  { code:"R73", cat:"Supreme",  desc:"Escendant can only have 1 Affinity attached" },

  // ── RESURRECTION TALLY (July 25, 2026 wave) ────────────
  { code:"R77", cat:"Resurrect",desc:"EVERY resurrection method counts toward the tally — paid, card effect, Tome, Time Primera — no matter method or source" },

  // ── THE SEAL (caP'n ruling, July 27, 2026) ─────────────
  { code:"R85", cat:"Resurrect",desc:"Tallies are PER COPY, not per name. Three copies of one Beb carry three separate 3× tallies — nine returns, not three. Ruined beings are sealed with a per-copy identity on entry to the Necropolis" },
  { code:"R86", cat:"Resurrect",desc:"After a Graveshift, the cards that shifted can NEVER be Resurrected by the paid rite again — only by card effects (Tomes, Impera, the Time Primera). Beings standing on the field during the shift are unaffected" },

  // ── PRIMERAS — THE STAMPS (July 25, 2026 wave) ─────────
  { code:"R78", cat:"Primera",  desc:"Every Escendant bears 2 Primeras — its Ilk's and its School's — innate stamp powers, not cards. Esclings bear none" },
  { code:"R79", cat:"Primera",  desc:"Primera cost = the being's Level (L1=1, L2=2, L3=3), paid ENTIRELY in Khao or ENTIRELY in Anima — never split. See R107; the mixable reading was struck on 2026-08-26 and this was the only fare in the game it had ever applied to" },
  { code:"R80", cat:"Primera",  desc:"Primeras activate on your own Turn only — NEVER in a Response Window, no interrupts" },
  { code:"R81", cat:"Primera",  desc:"One Primera per BEING per Turn — its Ilk or its School, never both" },
  { code:"R82", cat:"Primera",  desc:"One use per WORD per Turn across your whole field — Dragon is ONE word on both axes" },
  { code:"R83", cat:"Primera",  desc:"Primera effects from different beings stack within the Turn; all limits and effects reset at the start of your next Turn" },
  { code:"R84", cat:"Primera",  desc:"Primeras are of the flesh — cannot be ruined, stolen, or detached; Deviance does not strip them" },

  // ── THE CLOCK (caP'n ruling, 2026-08-20) ───────────────
  { code:"R87", cat:"Misc",     desc:"A Solrend lasts at most 20 minutes of live play. When the clock runs out the duel is CALLED, not drawn: highest Anima wins; tie goes to the most beings standing; tie again to the deeper Grimoire; tie again to the first seat. IN 2v2 THE CONTESTANT IS THE TEAM, which this row omitted while the board computed it: pooled Anima through matchSeats.getEffectiveAnima, and beings standing and Grimoire depth SUMMED across both seats (timeoutWinner groups by teamOf and sums both). RULEZ_V2 ch.19 now prints all three legs." },

  // ── RULES THAT WERE ALWAYS IN PLAY AND NEVER IN THE CATALOGUE ──
  // (2026-08-25, the coherency pass.) Every one of these was already
  // enforced somewhere in the engine and invisible to every checker.
  // THE CATALOGUE LAW cuts both ways: a rule the code enforces but
  // rules.js does not name is exactly as dangerous as a stale comment.
  { code:"R88", cat:"Turn",     desc:"Searching costs a fare, paid in Khao or Anima: Grimoire search 2 for a Level 1, 4 above it; Luxmoire search 5. The Anima leg is subject to R96 — you may not pay yourself to zero. ⚠ R08 SUSPENDS THIS FARE ENTIRELY ON THE OPENING CYCLE: neither Book may be searched at any price on a seat's first Turn — a CARD EFFECT that searches is exempt from that suspension (caP'n, 2026-08-26) and pays no fare anyway, because the card already paid its own cost" },
  { code:"R89", cat:"Resurrect",desc:"One PAID Resurrection per Turn, regardless of level. Card-effect returns - Tomes, Impera, the Time Primera - are exempt from this clock (being exempt IS what those cards buy) but still count toward that copy's tally under R77. The first cut of this rule said 'regardless of method' and was wrong: the shipping board has always let the Time stamp ride alongside the rite" },
  { code:"R90", cat:"Turn",     desc:"One mulligan per duel, on the OPENING CYCLE ONLY and only by a seat that has NOT YET DRAWN that Turn: return the opening hand and redraw the same number. A public act. caP'n, 2026-08-25: 'Either player, if they have not drawn yet, they can do a Mulligan on that first turn and no other'" },
  { code:"R91", cat:"Khao",     desc:"No natural Khao gain on the opening Turn of the duel — the climb starts on Turn 2" },
  { code:"R92", cat:"Tomes",    desc:"A response chain resolves last-declared-first. Any Nihil or Omnia of the right level may answer a declaration OR an answer — L3 from hand, L2+ from a face-down field card — and a rebuttal must be of the same level or higher. The real bound on a chain is cards and Khao — both sides run out of L3 instants long before any counter does" },
  { code:"R93", cat:"Misc",     desc:"THE SECOND HAND. A Solrend that reaches 150 half-turns is CALLED by the same verdict as R87. Twenty minutes is the human clock; 150 half-turns is the clock every simulated duel can reach. In a live player-versus-player Solrend the same bell hangs at TURN_CLOCK_PVP (500), out of reach of any real game, so R87 rings first. ⚠ 2026-09-15 UNIFICATION PASS: 'live PvP' is READ OFF THE BOARD (isLivePvP — every seat a signed-in human, no brain seat) because until that day clockLimitFor looked for `mode === \"pvp\"` — a label nothing in the game ever wrote — and every human duel rang at 150 while this row said 500. The Debugger's R93 check had stayed green by feeding it the fake shape (§4.4). The harness declares controller \"ai\" on its seats to keep the 150 bell and the ratified clock-call band" },
  { code:"R94", cat:"Deck Limits",desc:"Impera deck limits mirror Escendants by level: L1 max 5, L2 max 4, L3 max 3 of a unique name, per Luxmoire" },
  { code:"R95", cat:"Misc",     desc:"MODE OVERRIDES ARE RULES TOO. Boss phases suspend R33 for the boss seat and refresh its Anima to MAX_ANIMA between phases; Endless Depths and the Gauntlet carry Anima between bouts, refreshing only at a rung top. Every other mode is R-universal" },
  { code:"R96", cat:"Anima",    desc:"THE TILL LAW. A VOLUNTARY payment may never reduce your own Anima to 0 — the action is illegal, not suicidal. Damage and adversary effects still can, and R33 still ends the duel when they do. ONE EXEMPTION: the Graveshift fare (R41). R43 says you cannot lose by running out of cards, so R43's own remedy must always be payable — a seat on exactly 10 Anima with an empty Grimoire may still shift, and if that empties its Anima, R33 collects. Forbidding it made R96 a lock-maker inside the very pass that broke the lock" },

  // ── THE 2026-08-25 RULINGS (caP'n, verbatim in the banners above) ──
  { code:"R97", cat:"Misc",     desc:"THE PROPHET'S TOLL. In any mode that is NOT 1v1, a fulfilled Prophecy does not eliminate anyone — instead EVERY adversary takes a dim: if that adversary's current Anima is greater than 30 it becomes ceil(anima/2); otherwise it loses a flat 15. The two clauses are exclusive, not cumulative. It is an adversary effect, not a voluntary payment, so R96 does not protect against it and R33 collects if it reaches 0. In 1v1 a fulfilled Prophecy still wins the Solrend outright, subject to R98" },
  { code:"R129", cat:"Tomes",   desc:"THE TWO FED TREASURES, AND WHAT THEY EAT. caP'n, 2026-09-14, describing the whole life of one: 'you(the player) can only feed it, no one else, then once you have fed the necklace 6 Khao (so you just give it Khao and it stores it for you, if the card is ruined the Khao is gone) then once its full of 6 of the owner's Khao, the owner gets 6 Anima. That Tome has 3 Lyf, so it remains on the board (most if not all treasures behave this way) until ruined by Lyt attack or other means' — and, asked whether it may be filled twice: 'yes, it can be fed after use, it needs to be ruined for it to be taken off the shatterfield.' So: the food is the card's PRINTED currency (Khao for Joyous Courage, Anima for Congealed Death), `perTurn` is a real ALLOWANCE and not one feed per Turn, ONLY THE OWNER may feed it on their own Turn, the food is STORED ON THE CARD and LOST if the card is ruined (never refunded — that risk is what the card sells), the payout fires at the threshold and THE TREASURE STANDS, then THE STORE CLEARS AND IT MAY BE FILLED AGAIN, indefinitely, leaving the field only when something ruins it. TWO CONSEQUENCES: a Treasure's printed `lyf` is DURABILITY and never a fare — 'most if not all treasures behave this way', which generalises R105's 'you cannot pay Lyf' to the whole type; and THE FOOD IS NOT A SECOND FARE, because the printed khaoCost is what it costs to PLAY the Treasure and feeding is not a firing. ⚠ `perTurn` is therefore LOAD-BEARING rather than cosmetic: with the recharge, the allowance is the only brake on a permanent engine. All three numbers live once, in ATV_FEED" },
  { code:"R130", cat:"Misc",    desc:"A FULFILLED PROPHECY CANNOT BE ANSWERED, AND IT INTERRUPTS. caP'n, 2026-09-14, asked both halves. On timing: 'yes once its fulfilled and the conditions met etc you can activate immediately and interrupt the entire duel.' On whether anything can stop it: 'No, nothing can interrupt them; Prophecies are auto wins if fulfilled.' So the Prophecy is the ONE resolution in Terror that opens no response window: no Nihil, no Omnia, no Art, no broad answerScope (R120) and no chain link may touch it, on anyone's Turn. It is cashed the instant its condition is true — including in the middle of an adversary's Turn — and the duel ends there. ⚠ THE BOARD REFUSED THIS UNTIL 2026-09-14: handleActivateProphecy opened `if (!myTurn …) return`, and worse, it never asked whether the condition was FULFILLED at all — the only lock was a button declining to render, which is a rule living in the renderer. Removing the turn check without adding a real condition check would have shipped a win button; both landed together. ⚠ AND R120 IS NOT A ROUTE IN: a card broad enough to answer an Art still cannot answer a Prophecy, because a Prophecy is not an activation, an effect or a card — it is the duel ending" },
  { code:"R98", cat:"Misc",     desc:"PROPHECIES GRANT WINS, THEY DO NOT PREVENT THEM. A Solrend can end with MORE THAN ONE winner: a seat that won on the board still won, and a Prophecy fulfilled by that same blow ALSO wins. `winners` is the truth; `winner` stays populated with the primary/board winner for every consumer written before this ruling. caP'n: 'both players win... prophecies do not prevent wins merely grant them'" },
  { code:"R99", cat:"Misc",     desc:"A PROPHECY IS SPENT WHEN IT RESOLVES. One Prophecy, one resolution, per duel — on BOTH doors (the automatic hand-off check and the manual Prophecy button). Written on the seat as `prophecySpent`. This fixes a confirmed defect where one Ace of Shades fired three times and eliminated a whole 4-player table by itself" },

  // ── THE 2026-08-26 RULINGS (caP'n, verbatim in the banners above) ──
  { code:"R100", cat:"Anima",   desc:"SELF-INFLICTED ANIMA LOSS NEVER FEEDS A PROPHECY. caP'n: 'dimming your own Anima in any way at all does not satisfy Prophecy requirements'. Anima an ADVERSARY took off you counts toward every Prophecy that reads Anima loss (Bloody Hell at 45); Anima you dimmed YOURSELF — a card cost, a self-dim effect, the Graveshift fare, a Devotion, a search fare, a voluntary payment of any kind — counts toward NOTHING. Two ledgers, never mixed: totalAnimaLost / animaLostThisTurn is the adversarial one and is the ONLY one a Prophecy may read; totalAnimaSelfDimmed / animaSelfDimmedThisTurn records the rest. Before this rule one counter took both and a seat could win Bloody Hell by hurting only itself" },
  { code:"R101", cat:"Deviant", desc:"THE DEVIANT FARE, one table for Ascending AND Training (Rulez v2 ch.15, twice confirmed). 1 trait shared: L1 = 1 Khao or 1 Anima, L2 = 2 or 2, L3 = 3 or 3. 0 traits shared: L1 = 2 or 2, L2 = 3 or 3, L3 = 5 KHAO OR 4 ANIMA — that last row is deliberately asymmetric and is the only fare in the game where Anima is cheaper than Khao. THE LEVEL IS THE DESTINATION: for a Training it is the Impera's own level; for an Ascend it is the level being ascended TO, never the level being left. ⚠ The '1 trait shared' column is UNREACHABLE with the shipped card set — Ilk and School are paired 1:1, so sharedTraits is only ever 0 or 2, never 1. Deviant Transcend is unreachable for the same reason" },
  { code:"R102", cat:"Deviant", desc:"ONE ASCEND PER TURN, TOTAL — Natural or Deviant, they share the single budget (Rulez v2 ch.4 Main Phase and the glossary, stated twice). One Deviant Training per Turn. ANY NUMBER of Deviant Transcends per Turn, and Natural Impera Trainings carry no per-Turn limit at all" },
  { code:"R109", cat:"Turn",    desc:"THE GRIMOIRE SEARCH IS A RULE OF THE GAME, NOT AN ACCIDENT OF THE ENGINE. A seat may pay the R88 fare — 2 Khao or Anima for a Level 1, 4 above it — and take one being of that level out of its own Grimoire, spending the Turn's draw to do it (R11). caP'n, 2026-08-26: 'maybe I never told you to add it and you never realized it was missing till now… let's add that, it's pivotal to the game… keeps the game fluid and you never feel helpless, you have access to attain power at your fingertips… but it will of course cost you.' It is DESIGNED, and the design is the price: the fare, plus your draw. R08 suspends it entirely on the opening Cycle" },
  { code:"R104", cat:"Deck Limits",desc:"THREE U'HLTIS TO A LUXMOIRE. Beyond R59's 1-per-unique-name, a Luxmoire may hold at most 3 U'hltis in TOTAL. caP'n, 2026-08-26: \"U'hltis rules as it was in Grimoire just for the Lux, which should be 3 U'hltis if I'm not mistaken.\" Three different names is legal; a fourth is not. ⚠ 'if I'm not mistaken' — the 3 is the one number in this wave the designer may want to confirm" },
  { code:"R128", cat:"Tomes",   desc:"WHEN AN IMPERA'S PRINTED NUMBER IS PAID. caP'n, 2026-09-14: 'To keep consistency, we are going to make the user pay to use an Impera if it has OPT, OP3, etc type acronyms. If it doesn't have them, then you only pay once, if it does, you must pay to activate again.' A NATURAL Training pays the card's printed fare — always, tagged or not, exactly as RULEZ_V2 ch.10 has always said. A DEVIANT Training pays the R101 table INSTEAD, never both. A tagged Impera then pays the printed fare AGAIN on every activation (R105). An UNTAGGED Impera is a passive with no activation, so the Training payment is the only payment it ever takes. ⚠ THE BOARD CHARGED NOTHING FOR A NATURAL TRAINING until this ruling — the whole fare block sat inside handleAttachImpera's `if (!isNatural)` branch and NPC.trainOneImpera touched no pool — so a full Impera set attached free and an untagged Impera like Good Morning was free FOREVER. IMPERA ONLY: U'hltis and Affinities still attach free and pay at activation, which is already consistent because all sixteen of them carry a tag" },
  // ══════════════════════════════════════════════════════════════
  // 🕰 R126 · THE HALF-TURN CLOCK — FORTY-FIVE SECONDS
  // ══════════════════════════════════════════════════════════════
  // ⚠ WRITTEN DOWN 2026-09-14, AND IT HAD BEEN SHIPPING FOR WEEKS.
  // `PLAYER_TIMER = 45` (in THIS file since 2026-09-15; it sat in
  // Shatterfield.js as a local literal until then) force-ends the
  // acting seat's Turn at zero, in EVERY mode, against a living
  // adversary and against a brain alike. It had no rule code, no row
  // here, and no line in the rulebook — whose clock chapter was called
  // "The Two Clocks" while three clocks shipped. A player who did not
  // know it existed lost Turns to it. RULEZ_V2 ch.19 is now "Victory &
  // The Three Clocks" and states it.
  // ⚠ THE ROW IS V'AEL'S WRITING, THE RULE IS caP'n's GAME. Every clause
  // below is read off the shipping board, not invented — but caP'n has
  // never been asked whether the row says it the way he means it, and he
  // is being asked in the same session it was written. Nothing about the
  // behaviour changed.
  { code:"R126", cat:"Turn",     desc:"THE HALF-TURN CLOCK — 45 SECONDS. rules.PLAYER_TIMER (moved here from Shatterfield.js on 2026-09-15 — it was the one rule constant outside the catalogue), applied to every duel, PvP and NPC alike. At zero the acting seat's Turn is force-ended where it stands (doEndTurn), and whatever that seat had not done, it does not do. IT HOLDS and does not tick while: a response window is open, a chain is running (the GAPS between links included, while an adversary decides), a cinematic hold is playing (strikeLanguage.holdActive), or a PvP declaration is in flight. It does not run AT ALL on a Turn whose controller is 'ai' — a seat is never billed for a brain's thinking. A 10s grace past zero lets one elected client end a disconnected adversary's Turn. ⚠ IT IS NOT ONE OF R87/R93's DUEL CLOCKS: it ends a TURN, never a Solrend, and it produces no verdict. The board never opens it until both seats are ready, which is why the ready-gate exists" },
  // ══════════════════════════════════════════════════════════════
  // 🪑 R127 · THE SEAT SHAPES
  // ══════════════════════════════════════════════════════════════
  // ⚠ ALSO WRITTEN DOWN 2026-09-14, for the same reason: the catalogue
  // contained ZERO occurrences of "2v2", "team", "free-for-all" or
  // "co-op" while V'yen's Stall offers all of them and matchSeats.js
  // exists to serve them. Same provenance as R126 — read off the board,
  // flagged to caP'n in the session it was written.
  // \u23f0 R132 \u00b7 WHEN THE MAX-KHAO CLIMB LANDS
  //
  // caP'n, 2026-09-15, answering "does the climb land at the end of your
  // Turn or the start of your next one":
  //
  //   *"after 1 full turn of not using Khao, so if you didn't use any
  //   turn 1, then the beginning of turn 2 you get +1 to your Max Khao
  //   hopefully that also clarifies other rulings as far as how the 1
  //   full turn etc works"*
  //
  // R12 already said WHAT breaks the climb. This says WHEN it pays, and
  // his last clause generalises it: "1 full turn of not X" means a whole
  // full Turn must ELAPSE, and the reward arrives at the START of the
  // next one. Same shape as R126's durations (R129's feeds, R123's
  // wards) \u2014 one unit, measured in full Turns, paid on the boundary.
  //
  // \u26a0 THERE WERE FOUR COPIES OF THIS RULE AND THEY DISAGREED. The
  // renderer granted at the start of the player's Turn (correct, as it
  // turns out) and all three NPC runners granted at the END of their own
  // \u2014 so a brain played its second Turn on the OLD ceiling and only held
  // the climbed one on its third. THE BRAIN WAS A FULL TURN BEHIND THE
  // PLAYER ON MAX KHAO FOR THE WHOLE DUEL, in the player's favour.
  // Nobody chose that. It was drift between four copies of one number,
  // which is the exact failure turnEngine's own banner predicted when
  // the Khao refill moved there: "the same value written in five places
  // is five places for it to drift, and it had already drifted once."
  //
  // \u2b50 SO THE CLIMB NOW LIVES ONCE, IN handOffHalfTurn, granted to the
  // seat SITTING DOWN, riding the same atomic chair write that already
  // refills its pool \u2014 so the pool refills to the CLIMBED ceiling and no
  // client can observe a seat sitting down with a stale one. The
  // renderer and all three brains hold no copy. A renderer that decides
  // a rule is a rule that can be observed twice.
  //
  // \u26a0 AND THE GATE WAS A TWO-SEAT APPROXIMATION. `turn` counts
  // HALF-turns, so `turn >= FIRST_KHAO_TURN` (2) reads as "not your first
  // Turn" only at a 1v1 table. At 3-4 seats a full Turn is a full LAP
  // (R127), so the seats sitting down into half-turns 2 and 3 were on
  // their FIRST Turns and were handed the climb before they had played at
  // all \u2014 against both this ruling and R91. The floor is now
  // `Math.max(FIRST_KHAO_TURN, halfTurnsPerFullTurn(seatCount))`, which is
  // byte-identical at 1v1 and strictly later at 3-4. It can only ever
  // RAISE R91's floor, never lower it.
  //
  // \u26a0 AND IT NEVER CLAMPS DOWNWARD. The +1 stops at MAX_KHAO_NAT; a
  // ceiling a CARD pushed above 10 is KEPT. `Math.min(10, current + 1)`
  // is the shape that deleted a Khao Arcadia's whole effect on the next
  // quiet end-of-turn, and it must not come back.
  { code:"R132", cat:"Khao",    desc:"WHEN THE MAX-KHAO CLIMB LANDS. caP'n, 2026-09-15: 'after 1 full turn of not using Khao, so if you didn\u2019t use any turn 1, then the beginning of turn 2 you get +1 to your Max Khao hopefully that also clarifies other rulings as far as how the 1 full turn etc works.' R12 says what BREAKS the climb; this says when it PAYS \u2014 at the START of the Turn after a full Turn has elapsed quietly, never at the end of the Turn that earned it. His closing clause generalises the LENGTH and not the landing: any 'for 1 full turn' condition needs a whole full Turn to ELAPSE. ⚠ IT DOES NOT MAKE EVERY CLOCK PAY ON A TURN BOUNDARY, and reading it that way was V'ael's error on the day it landed — see R135. The climb pays on the boundary because the climb is tied to a SEAT'S OWN Turn opening; a DURATION starts where it is stamped and expires 2N half-turns later, wherever that falls (R103, R123, R129, R135) \u26a0 THERE WERE FOUR COPIES AND THEY DISAGREED \u2014 the renderer granted at the start of the player\u2019s Turn and all three brains at the END of their own, leaving every brain A FULL TURN BEHIND THE PLAYER on Max Khao for the entire duel, in the player\u2019s favour. It now lives ONCE, in turnEngine.handOffHalfTurn, granted to the seat SITTING DOWN inside the same atomic chair write that refills its pool \u2014 so the pool refills to the CLIMBED ceiling. \u26a0 THE GATE WAS ALSO A TWO-SEAT APPROXIMATION: `turn` counts HALF-turns, so a bare `>= 2` handed the climb to seats still on their FIRST Turn at a 3-4 seat table, where a full Turn is a full LAP (R127). The floor is `Math.max(FIRST_KHAO_TURN, halfTurnsPerFullTurn(seatCount))` \u2014 identical at 1v1, strictly later beyond it, and it can only raise R91\u2019s floor. \u26a0 AND THE NATURAL CLIMB NEVER CLAMPS A CARD-PUSHED CEILING DOWNWARD (R13)" },

  // \u23f3 R133 \u00b7 WHAT THE CLOCK BURIES WHEN THE HAND IS OVER THE CAP
  //
  // caP'n, 2026-09-15, asked which card the game should bury when a
  // Turn timer expires on a seat holding nine: *"the newest card,
  // that's 4xS right there"* \u2014 4xS being his own simplicity filter.
  //
  // R10 caps the hand at 8 and has never said WHICH card goes, because
  // for a human it is a choice. That is why the two doors get different
  // answers and always have: THE BUTTON is refused and a modal lets the
  // player pick, because a choice is theirs; THE TIMER cannot ask, so it
  // buries and tells the seat what left. His ruling governs the timer.
  //
  // \u26a0 THE OLD PICK WAS CLEVER AND THAT WAS THE PROBLEM: cheapest Tome,
  // else a second copy, else the last card. Three tie-breaks a player
  // cannot predict while watching a clock run out. "The newest" is a
  // pure function of the hand's LENGTH \u2014 no card data, no sort, no
  // tie-break \u2014 so it is also strictly MORE deterministic, which was the
  // original reason that function was hand-written: two clients can both
  // reach a grace-expiry hand-off in the same second, and two clients
  // burying two different cards is a board that can be observed wrong.
  //
  // \u26a0 AND IT DELIBERATELY NO LONGER MIRRORS THE BRAIN. npcPickDiscardIdx
  // still prefers the cheapest Tome, because a brain CHOOSING is the
  // equivalent of a human picking from the modal, not a forfeit \u2014 a
  // brain has no clock. That pick also carries a MEASURED result:
  // preferring the Tomes the dispatcher cannot fire cost FIVE POINTS of
  // clock-call rate. The mirror must not be restored.
  { code:"R133", cat:"Turn",    desc:"WHAT THE CLOCK BURIES. caP'n, 2026-09-15: 'the newest card, that\u2019s 4xS right there.' When a Turn timer expires on a seat holding more than R10\u2019s eight, the game buries THE NEWEST CARD IN HAND and names it. R10 caps the hand and has never said which card goes, because for a human it is a CHOICE \u2014 which is why the two doors differ on purpose: the END TURN BUTTON is refused and a modal lets the player pick; THE TIMER cannot ask. \u26a0 The old automatic pick was cheapest-Tome, else a duplicate, else the last card \u2014 three tie-breaks nobody can predict while a clock runs out. 'The newest' is a pure function of the hand\u2019s length, so it is also MORE deterministic, which matters because two clients can reach the same grace-expiry hand-off in the same second and two clients burying different cards is a board that can be observed wrong. \u26a0 IT NO LONGER MIRRORS THE BRAIN, ON PURPOSE: npcPickDiscardIdx still prefers the cheapest Tome because a brain choosing is the equivalent of a human picking from the modal, and that pick carries a measured five-point clock-call result. THE MIRROR MUST NOT BE RESTORED" },

  // \ud83e\udd7c R134 \u00b7 V'AEL'S VEIL COSTS NOTHING
  //
  // caP'n, 2026-09-15: *"completely free :3 purely narrative"*
  //
  // And what it is FOR, 2026-09-14, which is the whole reason it exists
  // and is written down nowhere else:
  //
  //   *"the veil was more Narrative, just filling the plot hole of why
  //   the couple aren't just immediately swarmmed after the first battle
  //   since their location is revealed. The cloak is the reason they can
  //   freely move around the Overworld... swarming one location would
  //   prove unwise as it leaves other spots unattended."*
  //
  // vaelsTools.js shipped a LEDGER \u2014 1 Khao "forever transferred" and 1
  // Lyf "kept on standby" per veiled step \u2014 with a banner saying the
  // price did not bite the duel YET and that making it real was caP'n's
  // to say when. He has said: it never will.
  //
  // \u26a0 AND THE BANNER'S OWN CLAIM WAS FALSE, which is why this is a rule
  // row and not a deletion. It said the debt was "counted, shown in the
  // chrome, and persisted." None of the three was true: stepCost,
  // addSteps, ledgerLine and priceRows had NO callers outside their own
  // file, saveVeilLedger had no callers at all, and the ctx.veil the
  // world wrote was never read back. A comment describing a system that
  // does not exist is the most expensive kind, because every later
  // reader budgets around it \u2014 this brief did, and said so.
  //
  // \u2b50 THE PROSE STAYS. "Khao, forever transferred. You will not feel it
  // go." is flavour now, marked as flavour. caP'n keeps placeholders on
  // purpose \u2014 "I love when you make placeholders it makes it easier for
  // me to go in and edit" \u2014 so a blessed placeholder is not unfinished
  // work and must never be swept as such (see 2.23).
  { code:"R134", cat:"Misc",    desc:"V'AEL'S VEIL COSTS NOTHING. caP'n, 2026-09-15: 'completely free :3 purely narrative.' The cloak exists to close a STORY hole, not to price movement \u2014 2026-09-14: 'the veil was more Narrative, just filling the plot hole of why the couple aren\u2019t just immediately swarmmed after the first battle since their location is revealed... swarming one location would prove unwise as it leaves other spots unattended.' So no Aenergy is owed, transferred or held on standby for a veiled step, and nothing is deducted at the top of a Solrend. \u26a0 THE LEDGER IT REPLACES WAS NEVER WIRED, and the banner claiming otherwise is the finding: it said the debt was 'counted, shown in the chrome, and persisted' when stepCost/addSteps/ledgerLine/priceRows had no callers outside their own file, saveVeilLedger had none at all, and the stored ledger was written and never read. A comment describing a system that does not exist costs every later reader, because they budget around it. \u2b50 The price PROSE survives as flavour, marked as flavour \u2014 a placeholder caP'n has blessed is not unfinished work" },

  // \u23f1 R135 \u00b7 A DURATION'S CLOCK STARTS WHERE IT IS STAMPED
  //
  // caP'n, 2026-09-15, clarifying his own duration ruling the moment
  // R132 risked being over-read:
  //
  //   *"a clarified previously however, that if a Tome or something is
  //   activated on an opponent's turn, the 1 full turn (or more) start
  //   at that moment, that way you dont get a free 1/2 turn skip, you
  //   still have to wait 2 half turns no matter, just depends when it
  //   starts"*
  //
  // So a duration is anchored to THE MOMENT, not to a Turn boundary. A
  // ward fired in the middle of an adversary's half-turn runs the same
  // 2N half-turns as one fired at the top of your own, and it runs OUT
  // in the middle of a half-turn too. There is no rounding, in either
  // direction. "Just depends when it starts" is the whole rule.
  //
  // \u26a0 THIS EXISTS BECAUSE R132 WAS ABOUT TO BE OVER-READ, AND THE
  // OVER-READING WAS V'AEL'S, IN WRITING. R132 settled that the Max-Khao
  // climb pays at the BEGINNING of a Turn, and the row for it claimed
  // his closing clause meant every "for 1 full turn" condition "pays on
  // the boundary". It does not. The climb pays on a boundary for a
  // reason that belongs to the climb alone \u2014 it is tied to a seat's own
  // Turn opening, so there is nowhere else for it to land. A duration
  // has no such tie. Snapping durations to boundaries would have handed
  // out exactly the free half-turn he names: a ward stamped late in an
  // adversary's half-turn would have expired at the next boundary
  // instead of two half-turns later.
  //
  // \u2b50 AND THE BOARD WAS ALREADY RIGHT, WHICH IS WHY THIS IS A ROW AND
  // NOT A FIX. `durationExpiryTurn(now, N) = now + N * halfTurnsPerFullTurn`
  // anchors on the live `match.turn` at the instant of the write, and
  // `durationStillRunning` is exclusive since his 2026-09-14 ruling \u2014
  // so an ODD anchor (an adversary's half-turn) and an EVEN one produce
  // identical lengths. Verified by execution, not by reading: a 3-Turn
  // duration stamped on half-turn 1 and on half-turn 3 both live exactly
  // 6 half-turns. The climb's own window measures the same way: a spend
  // on an adversary's half-turn costs 2 quiet half-turns and a spend on
  // your own costs 3, never fewer than 2. No free skip anywhere.
  //
  // \u26a0 THE NAMED BOUNDARIES ARE STILL EXEMPT, as they have been since
  // 2026-09-14: "at the end of the turn", "on your turn", "the turn it
  // was constructed" are SPECIFIC and stay half-turns. caP'n draws that
  // line himself \u2014 "I will specify with words like 'on your turn'".
  // Only a bare number of turns is broad, and only a broad one is a
  // duration in this sense.
  { code:"R135", cat:"Turn",     desc:"A DURATION'S CLOCK STARTS WHERE IT IS STAMPED. caP'n, 2026-09-15: 'if a Tome or something is activated on an opponent\u2019s turn, the 1 full turn (or more) start at that moment, that way you dont get a free 1/2 turn skip, you still have to wait 2 half turns no matter, just depends when it starts.' A bare 'for N turns' is anchored to THE INSTANT the effect is stamped, not to the next Turn boundary, and runs N x halfTurnsPerFullTurn half-turns from there \u2014 so a ward fired mid-adversary-half-turn lasts exactly as long as one fired at the top of your own, and expires mid-half-turn. No rounding in either direction. \u26a0 IT IS THE LIMIT ON R132: the Max-Khao climb pays at the BEGINNING of a Turn because the climb is tied to a seat\u2019s own Turn opening and has nowhere else to land; a duration has no such tie, and R132\u2019s row wrongly generalised 'pays on the boundary' to every clock before this clarification arrived. Snapping durations to boundaries would have handed out the very free half-turn he names. \u2b50 THE BOARD WAS ALREADY RIGHT: durationExpiryTurn anchors on the live match.turn and durationStillRunning is exclusive (R103, 2026-09-14), so an odd anchor and an even one give identical lengths \u2014 proven by execution, a 3-Turn duration stamped on half-turn 1 and on half-turn 3 each live exactly 6. \u26a0 THE NAMED BOUNDARIES STAY EXEMPT: 'at the end of the turn', 'on your turn', 'the turn it was constructed' are SPECIFIC and remain half-turns, because he draws that line himself" },
  { code:"R136", cat:"Tomes",    desc:"ANYONE MAY ANSWER. caP'n, 2026-09-15: 'anyone can answer it, even in a free for all, that adds a lot of drama.' A declaration — a Tome, an Impera, a stamp, a swing, a rebuttal — opens a response window for EVERY living adversary of the declarer at the table, not only the seat it was aimed at. At 1v1 that is the one other seat, byte-identical to before. The chain is unchanged (R‑ChainLaw): last declared, first resolved; the FIRST legal answer to arrive is the link on top, and every other adversary may rebut on the next link. ⚠ ONE LEG IS V'AEL'S: 'anyone' is read as every living ADVERSARY — a 2v2 teammate does not answer their own partner's card. Reverse at matchSeats.answerersFor. ⚠ THE BOARD HAD THIS BACKWARDS FOR EVERY 3–4 SEAT MODE: Shatterfield's PvP lane let only pa.targetSeat answer (`aimedAtMe`), so at a FFA table two of three adversaries had no window at all. The handshake grew per-seat slots (pendingResponses/{seat}, one writer per node) and the declarer settles from the set; the legacy single pendingResponse slot is still read so a client one version behind cannot stall a duel. ★ AND IN CO-OP THE PARTNER CAN ANSWER THE BOSS (closed the same evening): the brain's window is in-process on the host, so the host's responder now FANS OUT — its own window plus the Firebase handshake written on the brain's behalf (`as: aiSeat`, `proxyBy`) — and the first legal answer wins; the loser's window is withdrawn. Every living adversary of the boss holds the window, exactly as at a human table" },

  { code:"R131", cat:"Setup",    desc:"A CARD IN NO STARTER DECK IS NOT A DEAD CARD. caP'n, 2026-09-14, asked whether the Necklace of Congealed Death — a finished, printed card that appears in no starter deck, so no brain can play it and the simulator can never exercise it — was meant to be undraftable: 'no cards like those are still purchasable and attainable as rewards or packs etc.' So absence from the starter decks is the RARITY DOCTRINE working as designed: the cards deliberately left out are the good ones and are meant to feel rare — booster packs, special bosses, easter eggs, the shop. ⚠ THE CONSEQUENCE FOR THE INSTRUMENTS, which is why this is a rule and not a note: a card the balancer never fires is NOT evidence of a dead card, and a coverage number that counts it as one is lying in the expensive direction. When a fix touches a card no starter deck carries, say plainly that the simulator did not see it and prove it by lifted execution instead — never let 'the balancer was green' stand in for 'the card works'" },
  { code:"R127", cat:"Setup",    desc:"THE SEAT SHAPES. A Solrend seats 2-4: 1v1, free-for-all (3 or 4), 2v2, or co-op. Solrends were single-player-and-1v1 only until caP'n reversed it on 2026-08-22. IN 2v2 TEAMMATES SHARE ONE ANIMA POOL AND NOTHING ELSE — not Khao, not hands, not boards, not Books. matchSeats.getEffectiveAnima is the ONE reader of that pool, and every verdict and every death check goes through it; R87's call and R93's bell both sum the team (see R87). A full Turn at a 3-4 seat table is a full LAP of the table, which is what halfTurnsPerFullTurn(seatCount) exists for — every cadence, every duration and every 'once per turn' in this catalogue is measured on it" },
  { code:"R125", cat:"Tomes",   desc:"AN INTANGIBLE TREASURE CANNOT BE RUINED. Khao Paradise and Stars and Skies are Intangible for the half-turn they were constructed; the Necklace of Akula is Intangible for three FULL Turns from the same anchor (R103 — a named boundary means that boundary, a bare number of turns means full Turns). ⚠ ALL THREE WERE DEAD: attemptRuinOppTome, the one funnel every Tome ruin passes through, asked about nullifyFirstRuin and nothing else — two of the cards wrote a flag nobody read and Akula never wrote one at all" },
  { code:"R124", cat:"Combat",  desc:"A SECOND SWING, WHERE A CARD PRINTS ONE. R30 gives each Escendant one Lyt Attack per Turn; two shipped cards print an exception and neither worked. King of the Anthill (Impera L3) grants one extra swing per OTHER Antdroid being in combat — Shatterfield counted them into a local and a comment claiming they were 'tracked as extraAttackCount on the slot', a field that existed nowhere else in the codebase. ⚠ Sword of Shadows is NOT the same bug: it grants through slot.extraAttack, a boolean one-shot both attack paths honour, and it works. A GRANT and a PASSIVE add. The allowance is now a COUNT (slot.attacksThisTurn) and `attackedThisTurn` is still written the moment it is spent, because three auditors read it" },
  { code:"R123", cat:"Tomes",   desc:"THE TOME WARD, AND ITS TWO SCOPES. Shower of Lyt wards your BEINGS from Tome attacks for the turn; Phoenix Aura wards your CARDS — beings, Souls, Tomes, Curios, Prophecy, hand and Books — for two full Turns. caP'n, 2026-09-13: 'Cards is general and Beings is specific, written and chosen specifically for those purposes.' Neither ward touches Anima or Khao: those are pools, not cards. ⚠ BOTH CARDS WERE DEAD — they raised a field flag nothing read, on both sides. The ward is answered at the PAYLOAD, like R08, because that is the only layer at which 'beings' and 'cards' are different questions" },
  { code:"R122", cat:"Tomes",   desc:"THE CADENCE TAGS BIND EVERY CARD, NOT ONLY TOMES. OPT/OP2/OP3/ATV printed on an Impera, an U'hlti or an Affinity is a real once-per-N-Turns limit on that card's activation, counted in half-turns by the same clock a Tome's is. caP'n, 2026-09-13: 'every card, acronyms like OPT OP3 etc are real, they are not just there to look cool.' Enforced on both sides via slot.soulUsed, a per-card stamp — never one flag for the whole being, because a decorated being wears up to five activatable cards with three different cadences" },
  { code:"R105", cat:"Tomes",   desc:"THE ACTIVATION FARE IS PAID EVERY TIME. A Tome or Curio pays its printed Khao and Anima on EVERY activation — the first and every one after it. A persistent card (OPT/OP2/OP3/ATV) buys the right to STAY on the field, not the right to fire for free. And when a card prints BOTH numbers it pays both, each out of its own pool — that is the card naming two pools, not the split R107 forbids. Lyf printed on a Treasure is its durability and is never payable. caP'n, 2026-09-13: 'you must pay its fare again, thats the cost of it being able to stay on the field indefinitely.' OVERTURNS the 2026-08-26 'paid once' reading in full" },
  { code:"R106", cat:"Turn",    desc:"A LUXMOIRE SEARCH FINDS IMPERA, AND ONLY IMPERA. caP'n, 2026-08-26: 'you can still only search the Lux for Impera, not the U/A's.' U'hltis and Affinities live in the Luxmoire (R05) but may only be DRAWN, never tutored — 'because we added the U'hlti and Affinity into the Lux we can't have it so easy.' The search also COSTS YOUR DRAW — 'Yes it costs your draw, it's one or the other' — which supersedes Rulez v2 ch.3 and ch.11's 'this does not consume your turn's draw', stated there twice. A CARD EFFECT that names its own target is not this search and is bound only by its own text" },
  { code:"R107", cat:"Khao",    desc:"NO MIXING KHAO AND ANIMA. EVER. Every fare is paid ENTIRELY out of one pool. caP'n, 2026-08-26: 'no mixing for any reason at all unless the card tells you, like U'hltis etc.' If one pool cannot cover a cost alone, that cost cannot be paid from it. THE EXCEPTION IS NOT AN EXCEPTION: a card whose own text names BOTH currencies charges both in full — Heretical Training's 6 Khao AND 6 Anima (R67), Show Stomper's 10 and 10 — which is two fares on one card, not one fare split. ⚠ THIS OVERTURNS A STANDING RULE: the Primera stamp fare (R79) was genuinely mixable in the engine and in this catalogue, and is not any more. Rulez v2's 'You may mix' line for Resurrection is superseded" },
  { code:"R108", cat:"Turn",    desc:"THE PHASE GATE — REPLENISH · DRAW · MAIN · ATTACK · END. caP'n, 2026-08-26: 'After main phase is over — playing all your cards, ascending, resurrection, everything — now you declare attack phase, you cannot play any more cards.' Declaring Attack is an explicit, ONE-WAY step inside your own Turn. Once declared: NO MORE CARDS MAY BE PLAYED — no Tome activation or face-down placement, no Transcend, no Ascend, no Training, no Affinity, no Heretical Training, no Resurrection, no Curio, no Graveshift. STILL LEGAL: Lyt Attacks and Anima strikes, cards ALREADY ATTACHED to a being's Soul — its Impera, its U'hlti, its Affinity ('yes you can use cards attached to a being's Soul here') — and Primera stamps, which are of the flesh (R84) and never cards. A response window on your ADVERSARY's Turn is not your Main phase and is unaffected: your phase pointer is only ever read for the acting seat" },
  { code:"R103", cat:"Tomes",   desc:"A FULL TURN IS TWO HALF-TURNS. caP'n: 'once per turn ... is referring to combination of the user who played the card (1/2 turn) ending then the turn of their opponent ending, thus creating 1 full turn. But if I say at the end of the user's turn, that is the half turn.' So the Rulez v2 ch.18 cadence tags are measured in FULL turns: OPT = 2 half-turns, OP2 = 4, OP3 = 6. At a 3-4 seat table a full turn is a full ROUND of the table — seatCount half-turns — carried the same way R08's opening Cycle carries it. Before this, both cooldown consumers compared against the raw tag number, which made OPT and OP2 indistinguishable at a seat's own cadence. ⚠ AND THE SAME LAW GOVERNS DURATIONS, ruled the same day: 'if it's just broad \'2 turns\' that means 2 full turns which consists of 4 × 1/2 turns. That should clear up a lot of confusion in the future too — make sure you nail this point in for the future when we make cards, you\'ll know exactly what I mean already.' A NUMBER OF TURNS ON A CARD IS ALWAYS FULL TURNS — Divine Justice and Phoenix Aura's '2 turns' is 4 half-turns, not 2. The only way to mean a half-turn is to name a boundary ('this turn', 'at the end of your turn'). Durations and cooldowns share ONE helper so they can never drift apart" },

  // ── THE EXILE WAVE (caP'n, 2026-09-06/07) ───────────────
  { code:"R110", cat:"Setup",    desc:"THE BATTLE LINE IS FOUR. Every seat pre-places FOUR Esclings, one per battle rift, where it placed three from Terror's first day until 2026-09-07. The fourth rift exists because the four Role Arts (R115) each live in a rift of their own. caP'n: 'starter decks we're going keep everything exactly the same, just give the player 4 esclings now and update the rules to match. the min max etc everything stays the same, just 4 in beginning instead of 3.' NOTHING ELSE MOVED — Grimoire minimum 35, Luxmoire 10-40, every per-name deck limit and every fare are untouched. ⚠ AND THE RIFT COUNT WAS ALREADY WRONG: RULEZ_V2 said 'eleven rifts' in five places while its own table listed twelve (1 Prophecy + 3 battle + 1 Necropolis + 1 Grimoire + 1 Luxmoire + 3 Tome + 2 Curio). With the fourth battle rift and the Exile rift (R112) the true count is FOURTEEN, and the book now says so" },
  { code:"R111", cat:"Exile",    desc:"EXILIATION — AN ART EVERY AENARCHIST KNOWS. Not a card: nothing to draw, nothing to build into a Book, nothing to attach. caP'n: 'At any point a player can do this, this is a level 3 Deviant U'hltima the Aenarchist knows' and 'it still requires nothing other than the payment.' THE LEVEL IS THE TIMING — a Level 3 art activates on your turn, on an adversary's turn, and from hand on an adversary's turn, and caP'n named the level for exactly that reason ('I have explained how level 3 arts behave'). THE FARE IS THE LEVEL OF THE CARD YOU EXILE, not of the art: L1 = 2, L2 = 3, L3 = 4, Khao or Anima, never split (R107). It does NOT consume your Turn's draw. There are FOUR Exiliations and you take exactly one; any of them spends the same OP2 clock — four half-turns, measured by R103's helper like every other cadence. ⚠ THE SECOND OPTION WAS NARROWED BY caP'n IN THE SAME SESSION and the narrowing is load-bearing: 'add 1 card from either your G or L' would have tutored an U'hlti or Affinity out of the Luxmoire straight through R106, so he changed it himself to 'search for 1 Being from G of same level or 1 Impera from L of same level.' R106 stands. 'Of the same level' always means the level of the card exiled" },
  { code:"R112", cat:"Exile",    desc:"THE EXILE RIFT. A new rift on every seat's half, sitting BESIDE the Necropolis — caP'n first said 'yeah new rift literally right above the Necropolis' and then placed it himself: 'we could probably just sit it to the right of the Necroplis.' The later word is the ruling; the board draws it to the right. The Necropolis is where the ruined WAIT; the Exile rift is where they are FORGOTTEN. A card here is out of the Solrend: not in a Book, not in the Necropolis, never Resurrected, never tutored, never folded back into a Grimoire by a Graveshift. The only door out is R113 and it opens once per card, ever. ⚠ IT IS A DESTINATION AND CONSERVATION READS IT AS ONE — trap 4.3 in the referee's file is a card removed with no destination write, and an Exiliation is exactly that shape, so the between-boards referee gets an Exile clause and the conservation check counts this rift as a pile like any other. A multiset, never a set" },
  { code:"R113", cat:"Exile",    desc:"THE RITUAL PROPER — BUYING ONE BACK, ONCE EVER. TWO NUMBERS THAT ARE NOT THE SAME NUMBER, and caP'n separated them himself: 'You need 10 Anima to even have access to that Ritual. Then once you have at least 10 Anima, no matter how much even 33, halve it if you wish to perform the Ritual proper... There is no Anima minimum to perform an Exilation.' So the floor of 10 is the Ritual's alone; Exiliation (R111) has none. The fare is HALF YOUR CURRENT ANIMA, rounded against you by R114 — at 33 you pay 17. The card goes to your HAND and it is only ever YOUR card: no seat may reach into an adversary's Exile rift at any price. The cadence is OP3 — six half-turns — and it is separate from R111's OP2 in both directions. The Anima is SELF-DIMMED, so R100 bars it from feeding any Prophecy ('this also doesn't count towards the blood prophecy or other cards etc because you're self dimming'), and R96's Till Law binds it as a voluntary payment: refused, never fatal. ⚠ AND THE MARK IS FOREVER, added by caP'n unprompted: 'cards brought from Exile no matter the level cannot be resurrected once they get placed again and ruined, they are sent to the Exile rift after being ruined and can no longer be retrieved, only once ever per card.' A returned card can never be Resurrected by ANY method — stricter than R86's Graveshift generation, which still leaves card effects a door — is routed to the Exile rift instead of the Necropolis when ruined, and can never be bought back a second time. Per COPY, tracked the way R85's tally is tracked, because the necroSeal IS the identity" },
  { code:"R114", cat:"Misc",     desc:"THE HOUSE ROUNDS AGAINST YOU. A standing law of the whole game, not a clause of one art. caP'n, asked only about halving Anima: 'always round up so its against the players favor, think of J'ynxi being the house bending rules to them hehe in every situation it always rounds against the player performing the action.' One helper, called by everything that rounds, so the law can never be half-applied. ⚠ IT WAS ALREADY TRUE ONCE AND UNNAMED: the Prophet's Toll (R97) halves an adversary's Anima ROUNDED UP, leaving the victim more and landing the rounding on the Prophet, who is the one acting. prophetsToll is left exactly as it is — correcting code to match a law it already satisfies is a balance change wearing a coherency costume" },
  { code:"R115", cat:"Role",     desc:"THE ROLE RIFTS — FOUR ARTS, ONE PER BATTLE RIFT. A Role does NOT live on a card and not one of the 88 beings gained a field. caP'n: 'there are specific rifts, you can place whatever being you want there and it gains that Roles Primera.' THE RIFT CARRIES THE ART; the being standing there wears it, and moving the being leaves the Art behind. THE RIFTS HAVE NAMES AND THE ORDER IS FIXED, left to right: BODY (the Tank Art, of Lyf) · SOL (the Terror Art, of Anima) · HEART (the Healer Art, of Lyt) · MIND (the Warden Art, of Khao). caP'n gave the names and the pairings on 2026-09-07 and retired the old word for the Sol rift in the same breath — 'let's not even call it DPS ever again that's the Terror role' — so it appears nowhere in Terror except inside the verbatim quotations that predate the ruling, which are left unedited because a tidied quotation stops being evidence. WHAT KIND OF THING IT IS: a LEVEL 3 DEVIANT PRIMERA — caP'n, 'the rifts are level 3 Deviant Primeras. That means they're level 3 and can be used whenever, but they still have their cooldowns associated if they have em' — and 'No new stamps, just cool world building terminology for their Art.' The Axis is still two words and the innate stamps are still eight; a Role Art neither consumes nor competes with a being's Ilk and School stamps. ⚠ R81 (one Primera per being per Turn) AND R82 (one use per word per Turn) DO NOT REACH IT: 'Deviant Primeras have no limit on use like the Exilation. Only their specific cooldown stops them from stacking.' The Warden's own per-seat clause (R119) is the single exception. THE THREE-TURN LOCK: nothing role-shaped works for the first 3 FULL Turns — six half-turns — and caP'n confirmed it reaches the passives too: 'it locks all roles effects even the warden and Tank.' So a Warden is an ordinary being for three full Turns" },
  { code:"R116", cat:"Role",     desc:"THE TERROR ART — THE SOL RIFT. 5 Khao or 5 Anima, and it buys two limbs at once. caP'n, in the words he used before he renamed the rift: 'Terror dps rift can ruin 1 free fractured being if it ruins one OR if its lyt attack exceeds the adversary's Lyf by at least one, ruin it instead of fracturing.' THE FREE RUIN: if this being ruins something, it may also ruin one Fractured being for free, reaching a being at 0 Lyf without spending a swing. OVERKILL: ordinary combat (R22-R29) drops a defender whose Lyf is at or below the attacker's Lyt to 0 and FRACTURES it, and only a second strike ruins. For this being, a Lyt STRICTLY GREATER by one or more RUINS where it stands. Equal is still a Fracture — 'exceeds ... by at least one' is a strict inequality and it is quoted in the code so nobody softens it to >= later. ⚠ NO COOLDOWN, AND THAT IS RULED, NOT OMITTED: caP'n gave tags to the other three and then ruled the general case, 'Deviant Primeras have no limit on use... Only their specific cooldown stops them from stacking.' The Khao pool is the only brake — two firings a Turn at the natural ceiling, three at the card ceiling. THIS IS THE NUMBER IN THE WAVE MOST LIKELY TO WANT A PLAYTEST and it is a DIAL: one tag in ROLE_CADENCE makes it once-per-Turn everywhere (Role Arts have one resolver, roleArts.js, since 2026-09-13)" },
  { code:"R117", cat:"Role",     desc:"THE HEALER ART. caP'n: 'Healer rift gives 1 free rez once per turn, does not incur Necropolis limitation(same costs)' — and, when asked which of Terror's two Resurrection brakes he meant, 'yes those were the limits I was actually referencing, the return tally is not spent.' BOTH. R89's one-paid-rite-per-Turn does not bite, so a seat may take its ordinary paid Resurrection AND this one. R85's per-copy tally — Beb 3 / Champion 2 / Lord 1, spent for the whole Solrend by every method that returns a being — IS NOT SPENT AT ALL. That is the expensive half and it is deliberate: while a Healer stands, one being can be returned without limit. It is NOT free of fare: it pays the ordinary Resurrection schedule for the level it returns (L1 = 2, L2 = 4, L3 = 4), caP'n's own correction — 'whoops yeah lets make it cost the price of a normal Resurrection.' What it buys is the clock and the tally, not the coin. OPT" },
  { code:"R118", cat:"Role",     desc:"THE TANK ART — THE BODY RIFT. caP'n, again pre-rename: 'Tank cannot be ruined by the dps role effect, passive and free. Opt: can reduce 1 being's Lyt attack's Lyt power by 1. Cost: 3 Khao or 1 Anima.' THE PASSIVE costs nothing, is never activated and cannot be answered because there is nothing to answer. It is narrow on purpose — it stops R116 and only R116; an ordinary Lyt Attack ruins a Tank exactly as it ruins anything else. THE DULLED SWING is an OPT (caP'n wrote the tag himself) and takes 1 off a Lyt Attack's Lyt for THAT ONE SWING: 'it lasts for only that one specific Lyt Attack. It can only hit 1 of your Adversary's beings per turn.' Not a duration and not a debuff on the being — a single swing dulled as it lands, against one adversary being per Turn. ⚠ THE FARE IS ASYMMETRIC ON PURPOSE — 3 Khao OR 1 Anima. It is the second such square in Terror after the Deviant L3/no-traits fare (5 Khao or 4 Anima, R101) and it leans the same way, Anima the cheaper coin. Do not tidy it. R107 still bars mixing" },
  { code:"R119", cat:"Role",     desc:"THE WARDEN ART. THE SHIELD IS THE TANK'S, NOT THE WARDEN'S. caP'n: 'Warden: tank must be ruined and either left in a fallen state, or only esclings left on board to attack the Warden with Lyt attacks. All other ways are always viable.' A Warden cannot be Lyt-attacked while its seat's Tank still stands; two doors open it — the Tank ruined AND left Fallen, or only Esclings left on that side. Everything that is not a swing — a Tome, an Impera, an U'hlti, a Curio, a stamp, an Affinity — reaches a Warden on any Turn. The shield stops the SWING and only the swing, the same shape as the wall of beings (R31), deliberately. ⚠ A WARDEN IS NOT A WALL: caP'n, 'You dont need to ruin the warden or leave it in a fallen state to dim Anima, only the other 3', and on how to say it, 'pretend as though the Warden isn't there is a good way to explain that.' For the question 'may I swing at your Anima', the Warden is invisible — three standing beings hold the wall and the fourth does not. That is what the Warden pays for being unreachable. TWO OPTIONS, both OP2 and both 5 Khao or 5 Anima, each paying a card into the Necropolis on top of the fare: hand -> Necropolis nullifies 1 card effect, Grimoire -> Necropolis nullifies 1 Lyt Attack. ONE WARDEN ART PER AENARCHIST PER TURN across both options — 'Each Aenarchist can only use 1 of their Warden Primeras per turn' — the one place a per-seat law reaches a Deviant Primera. THE BOUNTY: ruining a Warden of ANY level pays the ATTACKER 3 Anima or +1 Max Khao, attacker's choice. caP'n widened this from Level 1 himself: 'let's just make it a reward for ruining any level Warden, doesn't matter same reward at every level'" },
  { code:"R121", cat:"Role",     desc:"THE MIND'S SILENCE — THE PRICE OF THE SHIELD. caP'n, 2026-09-07, flagging it himself as the piece he had left out: 'very important! I forgot to add this as well! Whatever being is in the Warden Rift cannot perform any arts, attacks, etc of any kind at alllll except for the Warden Rift Primera, if the other 3 rifts are occupied... it behaves like a normal being if only 2 of the Trio Rifts are occupied, then when all 3 ... are occupied with Ascendants not Esclings, the Mind cannot perform any attacks of any kind etc except the Warden Primera.' So R119 makes the Mind unswingable while the Body stands, and THIS is what that costs: with Body, Sol and Heart all holding ESCENDANTS, the being in the Mind rift may do NOTHING — no Lyt Attack, no Primera stamp of its own, no Impera or U'hlti or Affinity power, no Exiliation declared by it — except the Warden Art itself. Let one of the three fall and the Mind wakes. ⚠ THE TRIO IS COUNTED IN ESCENDANTS: a bare Escling does not occupy a rift for this count, in caP'n's own words 'with Ascendants not Esclings'. ⚠ AND GROWTH IS NOT SILENCED — the line is between what the BEING performs and what the AENARCHIST does to it. caP'n: 'no you can keep ascending etc and attaching cards to it etc like your ace in the hole if you want to play that way, you can beef up your warden and when your scapegoat is ruined its unleashed with all these Tomes/support cards etc.' Transcend, Ascend, Train, U'hlti, Affinity, Heretical Training and Resurrection into that rift are all legal every Turn. It is a place to build something terrible and wait. ⚠ ONE LEG IS V'AEL'S: a FALLEN Escendant still occupies its rift for the trio count. Fallen is a half-turn state of a being that is standing there, not an absence — and reading it the other way would wake a seat's Mind precisely because its own Body had just been knocked down, which rewards being beaten" },
  { code:"R120", cat:"Exile",    desc:"AN ART IS NOT A CARD. caP'n, on whether a Nihil or an Omnia may answer an Exiliation: 'no you can definitely stop it if a card is broad enough like stop something from activating, but if it outright mentions a card, then no, because they are not cards, these are Arts the Aenarchist knows that's why I was so specific.' WHO may answer is unchanged (R-Chain: Nihil and Omnia only, Level 3 from hand, Level 2+ from the field) and the reason is lore — 'only those 2 can because in Lore they're would be that powerful.' WHAT they may answer is what moved: a card that nullifies an activation or an effect reaches an Art; a card that nullifies a CARD does not. ⚠ IT IS AN ALLOW-LIST AND IT DEFAULTS TO PROTECTED. The engine does not read printed prose to decide — that is trap 4.8, where printed text and branch disagreed on five cards, and trap 4.4, where a regex-over-source check could not fail. A card DECLARES `answerScope: \"broad\"`; anything undeclared is narrow and cannot touch an Art. The failure mode is therefore an Art that survives an answer it should have taken, which one data row fixes, rather than an Art silently voided by a card that never claimed it could. ⚠ THE DATA PASS IS OWED AND IS DECLARED, NOT HIDDEN: no card carries answerScope yet, so today NOTHING answers an Art. ROADMAP, caP'n's words: 'there may be a school who's whole purpose is stopping Exilations and Aenarchist focused Arts'" },

  
  // ── MISC ────────────────────────────────────────────────
  { code:"R74", cat:"Misc",     desc:"Prophecies/Esclings unaffected by all cards" },
  { code:"R75", cat:"Misc",     desc:"Curios behave like Tomes with their own two Rifts — same activation shape, same tags, own lane. BUT A CARD THAT NAMES *TOMES* DOES NOT REACH A CURIO, AND A CARD THAT NAMES *CURIOS* DOES NOT REACH A TOME. The shapes are alike; the words are not. RULEZ_V2 ch.14: 'A card that affects Tomes does not affect Curios, and the reverse.' Enforced 2026-09-14 in RulesEnforcer.validateTargeting, on an effectScope the CALL SITE declares — never read off printed prose, which is trap 4.8. AND WHAT THE BOARD ACTUALLY DOES TODAY IS NARROWER THAN THIS ROW: a Curio is a once-per-duel automatic gift (activateCuriosForPlayer / activateNpcCurios, both guarded on curiosActivated, both reading the ONE table rules.CURIO_GIFTS since 2026-09-15 — it was two hand-typed copies before, and tools/unify0915.selftest.mjs re-derives every number from the printed card face), pays no fare and reads no cadence, while the book says a Curio activates, pays and carries OPT/OP2/OP3/ATV. The board is the evidence; gateCurioActivation is the door for the day one can be pressed, and tools/gateReach.selftest.mjs fails if that day arrives without the fare and the cadence being asserted." },
  { code:"R76", cat:"Misc",     desc:"Game-actions don't trigger Anima/Khao proc effects" },

  // ── SPECIFIC BUG REGRESSION CHECKS ─────────────────────
  // These are targeted checks for known bugs; they fire when
  // the simulator observes game states that should be impossible.
  { code:"B01", cat:"Bug",      desc:"Tome activated multiple times in one turn without OPT/OP2/OP3/ATV tag (regression: Devil's Advocate loop)" },
  { code:"B02", cat:"Bug",      desc:"Non-persistent Tome remained on field after activation (regression: missing ruin after use)" },
  { code:"B03", cat:"Bug",      desc:"Anima hit while non-Fallen Escendant on field (regression: bypass protection violated)" },
];

// ════════════════════════════════════════════════════════════════════
// 📜 THE PROVENANCE BLOCK — WHO ACTUALLY MADE EACH RULE
// ════════════════════════════════════════════════════════════════════
// caP'n asked, 2026-08-25, for the list of every rule V'ael added
// without his explicit permission. This is that list, written into the
// catalogue itself so it can never be lost again.
//
// ════════════════════════════════════════════════════════════════════
// ⚖ THE STANDING DISCIPLINE — MARK IT WHEN YOU WRITE IT
// ════════════════════════════════════════════════════════════════════
// caP'n, 2026-08-26, verbatim:
//
//   *"with all games let me know when you make changes without my
//   knowledge but 1000000% especially if it's Terror because that game
//   is suuuuper far along, changed rules now could be very detrimental
//   to its health (but very easily reversible no worries if that ever
//   does happen ok partner)"*
//
// So this block is not a one-off audit that happened on 2026-08-25. It
// is a STANDING OBLIGATION, and it binds whoever writes the next rule:
//
//   1. ANY rule added without caP'n's say-so is marked HERE, with
//      verdict "b", AT THE MOMENT IT IS WRITTEN — in the same edit, not
//      in a later tidy-up. A rule that ships unmarked is the failure
//      this discipline exists to prevent; noticing it afterwards is
//      already too late, because by then it has been played.
//   2. IT IS SURFACED TO HIM, not left for somebody to find. The point
//      of the marking is that it can be READ OUT — rulesInventedWithoutPermission()
//      is the one function that answers "what have I added that you have
//      not blessed", and its answer belongs in the session report the
//      day the rule lands.
//   3. THE MARK IS NEVER LAUNDERED. When he approves a rule it does not
//      become somebody else's idea: the verdict stays "b" — that is the
//      HISTORY, and history does not change — and an `approved` stamp is
//      added beside it with his date and his words. R99 is the model
//      (invented, then ratified, filed with its history intact).
//   4. THE FUNCTION IS THE TRUTH, NOT THE PROSE. Anything pending is
//      pending in `rulesInventedWithoutPermission()`. If a rule is
//      unblessed and that list does not name it, this whole block is
//      decoration. It is honest as of 2026-08-26 and it is asserted in
//      tools/openingTurn.selftest.mjs section 14.
//
// He also said the damage is *"very easily reversible"*. That is only
// true while every invented rule is still NAMED — an unmarked rule is
// not reversible, because nobody knows it is there to reverse.
//
// ⚠ FIRST, THE HONEST CAVEAT ABOUT THE EVIDENCE. This repository has
// NO GIT HISTORY — `git log` answers "your current branch 'master' does
// not have any commits yet", and there are no tags, stashes or other
// refs. So none of the below is derived from a diff. It is derived from
// two things that CAN be checked: the banners the authors left in these
// files, and the SHIPPING BEHAVIOUR of the engine, which either did or
// did not already do the thing the rule row claims. Where those two
// disagree, the behaviour wins and the disagreement is written down.
//
// THREE VERDICTS:
//   (a) ALREADY ENFORCED — the engine was doing this before anybody
//       wrote it in the catalogue. Naming it changed nothing. These
//       need no permission; they were already caP'n's game.
//   (b) V'AEL'S INVENTION — a rule that did not exist in the engine
//       until it was written here. THESE ARE THE ONES TO REVIEW.
//   (c) caP'N'S — a designer ruling, usually quoted verbatim in a
//       banner above the code that implements it.
//
// AND ONE STAMP THAT SITS BESIDE THE VERDICT, NEVER ON TOP OF IT:
//   `approved: { by, on, note }` — caP'n has since blessed this rule.
//   The verdict stays whatever it always was, so the record still says
//   who wrote it; the stamp says who signed it off and when. A row with
//   verdict "b" AND an `approved` stamp is a rule V'ael invented and
//   caP'n has now kept. It leaves the review list and nothing else
//   about it changes.
//
export const RULE_PROVENANCE = {
  // ── (c) caP'n'S OWN RULINGS OF 2026-09-14 ───────────────────────
  // Four questions asked at the end of the coherency sweep and answered
  // the same day, each quoted verbatim in its catalogue row. Nothing here
  // is a reading: every one is his sentence, and three of the four were
  // asked precisely BECAUSE the printed card, the book and the board
  // disagreed and guessing would have been a balance change.
  R128: { by: "caP'n", verdict: "c", note:
    "WHEN AN IMPERA'S PRINTED NUMBER IS PAID — 'we are going to make the user pay to use an Impera if it has OPT, OP3, etc type acronyms. If it doesn't have them, then you only pay once, if it does, you must pay to activate again.' Asked because RULEZ_V2 ch.10 charged the Training, the board charged nothing for a Natural Training, and the two readings made an untagged Impera (Good Morning, effectTag null) FREE FOREVER. His answer restored the book's own line and settled the tagged/untagged split. The largest single number in the game and it was not being charged." },
  R129: { by: "caP'n", verdict: "c", note:
    "THE TWO FED TREASURES — quoted in full in the row, across two messages: the whole lifecycle on 2026-09-14, then the recharge when asked whether a paid-out Treasure may be filled again. Asked because the board fed one of them the wrong currency, voided both on payout, and treated a per-Turn ALLOWANCE as one feed per Turn — three readings of one printed card. The generalisation is his too: 'most if not all treasures behave this way', which is what makes a Treasure's printed Lyf durability rather than a fare." },
  R130: { by: "caP'n", verdict: "c", note:
    "A FULFILLED PROPHECY CANNOT BE ANSWERED, AND IT INTERRUPTS — two answers the same day: 'yes once its fulfilled and the conditions met etc you can activate immediately and interrupt the entire duel', then 'No, nothing can interrupt them; Prophecies are auto wins if fulfilled.' Asked because the human's manual door refused to open on an adversary's Turn while the automatic door fired at the boundary, so the same board won now for one seat and maybe for the other. The second half closed the follow-up question rather than leaving a response window to be discovered." },
  R131: { by: "caP'n", verdict: "c", note:
    "A CARD IN NO STARTER DECK IS NOT A DEAD CARD — 'no cards like those are still purchasable and attainable as rewards or packs etc.' Asked because a fix landed on the Necklace of Congealed Death and the balancer could not exercise it: no starter deck carries the card, so no brain can play it. This is THE RARITY DOCTRINE (2026-08-25) applied to the instruments, and it is the reason 'the balancer was green' may never stand in for 'the card works'." },

  // ── (d) caP'n'S GAME, V'AEL'S ROW ───────────────────────────────
  // Neither of these is an invention and neither is a quote. The
  // BEHAVIOUR is caP'n's — it ships, he built it, and nothing about it
  // changed on 2026-09-14. What is V'ael's is the SENTENCE: until that
  // date the catalogue had no row for either, so nobody could check
  // whether the game's own clock and its own seat shapes were being
  // enforced the way he meant. They are therefore verdict "c" — his
  // rule — with the authorship of the wording stated outright, and they
  // were put to him as questions in the same session they were written.
  // ⚠ DO NOT re-file these as (b) to get them onto the review list: the
  // review list is for rules V'ael INVENTED, and inflating it would cost
  // the one number in this file that tells caP'n something true.
  R126: { by: "caP'n", verdict: "c", note:
    "THE 45-SECOND HALF-TURN CLOCK. PLAYER_TIMER = 45 in Shatterfield.js, force-ending the acting seat's Turn in every mode since well before this row existed — caP'n's own value, his own behaviour, and the banner above it records him raising it from 35. WHAT IS V'AEL'S: the row, written 2026-09-14, and the four HOLD conditions in it, which were read off the shipping code (the response-window guard, strikeLanguage.holdActive, the PvP declaration window, and the controller==='ai' skip) rather than stated by him. He had never been shown the clock written as a rule; RULEZ_V2 ch.19 called itself The Two Clocks while three shipped. Flagged to him the same day. Nothing about the behaviour was touched." },
  // Six answers on 2026-09-15, closing the questions left standing at
  // the end of the coherency sweep. Three became rules (R132-R134); two
  // ratified something that LOOKED like a defect and must never be
  // "fixed" (the clock-call band, and the lore-page placeholder \u2014 see
  // TERROR_REFEREE.md 2.23); one, Starter Master, confirmed a number
  // that was already right. Recording a ratification is worth as much as
  // recording a change: an unratified intentional choice is the thing
  // the next sweep deletes.
  R136: { by: "caP'n", verdict: "c", note:
    "ANYONE MAY ANSWER \u2014 'anyone can answer it, even in a free for all, that adds a lot of drama hehe let's make sure to make that canon.' Asked at the close of the 2026-09-15 unification pass because Chapter 15 never said who may answer beyond 1v1 and the board had quietly decided: only the targeted seat. His answer is the opposite, and it is now the board. ONE LEG IS V'AEL'S: reading 'anyone' as every living ADVERSARY, so a 2v2 teammate does not answer their own partner. Flagged at matchSeats.answerersFor." },
  R135: { by: "caP'n", verdict: "c", note:
    "A DURATION'S CLOCK STARTS WHERE IT IS STAMPED \u2014 'if a Tome or something is activated on an opponent\u2019s turn, the 1 full turn (or more) start at that moment, that way you dont get a free 1/2 turn skip, you still have to wait 2 half turns no matter, just depends when it starts.' NOT asked: he volunteered it, unprompted, the moment R132 landed \u2014 because R132's own row had just over-read his earlier ruling into 'every clock pays on a Turn boundary', which would have shortened every duration stamped mid-half-turn. The board already behaved correctly; what was wrong was the written rule, in V'ael's wording. Filed as his because the sentence is his and it is now the limit on R132." },
  R132: { by: "caP'n", verdict: "c", note:
    "WHEN THE MAX-KHAO CLIMB LANDS \u2014 'after 1 full turn of not using Khao, so if you didn\u2019t use any turn 1, then the beginning of turn 2 you get +1 to your Max Khao hopefully that also clarifies other rulings as far as how the 1 full turn etc works.' Asked because the renderer and the three brains held four copies of one rule and granted at two different moments, which had left every brain a full Turn behind the player on Max Khao for the entire duel \u2014 in the player\u2019s favour, and chosen by nobody. His closing clause is the part that generalises: it fixes the boundary for every 'for 1 full turn' condition in the catalogue, not just this one." },
  R133: { by: "caP'n", verdict: "c", note:
    "WHAT THE CLOCK BURIES \u2014 'the newest card, that\u2019s 4xS right there.' Asked because R10 caps the hand and has never said which card goes, and the automatic path had invented three tie-breaks (cheapest Tome, then a duplicate, then the last card) that a player watching a clock run out cannot predict. He chose the simplest rule, by his own 4xS filter, and it is also the more deterministic one." },
  R134: { by: "caP'n", verdict: "c", note:
    "V'AEL'S VEIL COSTS NOTHING \u2014 'completely free :3 purely narrative.' Asked because vaelsTools.js shipped an Aenergy ledger for veiled steps under a banner saying the price did not bite the duel yet and that making it real was his to say when. He closed it the other way. The finding that came with it: the ledger had no callers at all, so the banner's claim that the debt was 'counted, shown in the chrome, and persisted' was false on all three counts." },

  R127: { by: "caP'n", verdict: "c", note:
    "THE SEAT SHAPES. caP'n reversed 'Solrends are always 1v1' on 2026-08-22 and the shapes have shipped since — V'yen's Stall offers 3-way and 4-way free-for-all, 2v2, 2v2 ranked, Story co-op and the Gauntlet Duo. The shared-Anima-and-nothing-else reading is his too, enforced through matchSeats.getEffectiveAnima. WHAT IS V'AEL'S: the row, written 2026-09-14, because the catalogue contained no occurrence of '2v2', 'team', 'free-for-all' or 'co-op' at all — so every rule measured in full Turns was being generalised to a 3-4 seat lap with nothing written down to check it against. Flagged to him the same day." },

  // ── (b) V'AEL'S INVENTIONS — the review list ────────────────────
  R93: { by: "V'ael", verdict: "b",
    approved: { by: "caP'n", on: "2026-09-13", note:
      "*\"yeah that's fine, you said the video game code etc literally starts to break down so gorgeous find right there partner yes, need that rule.\"* Asked outright whether he blessed the 150-half-turn bound; he did, on the reason it was built for — an unbounded duel is a simulator that never terminates and an engine that degrades." },
    note:
    "THE SECOND HAND. caP'n gave R87 — twenty minutes of live play — and nothing else. 150 half-turns is a number V'ael chose so a HEADLESS batch could reach a clock at all (R87 can never elapse in a simulator). It is a real rule with a real effect on the shipping game: TURN_CLOCK_PVP (500) exists precisely because 150 would otherwise ALWAYS ring before R87 in a human duel. Nothing in the engine bounded a duel in turns before this. ⚠ RATIFIED by caP'n 2026-09-13 — the verdict stays (b) because that is the history and the history does not change; the `approved` stamp is what takes it off the review list. THE REVIEW LIST IS NOW EMPTY." },
  R96: { by: "V'ael", verdict: "b",
    approved: { by: "caP'n", on: "2026-08-26", note:
      "*\"It is a payment and yes you cannot end yourself that way.\"* Asked and answered. The same ruling WIDENED it: R96 now binds every card effect that dims its own controller's Anima, not just the tills — see R100 for the second half of the same sentence." },
    note:
    "THE TILL LAW — 'a voluntary payment may never reduce your own Anima to 0'. Self-flagged in this file as V'ael's ruling, awaiting veto. Before it, every spend read `current < amount`, which let an EXACT payment through. Killing the duel on an exact payment was the BUG; forbidding the payment is the RULING, and the ruling was V'ael's. The Graveshift exemption is a second V'ael judgement layered on the first. ⚠ RATIFIED by caP'n 2026-08-26 — the verdict stays (b) because that is the history and the history does not change; the `approved` stamp is what takes it off the review list." },
  R100: { by: "caP'n", verdict: "c", note:
    "SELF-INFLICTED ANIMA LOSS NEVER FEEDS A PROPHECY — 2026-08-26, quoted verbatim above ANIMA_LOSS_KINDS in this file. caP'n volunteered it unprompted ('I don't know if you asked later or if I ever mentioned it'), which makes it his rule outright. It fixed a live hole: Bloody Hell reads totalAnimaLost, one counter took every source, and a seat could farm its own Anima to 45 and win." },
  R101: { by: "caP'n", verdict: "c", note:
    "THE DEVIANT FARE — Rulez v2 ch.15, confirmed twice in its own CAP'N CHECK blocks (once for Ascending, once for Training) and pointed at by caP'n on 2026-08-26 with 'see attached rulez v2'. The 5-Khao-or-4-Anima asymmetry at L3/0-traits is HIS, quoted in the DEVIANT_FARE banner. ⚠ ONE LEG IS V'AEL'S AND IS FLAGGED: Rulez v2 does not say which LEVEL an Ascend is charged at, and three implementations disagreed. caP'n's instruction was to read it as Training reads it — the destination level — which is a reading, not a quote." },
  R102: { by: "caP'n", verdict: "c", note:
    "ONE ASCEND PER TURN TOTAL / one Deviant Training / unlimited Deviant Transcends — Rulez v2 ch.4 Main Phase, restated in the ch.19 glossary. Verified against the code rather than imposed on it: R18/R66 already enforced the shared Ascend budget on both sides, and the Transcend loops were already unbounded." },
  R104: { by: "caP'n", verdict: "c", note:
    "THREE U'HLTIS PER LUXMOIRE — 2026-08-26, quoted verbatim above LUXMOIRE_UHLTI_MAX in this file. ⚠ HIS OWN HEDGE IS PART OF THE RECORD: \"which should be 3 U'hltis if I'm not mistaken\". Built as 3, flagged as unconfirmed. Every shipped starter deck carries exactly ONE U'hlti, so nothing anyone owns is affected either way." },
  R105: { by: "caP'n", verdict: "c", note:
    "THE ACTIVATION FARE IS PAID EVERY TIME — 2026-09-13, quoted verbatim above activationFare in this file. ⚠ THIS ENTRY REPLACED ITS OWN OPPOSITE. From 2026-08-26 it read 'PAID ONCE', on caP'n's answer to a bug report about double-charging; asked the question directly a fortnight later he ruled the other way, and the persistent tags are priced on it: 'thats the cost of it being able to stay on the field indefinitely.' Carries two clauses minted the same day — a card printing BOTH numbers pays both (all twelve U'hltis do), and Lyf printed on a Treasure is durability, never a fare." },
  R106: { by: "caP'n", verdict: "c", note:
    "THE LUXMOIRE SEARCH FINDS IMPERA ONLY — 2026-08-26, quoted verbatim above LUX_SEARCHABLE_TYPES in this file. The draw-consuming half was already canon (July 4 2026, and NPC.js's banner has recorded it since); the Impera-only half is NEW LAW and closes a real hole: the player's picker offered the whole Luxmoire, U'hltis and Affinities included, and NPC.js could tutor Supreme pieces out of it through extraWantIds." },
  R107: { by: "caP'n", verdict: "c", note:
    "NO MIXING KHAO AND ANIMA — 2026-08-26, quoted verbatim above FARE_POOLS in this file, and he named the culprit himself: \"that was something you must've added hehe\". ⚠ THIS IS A LIVE BALANCE CHANGE AND IT IS THE ONLY ONE IN THIS WAVE. R79's Primera fare was mixable in rules.js, in Shatterfield.payPrimeraCost and in NPC.npcPayPrimeraFare — a till written specifically because the shared either/or helper 'would decline a fare the rules say is payable'. It is single-pool now, so a stamp near the bottom of both pools is no longer payable at all." },
  R108: { by: "caP'n", verdict: "c", note:
    "THE PHASE GATE — 2026-08-26, quoted verbatim above PHASE in this file. ⚠ THE MOST INVASIVE CHANGE IN THE WAVE: the engine had NO phase machine at all, so 'attack, then Transcend and set three Tomes' was legal on every screen and in the brain. ONE LEG IS V'AEL'S AND IS FLAGGED: caP'n named what is barred ('you cannot play any more cards') and what survives (Lyt Attacks, Souls, and stamps by R84's logic); mapping that onto THIS engine's specific action types — and choosing an ALLOW-list so a new action type is barred by default — is V'ael's reading of it. The Draw-phase actions (DRAW/SEARCH/MULLIGAN) are barred in Attack as belt-and-braces, not because the ruling names them." },
  R109: { by: "caP'n", verdict: "c", note:
    "THE GRIMOIRE SEARCH — 2026-08-26. It previously had NO rule of its own: R88 named its fare and the provenance block justified the whole thing as 'the engine was already doing it', which is a description, not a law. caP'n ruled it in as design — 'it's pivotal to the game… but it will of course cost you' — so it has a row that says what it is FOR and what it costs." },
  R103: { by: "caP'n", verdict: "c", note:
    "A FULL TURN IS TWO HALF-TURNS — 2026-08-26, quoted verbatim above COOLDOWN_FULL_TURNS in this file, defining the Rulez v2 ch.18 cadence tags. ⚠ ONE LEG IS V'AEL'S AND IS FLAGGED: caP'n's sentence describes 1v1 ('the user ... then the turn of their opponent'). Reading 'a full turn' at a 3-4 seat table as a full ROUND of the table — seatCount half-turns, the same unit R08's opening Cycle uses — is V'ael's generalisation and nothing in the ruling states it." },
  R94: { by: "V'ael", verdict: "b",
    approved: { by: "caP'n", on: "2026-08-26", note:
      "*\"let's keep this rule and very 4xS using the 5/4/3.\"* And for the record, on how it came to be missing in the first place: *\"a common sense rule that got lost or forgotten.\"* The 5/4/3 per-name limit for Level 1/2/3 Impera STANDS." },
    note:
    "IMPERA DECK LIMITS. Presented as a coherency row but it is NOT: RulesEnforcer's own banner records that 'until this line existed, every Impera in the game had NO deck limit at all', because getCardType returned null for the type. So this rule CHANGED WHAT DECKS ARE LEGAL. It mirrors R53-R55 exactly and is defensible, but it is a new restriction, not a description of one. ⚠ APPROVED by caP'n 2026-08-26 — the verdict stays (b) because that is the history and the history does not change; the `approved` stamp is what takes it off the review list." },
  R92: { by: "mixed",  verdict: "b",
    approved: { by: "caP'n", on: "2026-08-26", note:
      "THE CHAIN HAS NO LENGTH LIMIT, ruled outright: *\"as long as they want that's the point — you said it, players will run out of cards but mainly Khao in like 2 or 3 chains max, it will never get to 12 and if it does that'd be cool xD but like impossible to even do 5 unless both players sat there trying to make it happen over and over using super low cost cards\"*. The invented half was the CAP, and the ruling deletes it as a rule: the row no longer names a number and CHAIN_DEPTH_CAP is a runaway guard raised to 64 so it can never bind. What is left in the row is caP'n's own alternation canon." },
    note:
    "THE CHAIN. Two halves with different parents. 'A response chain resolves last-declared-first, Nihil answers a declaration, Omnia answers an answer' is caP'n's canon and ChainLaw.js implemented it long before this row. THE 12-LINK CAP IS NOT A RULE and ChainLaw.js says so in as many words: 'Runaway guard, NOT a rule... it is a DIAL: raise it freely.' Writing it into the catalogue promoted a documented non-rule into law, and rules.js and ChainLaw.js contradicted each other in print. The row was corrected 2026-08-25 (the cap struck, the alternation kept) and caP'n settled the underlying question on 2026-08-26 — see the stamp above." },

  // ── (a) ALREADY ENFORCED — naming only ──────────────────────────
  R88: { by: "engine", verdict: "a", note:
    "SEARCH FARES. ⚠ THE FARE was already enforced; THE SEARCH ITSELF IS NOW A DESIGNED RULE and has its own row — see R109, ruled by caP'n on 2026-08-26. This note used to be the only justification either search had, and 'the engine was already doing it' is not a reason a rule exists. " +
    " Shatterfield's handleGrimoireSearch has always charged 2 for a Level 1 and 4 above it, and the Luxmoire picker has always charged 5. ⚠ THE FIRST CUT OF THIS ROW WAS WRONG AND IT WAS V'AEL'S ERROR: it wrote a FLAT 4 for the Grimoire, which would have quietly doubled a fare players already pay. Corrected to the by-level table, which is what shipped. R08 now suspends the fare entirely on the opening Cycle (caP'n, 2026-08-25)." },
  R89: { by: "engine", verdict: "a", note:
    "ONE PAID RESURRECTION PER TURN. The `resurrectedThisTurn` clock predates the row. ⚠ THE FIRST CUT SAID 'regardless of method' AND WAS V'AEL'S ERROR: the shipping board has always let the Time Primera ride alongside the paid rite. Corrected to paid-rite-only." },
  R90: { by: "mixed",  verdict: "a", note:
    "THE MULLIGAN. One-per-duel was already enforced on both seats (Shatterfield.handleMulligan, NPC.maybeNpcMulligan, MULLIGAN_LIMIT). The row's original 'before the first Turn' was V'ael's paraphrase of some React state. caP'n's 2026-08-25 ruling REPLACED it with the real law — opening Cycle only, and only by a seat that has not yet drawn — so the clauses that now stand are (c)." },
  R91: { by: "engine", verdict: "a", note:
    "NO NATURAL KHAO ON THE OPENING TURN. All three NPC brains gate the maxKhao climb on `turn >= 2` and always have; FIRST_KHAO_TURN names that number instead of repeating it. No behaviour changed." },
  R95: { by: "engine", verdict: "a", note:
    "MODE OVERRIDES ARE RULES TOO. Pure description: bossPhases.js suspends R33 for the boss seat and refreshes to MAX_ANIMA, EndlessDepths and the Gauntlet carry Anima between bouts. Every clause was already shipping code." },

  // ── (c) caP'N'S OWN RULINGS ─────────────────────────────────────
  // ── THE EXILE WAVE, 2026-09-06/07 — ALL caP'n's ─────────────────
  // The largest single wave Terror has taken since it went digital, and
  // every rule in it is his, given in one long message and then sharpened
  // across two rounds of questions before a single file was touched. What
  // follows the (c) verdicts is the honest part: FOUR LEGS OF THIS WAVE
  // ARE V'AEL'S READINGS, and each is named here and in the code.
  R110: { by: "caP'n", verdict: "c", note:
    "THE BATTLE LINE IS FOUR. 2026-09-07, verbatim: 'starter decks we're going keep everything exactly the same, just give the player 4 esclings now and update the rules to match. the min max etc everything stays the same, just 4 in beginning instead of 3.' ⚠ THE MOST INVASIVE SINGLE NUMBER IN THE GAME'S HISTORY: ESCLING_SLOTS is read by deck legality, board-shape validation, Tome wielder bounds, the Primera ledger and the Debugger's own simulator, and NPC.js additionally hardcoded [0,1,2] in one place, which is a bug this wave exposed rather than caused. ⚠ THE RIFT-COUNT CORRECTION RIDING WITH IT IS V'AEL'S FINDING, NOT caP'n's RULING: RULEZ_V2 said 'eleven rifts' in five places while its own table listed twelve, and had since the book was written. Nobody ruled eleven; it was arithmetic nobody did. Corrected to fourteen with the wave rather than carried forward." },
  R111: { by: "caP'n", verdict: "c", note:
    "EXILIATION. 2026-09-06, quoted verbatim above EXILE_FARE in this file, with the fare schedule and the no-Anima-floor clarified 2026-09-07. ⚠ ONE LEG IS V'AEL'S AND IS FLAGGED IN THE CODE: whether the hand_search option is barred on the opening Cycle. caP'n ruled its fare and ruled that it does not eat the draw; he said nothing about R08. Reading an Art as riding R08's existing card-effect search exemption — because an Art is the Aenarchist's printed job the way a card effect is the card's — is V'ael's, and EXILIATION_SEARCH_BY is the one line that reverses it. ⚠ A SECOND, SMALLER READING: 'once per 2 turns' is spelled as the OP2 tag so it is measured by R103's helper. caP'n did not say OP2 for Exiliation, he said it for the Ritual; the tags agree with his words at a two-seat table and generalise them at a four-seat one." },
  R112: { by: "caP'n", verdict: "c", note:
    "THE EXILE RIFT. 2026-09-07: 'yeah new rift literally right above the Necropolis.' Its position is his; its being a conserved destination rather than a hole cards fall through is engineering, and is the direct lesson of trap 4.3 — one illegal transition in 400 duels, a card spliced out of a hand with no destination write, invisible because a set cannot see one copy of five evaporate." },
  R113: { by: "caP'n", verdict: "c", note:
    "THE RITUAL PROPER. 2026-09-06 for the half-Anima fare and the floor of 10; 2026-09-07 for separating the two ('You need 10 Anima to even have access to that Ritual... There is no Anima minimum to perform an Exilation'), for the destination and ownership ('Card goes to hand yes; only ever your cards'), for the OP3 tag ('make sure it is OP3 I don't think I put that anywhere hehe'), and for the self-dim clause ('this also doesn't count towards the blood prophecy or other cards etc because you're self dimming'). ⚠ THE ONCE-EVER MARK WAS VOLUNTEERED MID-ANSWER AND IS ENTIRELY HIS: 'also new limitations I just thought of, cards brought from Exile no matter the level cannot be resurrected once they get placed again and ruined, they are sent to the Exile rift after being ruined and can no longer be retrieved, only once ever per card.' Tracking it per COPY rather than per name is not a reading — R85 already settled that a copy is the unit and necroSeal.js already carries the identity." },
  R114: { by: "caP'n", verdict: "c", note:
    "THE HOUSE ROUNDS AGAINST YOU. 2026-09-07, and he was asked about ONE number and answered about the whole game: 'in every situation it always rounds against the player performing the action.' Quoted verbatim above roundAgainstActor. ⚠ NOTHING SHIPPING CHANGED BEHAVIOUR: the only rounding in the engine was the Prophet's Toll's halve-rounded-up, which already lands the rounding on the acting Prophet and therefore already obeyed the law. prophetsToll was deliberately left untouched. This row is a law given a name and a helper, so the NEXT thing that rounds cannot round the other way." },
  R115: { by: "caP'n", verdict: "c", note:
    "THE ROLE RIFTS. 2026-09-07, quoted verbatim above ROLES in this file, including the correction that saved a very large wasted pass: the first reading of the 2026-09-06 message had roles as a third stamp printed on all 88 beings, and caP'n rejected it — 'no its not based on Esclings... there are specific rifts, you can place whatever being you want there and it gains that Roles Primera.' Not one card row was touched. ⚠ ONE LEG IS V'AEL'S AND IS FLAGGED IN THE CODE: an Escling standing in a Role rift carries no Art. caP'n did not rule it; Rulez v2 ch.7 and ch.11 already say Esclings bear no Primera stamps, and caP'n calls a Role Art a Primera, so the standing law answers it. It also has to be this way — an Escling cannot be targeted, ruined or Lyt-attacked, so a Warden Escling would be permanently untouchable and permanently unruinable, making the R119 bounty unclaimable and the Tank-then-Warden sequence meaningless. riftArtIsAwake is the one function that reverses it." },
  R116: { by: "caP'n", verdict: "c", note:
    "THE TERROR ART. 2026-09-06, quoted verbatim. The strict inequality is his word, 'exceeds ... by at least one', and is written as >= 1 on the DIFFERENCE rather than as a comparison so it cannot be softened into 'Lyt >= Lyf' by a later tidy-up — that softening would collapse the Art into ordinary combat. ⚠ THE ABSENT COOLDOWN IS A RULING, NOT A GAP: he tagged the other three and then ruled the general case ('Deviant Primeras have no limit on use... Only their specific cooldown stops them from stacking'). It is nonetheless the number in this wave most likely to want a playtest, and it is built as a one-line dial for exactly that reason." },
  R117: { by: "caP'n", verdict: "c", note:
    "THE HEALER ART. 2026-09-06 for the shape; 2026-09-07 for both halves of what 'Necropolis limitation' meant, after the two brakes were laid out for him separately — 'yes those were the limits I was actually referencing, the return tally is not spent' — and for the fare, 'whoops yeah lets make it cost the price of a normal Resurrection.' ⚠ THIS IS THE LARGEST POWER GRANT IN THE WAVE AND IT IS EXPLICITLY HIS: R85's per-copy tally is the only hard ceiling on recursion in Terror, and while a Healer stands the tally does not apply. It was put to him as the huge reading of two, named as such, and he took it." },
  R118: { by: "caP'n", verdict: "c", note:
    "THE TANK ART. 2026-09-06 for the passive and the OPT; 2026-09-07 for the fare's ownership and the swing's reach — '(3 Khao or 1 Anima is the cost to use the Primera just so we're clear). it lasts for only that one specific Lyt Attack. It can only hit 1 of your Adversary's beings per turn.' The asymmetric square is his and is banner-quoted beside it so it is not tidied, exactly as R101's 5-or-4 square is." },
  R119: { by: "caP'n", verdict: "c", note:
    "THE WARDEN ART. 2026-09-06 for the shield, the not-a-wall clause and the two OP2 options; 2026-09-07 for the bounty's widening ('let's just make it a reward for ruining any level Warden, doesn't matter same reward at every level') and for how the not-a-wall clause should be explained ('pretend as though the Warden isn't there'). ⚠ TWO LEGS ARE V'AEL'S AND BOTH ARE FLAGGED IN THE CODE. FIRST: a seat with no Tank on the field at all has no shield. caP'n's condition is 'the Tank must be ruined and left Fallen', which can never be met if no Tank was ever stood, and reading that as a permanent shield would make a Warden untouchable by declining to play a card. wardenOpenToLytAttack is the one function that reverses it. SECOND: the +1 Max Khao bounty takes the CARD ceiling of 15 rather than the natural ceiling of 10, because a bounty is an effect and the two ceilings were already split that way. Both were declared to caP'n before a file was touched and neither was objected to, which is not the same as being ruled, so they stay marked." },
  R125: { by: "caP'n", verdict: "c", note:
    "INTANGIBILITY, FINALLY READ. The RULE is printed on three shipped cards and this row records a card text being obeyed, not a new ruling. ⚠ ONE READING IS V'AEL'S AND IS FLAGGED AT tomeIsIntangible: the Necklace of Akula's 'first 3 turns' is anchored to WHEN IT WAS PLACED rather than to the start of the Solrend, because the two cards either side of it in the same file say 'the turn it was constructed' and a Treasure that is only untouchable if you happened to open with it is not a design anyone would print. One line to reverse." },
  R124: { by: "caP'n", verdict: "c", note:
    "A SECOND SWING. The RULE is caP'n's and it is PRINTED ON TWO CARDS — 'This being can make an additional Lyt Attack, for every other Antdroid being in combat' — so this row records a card text finally being obeyed rather than a new ruling. ⚠ TWO READINGS ARE V'AEL'S AND BOTH ARE FLAGGED AT extraLytAttacks: 'in combat' is read as BOTH halves of the board (which is how Z4Z's and Pulsar's counts already read it, and the card does not say 'yours'), and 'other' is read as excluding the wielder. Neither was put to caP'n; both are one line to reverse." },
  R123: { by: "caP'n", verdict: "c", note:
    "THE TOME WARD'S TWO SCOPES. 2026-09-13, confirming the printed nouns are deliberate: 'Cards is general and Beings is specific, written and chosen specifically for those purposes.' The RULE is his and it was already printed on both cards; what was missing was any code that read it. ⚠ ONE LEG IS V'AEL'S AND IS FLAGGED AT THE PREDICATE: neither ward stops Anima or Khao dimming. caP'n named cards and beings; he did not rule on the pools. Read as NOT warded, because a pool is not a card by the same sentence that makes 'beings' narrower than 'cards' — and because the wider reading would make an 8-Khao Phoenix a two-Turn immunity to the entire Tome lane, which is the shape that was already reverted once as too large. Reverse at the `pools` line in adversaryDamageInPayload." },
  R122: { by: "caP'n", verdict: "c", note:
    "THE CADENCE TAGS BIND EVERY CARD. 2026-09-13, in answer to a direct question about whether OPT/OP2/OP3 are real on non-Tomes: 'every card, acronyms like OPT OP3 etc are real, they are not just there to look cool.' ⚠ THIS IS A RULE THE CARDS HAVE CARRIED ALL ALONG AND NO LAYER ENFORCED — 29 of 36 Impera, 10 of 12 U'hltis and all 4 Affinities print a tag, and a soul card could be pressed as many times in a half-turn as the seat could pay for. It bites harder now that R105 charges the fare every time, which is the same day's ruling. ⚠ ONE SHAPE IS V'AEL'S AND IS FLAGGED IN THE CODE: the stamp is a PER-CARD map (slot.soulUsed) rather than one lastUsed on the slot, because a Supreme Being wears five activatable cards with three different cadences and a single flag would make the L1 Impera's OPT govern the U'hlti's OP3." },
  R121: { by: "caP'n", verdict: "c", note:
    "THE MIND'S SILENCE. 2026-09-07, quoted verbatim above TRIO_RIFTS in this file, and volunteered by caP'n unprompted as the piece he had left out of the original message ('very important! I forgot to add this as well!'). The growth carve-out is equally his, given in answer to a direct question: 'no you can keep ascending etc and attaching cards to it etc like your ace in the hole.' ⚠ ONE LEG IS V'AEL'S AND IS FLAGGED IN THE CODE: whether a FALLEN Escendant still occupies its rift for the trio count. He said 'occupied with Ascendants not Esclings', which settles Esclings and says nothing about Fallen. Read as occupying, because Fallen is a state of a being that is standing there rather than an absence, and because the other reading would wake a seat's Mind as a REWARD for its own Body being knocked down. Reverse it in trioOccupied. ⚠ A SECOND, SMALLER CALL: the silence is built as an ALLOW-LIST of what a silenced Mind may still do, mirroring R108's phase gate, so an action type invented tomorrow is silenced by default rather than slipping through." },
  R120: { by: "caP'n", verdict: "c", note:
    "AN ART IS NOT A CARD. 2026-09-07, quoted verbatim above ANSWER_SCOPE_BROAD, including the lore reason only Nihil and Omnia may answer at all. ⚠ THE MECHANISM IS V'AEL'S AND IS THE MOST IMPORTANT FLAG IN THIS WAVE. caP'n drew the line in PROSE — broad enough to stop an activation, yes; naming a card, no. Deciding that line by a declared `answerScope` field rather than by reading printed text is V'ael's, and it was chosen because the two ways of reading text have each already cost this codebase a bug: trap 4.8, where the printed card and the branch that resolved it disagreed on five cards, and trap 4.4, where a regex-over-source check stayed green with a whole branch commented out. ⚠ AND THE CONSEQUENCE IS DECLARED RATHER THAN HIDDEN: no card carries the field yet, so TODAY NOTHING ANSWERS AN ART. That is a starting position, not a finished rule, and the Nihil and Omnia rows want reading one at a time against their printed text before it can be called done." },
  // Each of these has the designer's words quoted verbatim in a banner
  // above the code that implements it.
  // ── THE FOUR FALSE "July 2026" CITATIONS, CLOSED 2026-08-27 ────
  // Four comments in the R01-R76 band cited a July-2026 revision of
  // the rulebook that never existed, so this ledger under-reported them. The
  // PERFECTED RULEZ_V2.md (caP'n, 2026-08-27) now states every one of
  // these outright, which settles them as (c). The false cites in the
  // code were scrapped the same day.
  R05: { by: "caP'n", verdict: "c", note: "THE LANES — Grimoire = Escendants + Tomes only; Luxmoire = Impera, U'hltis, Affinities; Curios, Prophecies and Esclings pre-placed. Ruled 2026-08-26, and now stated verbatim in the perfected RULEZ_V2.md ('The Grimoire holds Escendants and Tomes. Nothing else.'). The old comment cited a nonexistent July book." },
  R06: { by: "caP'n", verdict: "c", note: "LUXMOIRE 10-40 — the perfected RULEZ_V2.md: 'at least ten cards and at most forty.' The 30->40 ceiling once leaned on the false July citation; it is the book's own number now." },
  R07: { by: "caP'n", verdict: "c", note: "FIRST PLAYER, OPENING TURN, NO GRIMOIRE DRAW — perfected RULEZ_V2.md: 'The seat going first may not draw from its Grimoire; the seat going second may.' Part of the R08 opening-Cycle canon of 2026-08-25." },
  R10: { by: "caP'n", verdict: "c", note: "HAND CAP 8, DRAW-THEN-DISCARD — ruled 2026-08-26 ('so I think it's 8 now, if it is good leave it'), and the perfected RULEZ_V2.md states it: 'If your hand would exceed 8 cards, the draw still resolves — then send cards to the Necropolis until you are at 8.' The April book's 7 is gone from canon." },
  R31: { by: "caP'n", verdict: "c", note: "FRACTURED STANDS, FRACTURED BLOCKS — perfected RULEZ_V2.md: 'Fractured Escendants still block — being at 0 Lyf is not the same as being gone.' Esclings never block, never target Anima, may Lyt-attack opposing Escendants only — all stated in the same book." },
  R87: { by: "caP'n", verdict: "c", note: "THE CLOCK — twenty minutes of live play, 2026-08-20, quoted in this file." },
  R85: { by: "caP'n", verdict: "c", note: "Resurrection tallies are PER COPY, not per name — July 27 2026." },
  R86: { by: "caP'n", verdict: "c", note: "Post-Graveshift cards can never be paid-Resurrected again — July 27 2026." },
  R77: { by: "caP'n", verdict: "c", note: "Every resurrection method counts toward the tally — Rulez July 25 2026 wave." },
  R78: { by: "caP'n", verdict: "c", note: "The Primeras — Rulez July 25 2026 wave (R78-R84 are one ruling)." },
  R08: { by: "caP'n", verdict: "c", note:
    "THE OPENING CYCLE, rewritten 2026-08-25 and quoted verbatim in RulesEnforcer.validateFirstTurnRestrictions. The row it replaced ('neither player can Lyt Attack on turn 1') named only the swing; the ruling makes Attack the broad word and adds the search ban, which caP'n himself marked '*new*'." },
  R97: { by: "caP'n", verdict: "c", note: "THE PROPHET'S TOLL — 2026-08-25, quoted verbatim above prophetsToll in this file. The reading of 'all of your adversary's Anima' as EVERY adversary is V'ael's and is flagged there for veto." },
  R98: { by: "caP'n", verdict: "c", note: "CO-WINNERS — 2026-08-25, quoted verbatim above normalizeWinners in this file." },
  R99: { by: "caP'n", verdict: "c",
    approved: { by: "caP'n", on: "2026-08-25", note:
      "RATIFIED by being named in caP'n's own list of rulings. Stamped here so the approval ledger and the review list are read off the same field rather than off a sentence in a note." },
    note:
    "A PROPHECY IS SPENT WHEN IT RESOLVES. ⚠ THE RULE WAS V'AEL'S — written into prophecyEngine as a ruling in the August coherency pass, to fix a confirmed defect (one Ace of Shades fired three times and ate a whole 4-player table). caP'n RATIFIED it on 2026-08-25 by naming it in his own list of rulings, which is what moves it from (b) to (c). Filed here with its history intact rather than laundered." },
};

// ── THE ONE FUNCTION THAT ANSWERS THE DISCIPLINE ────────────────
// A rule with no row above is either from the original Rulez document
// (R01-R76) or from a wave whose provenance was never in question.
// The ones to argue with are `verdict === "b"` — MINUS the ones caP'n
// has since signed off, which carry an `approved` stamp beside their
// verdict rather than instead of it.
//
// ⚠ THIS IS THE ANSWER TO "WHAT HAVE YOU CHANGED WITHOUT ASKING ME",
// and it is the ONLY thing that answers it. Read it out in the session
// report, not once a month. If it is empty, say so; if it is not, name
// every row it returns. It is honest as of 2026-08-27 (the four false
// 'July 2026' citations in the R01-R76 band are closed — see the R05/
// R06/R07/R10/R31 rows — so the band assumption below holds again):
//
//   ⭐ THE LIST IS EMPTY, as of 2026-09-13. Say THAT out loud, and say
//   it in those words — an empty list is the only reading of this
//   block that a session can get wrong by staying quiet about.
//
//   R92 (the chain), R94 (the Impera deck limits), R96 (THE TILL LAW)
//   and now R93 (THE SECOND HAND) were all APPROVED by caP'n and have
//   left this list — R96 on 2026-08-26 with *"It is a payment and yes
//   you cannot end yourself that way."*, R93 on 2026-09-13 with
//   *"yeah that's fine… you said the video game code etc literally
//   starts to break down… yes, need that rule."* Their verdicts are
//   untouched: approval is not authorship.
//
//   ⚠ TWO READINGS INSIDE THE 2026-08-26 RULINGS ARE V'AEL'S AND ARE
//   NOT ON THIS LIST, because the RULES are caP'n's and only a leg of
//   each is not. Say them out loud anyway:
//     · R101 — which LEVEL a Deviant Ascend is charged at. Rulez v2
//       never says; caP'n said to read it as Training reads it, and
//       "the destination level" is that reading written down.
//     · R103 — what "a full turn" means at a 3-4 seat table. caP'n
//       described 1v1. A full ROUND of the table is the
//       generalisation, borrowed from R08's opening Cycle.
//
//   ⚠ AND FIVE READINGS INSIDE THE 2026-09-06/07 EXILE WAVE ARE V'AEL'S.
//   Same shape as the two above: every RULE in that wave is caP'n's, and
//   only a leg of each of these is not. None is on the list, and all five
//   are flagged in the code at the line that decides them:
//     · R111 — whether Exiliation's book-reaching option is barred on the
//       opening Cycle. He ruled its fare and its draw, not R08. Reversed
//       by EXILIATION_SEARCH_BY.
//     · R115 — an Escling in a Role rift carries no Art. Derived from the
//       standing law that Esclings bear no Primera stamps, which he did
//       not restate. Reversed by riftArtIsAwake.
//     · R119 — a seat with no Tank on the field has no Warden shield, and
//       the +1 Max Khao bounty takes the card ceiling of 15. Both were
//       declared to him before any file was touched and neither was
//       objected to. Not objected to is not ruled, so both stay marked.
//       Reversed by wardenOpenToLytAttack and WARDEN_BOUNTY.
//     · R121 — whether a FALLEN Escendant still occupies its rift for
//       the trio count that silences the Mind. He settled Esclings and
//       said nothing about Fallen. Reversed by trioOccupied.
//     · R120 — deciding "broad enough" by a declared answerScope field
//       rather than by reading printed prose, which defaults every card
//       to NARROW and therefore leaves NOTHING able to answer an Art
//       until a data pass says otherwise. He drew the line in prose; the
//       mechanism, and its safe default, are V'ael's.
//     · R110 carries a FINDING rather than a reading: the rulebook said
//       eleven rifts and listed twelve, and had since it was written.
export const isApproved = (row) => !!(row && row.approved && row.approved.by);
export const rulesInventedWithoutPermission = () =>
  Object.entries(RULE_PROVENANCE)
    .filter(([, v]) => v.verdict === "b" && !isApproved(v))
    .map(([k]) => k);

// ── THE THIRD QUESTION THE LEDGER COULD NOT ANSWER ──────────────────
// ⚠ ADDED 2026-09-14. The two functions above answer "what did V'ael
// invent" and "what has caP'n blessed". Neither could answer the one a
// reader actually needs first: WHICH RULES HAS NOBODY WRITTEN A
// PROVENANCE ROW FOR AT ALL. On the day this was added it stood at 79 of
// 130 — well over half the catalogue — and the number was invisible,
// because an absent row looks exactly like a row that was checked and
// found unremarkable. That is the coverage-number trap in the referee's
// own file, wearing a ledger for a costume: `rulesInventedWithoutPermission()`
// returning [] reads as "everything is accounted for" when it means
// "nothing UNACCOUNTED-FOR IS FILED".
//
// This does not fill the 79 rows. Filling them honestly needs caP'n's
// memory, not V'ael's inference, and a guessed provenance is worse than
// a missing one — it launders authorship, which is the single thing this
// ledger exists to prevent. So the gap is DECLARED and COUNTED instead,
// and it is the standing to-do list: every row that gets written is one
// fewer here, and the number can only go down.
export const rulesWithNoProvenance = () =>
  RULES.map((r) => r.code).filter((c) => !RULE_PROVENANCE[c]);

/** How much of the catalogue's authorship is on the record, 0..1. */
export const provenanceCoverage = () => {
  const total = RULES.length || 1;
  return { filed: total - rulesWithNoProvenance().length, total,
           ratio: (total - rulesWithNoProvenance().length) / total };
};

// The other half of the same ledger: what he HAS blessed, and when.
// Surfacing an approval is how a reader tells NOBODY HAS LOOKED AT THIS
// apart from HE LOOKED AND KEPT IT. (Said in capitals rather than in
// quotation marks on purpose: tools/harness/build.mjs scans this file
// with an import regex, and an import keyword followed by a quoted
// phrase reads to it as a module specifier even inside a comment.)
export const rulesApprovedByDesigner = () =>
  Object.entries(RULE_PROVENANCE)
    .filter(([, v]) => isApproved(v))
    .map(([k, v]) => ({ code: k, by: v.approved.by, on: v.approved.on }));

export const CAT_COLORS = {
  Setup:"#22d3ee", Turn:"#a78bfa", Khao:"#f0d060", Transcend:"#22c55e",
  Ascend:"#22c55e", Combat:"#f87171", Anima:"#dc2626", Tomes:"#c9a84c",
  Graveshift:"#fb923c", Resurrect:"#818cf8", Soul:"#c084fc",
  "Deck Limits":"#6d28d9", Deviant:"#e879f9", Supreme:"#fbbf24",
  Primera:"#5eead4",
  // THE EXILE WAVE (caP'n, 2026-09-06/07) — two new bands.
  // Exile is the void violet the rift is drawn in; Role is the steel of
  // the four Arts. Both are read by the Debugger's rule browser, so a new
  // category with no colour renders as an unlabelled grey row.
  Exile:"#7c3aed", Role:"#38bdf8",
  Misc:"#9a8a7a", Bug:"#ef4444",
};
